import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import RecentlyLearnedRules from '../components/bankfeed/RecentlyLearnedRules';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { ClosedPeriodNotice, useClosedPeriodInfo } from '../components/period/ClosedPeriodNotice';
import { isOpenBankingCountry } from '../constants/openBanking';
import ReportIssueButton from '../components/report/ReportIssueButton';
import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';
import { useMonoConnect } from '../hooks/useMonoConnect';
import { MonoSyncProgress } from '../components/shared/MonoSyncProgress';
import { BankFeedSyncBanner } from '../components/shared/BankFeedSyncBanner';
import MonoBanksRefreshSection from '../components/banking/MonoBanksRefreshSection';
import OrphanRecoveryCard from '../components/banking/OrphanRecoveryCard';
import PlaidBanksRefreshSection from '../components/banking/PlaidBanksRefreshSection';
import AIConfidenceBadge from '../components/common/AIConfidenceBadge';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { readInstant, writeInstant } from '../lib/instantCache';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FormattedNumberInput } from '../components/ui/formatted-number-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { notifyUploadCancelled } from '../utils/uploadCancelToast';
import { recordConnectUnconfirmed, clearConnectUnconfirmed, settleAbandonedMonoReturn } from '../utils/connectUnconfirmed';
import { isFreshEnoughForModal } from '../utils/isFreshEnoughForModal';
import { useStatementUploader } from '../hooks/useStatementUploader';
import { showPeriodLockToast } from '../utils/periodLockToast';
import { motion, AnimatePresence } from 'framer-motion';
import { BankIntegrationTooltip, SUPPORTED_BANKS_BY_COUNTRY, getCountryDisplayName } from '../components/BankIntegrationTooltip';
import { BankFeedSafetyModal } from '../components/BankFeedSafetyModal';
import SpecialItemModal from '../components/SpecialItemModal';
import { PendingTransactionCard } from '../components/bankfeed/PendingTransactionCard';
import { TransactionTable } from '../components/bankfeed/TransactionTable';
import { TransactionTableHeader } from '../components/bankfeed/TransactionTableHeader';
import { BankFeedHelpCard } from '../components/bankfeed/BankFeedHelpCard';
import { AutoApproveDialog } from '../components/bankfeed/AutoApproveDialog';
import bankFeedQuickStartTour from '../tours/bankFeedQuickStart';
import { useTour } from '../components/tour/TourProvider';
import { useBusinessTaxContext } from '../hooks/useBusinessTaxContext';
import { useBankFeedData } from '../hooks/useBankFeedData';
import { useBankFeedTransactions } from '../hooks/useBankFeedTransactions';
import { useBankFeedInsights } from '../hooks/useBankFeedInsights';
import { FeedSourcesCard } from '../components/bankfeed/FeedSourcesCard';
import { AISettingsCard } from '../components/bankfeed/AISettingsCard';
import { StatementUploadDialog as UploadStarterDialog } from '../components/bankfeed/StatementUploadDialog';
import { PostDeleteGapFillModal } from '../components/bankfeed/PostDeleteGapFillModal';
import { useStepUp } from '../components/auth/StepUpProvider';
import { BulkActionsToolbar } from '../components/bankfeed/BulkActionsToolbar';
import { BankFeedDocumentDialog } from '../components/bankfeed/BankFeedDocumentDialog';
import RecordOtherReceiptModal from '../components/payments/RecordOtherReceiptModal';
import RecordOtherPaymentModal from '../components/payments/RecordOtherPaymentModal';
import { CacheIndicator } from '../components/CacheIndicator';
// BankReconciliationCenter is now imported lazily inside BankFeedReconciliationTab
import { AutoDiscoveryPanel } from '../components/bankfeed/AutoDiscoveryPanel';
import { BulkReclassifyDialog } from '../components/bankfeed/BulkReclassifyDialog';
import { LinkDocumentDialog } from '../components/bankfeed/LinkDocumentDialog';
import SmartLinkDocumentModal from '../components/bankfeed/SmartLinkDocumentModal';
import InBooksDetailModal from '../components/bankfeed/InBooksDetailModal';
import { BankFeedKPIStrip } from '../components/bankfeed/BankFeedKPIStrip';
import { SmartMatchEducationCard } from '../components/bankfeed/SmartMatchEducationCard';
import { BankFeedSaveChangesBar } from '../components/bankfeed/BankFeedSaveChangesBar';
import { BankFeedHeader } from '../components/bankfeed/BankFeedHeader';
import { ColumnMappingDialog } from '../components/bankfeed/ColumnMappingDialog';
import { confirmAction } from '../utils/confirmAction';
import SpecialItemDecisionModal from '../components/SpecialItemDecisionModal';

// Phase 5 - Lazy-load tab bodies so each ships its own webpack chunk.
// Initial /bank-feeds bundle only loads the active tab (default: Overview).
// `Suspense` shows a tiny skeleton while a chunk is fetched on tab switch.
const importOverviewTab = () => import('../components/bankfeed/BankFeedOverviewTab');
const importReconciliationTab = () => import('../components/bankfeed/BankFeedReconciliationTab');
const importUploadsTab = () => import('../components/bankfeed/BankFeedUploadsTab');
const BankFeedOverviewTab = lazy(() => importOverviewTab().then(m => ({ default: m.BankFeedOverviewTab })));
const BankFeedReconciliationTab = lazy(() => importReconciliationTab().then(m => ({ default: m.BankFeedReconciliationTab })));
const BankFeedUploadsTab = lazy(() => importUploadsTab().then(m => ({ default: m.BankFeedUploadsTab })));
const importTransactionsTab = () => import('../components/bankfeed/BankFeedTransactionsTab');
const BankFeedTransactionsTab = lazy(() => importTransactionsTab().then(m => ({ default: m.BankFeedTransactionsTab })));
// Phones default to cards and remember their choice separately from desktop.
const IS_PHONE_VIEWPORT = typeof window !== 'undefined' && window.innerWidth < 768;
const BF_VIEW_KEY = IS_PHONE_VIEWPORT ? 'akountfly_bf_view_phone' : 'akountfly_bf_view';
// Kick the (large) Transactions tab chunk download immediately so it is not
// serialised behind the critical data fetch on first visit.
importTransactionsTab();
// Warm the other tab chunks right after so the first click on Uploads /
// Reconciliation / Overview never waits on a chunk download.
setTimeout(() => { importUploadsTab(); importReconciliationTab(); importOverviewTab(); }, 1500);

const TabLoader = () => (
  <div className="space-y-3 py-6" aria-label="Loading section">
    <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
    <div className="h-32 rounded-lg bg-muted/40 animate-pulse" />
    <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
  </div>
);
// Phase 5 - Lazily load specialty dialogs that only render conditionally.
// They're heavy and rarely opened, so keep them out of the initial bundle.
const InvestmentMatchDialog = lazy(() => import('../components/bankfeed/InvestmentMatchDialog').then(m => ({ default: m.InvestmentMatchDialog })));
const LoanMatchDialog = lazy(() => import('../components/bankfeed/LoanMatchDialog').then(m => ({ default: m.LoanMatchDialog })));
const TaxPaymentDialog = lazy(() => import('../components/bankfeed/TaxPaymentDialog').then(m => ({ default: m.TaxPaymentDialog })));
const ClassificationProgressTracker = lazy(() => import('../components/ClassificationProgressTracker').then(m => ({ default: m.ClassificationProgressTracker })));
import { useActivityTracker } from '../hooks/useActivityTracker';
import { PeriodSearchFilter } from '../components/PeriodSearchFilter';
const PrepaymentMatchDialog = lazy(() => import('../components/bankfeed/PrepaymentMatchDialog').then(m => ({ default: m.PrepaymentMatchDialog })));
const AssetDisposalDialog = lazy(() => import('../components/bankfeed/AssetDisposalDialog').then(m => ({ default: m.AssetDisposalDialog })));
const PeriodLockWarningDialog = lazy(() => import('../components/bankfeed/PeriodLockWarningDialog').then(m => ({ default: m.PeriodLockWarningDialog })));
const InterBankTransferModal = lazy(() => import('../components/bankfeed/InterBankTransferModal').then(m => ({ default: m.InterBankTransferModal })));
const InterBankTransfersTab = lazy(() => import('../components/bankfeed/InterBankTransfersTab').then(m => ({ default: m.InterBankTransfersTab })));
const UploadBacklogFeeDialog = lazy(() => import('../components/bankfeed/UploadBacklogFeeDialog').then(m => ({ default: m.UploadBacklogFeeDialog })));
import TrainAITable from '../components/bankfeed/TrainAITable';
import SuggestedRulesPanel from '../components/bankfeed/SuggestedRulesPanel';
import CorrectionHistoryTable from '../components/bankfeed/CorrectionHistoryTable';
// Phase 3 Refactored Dialog Components (SOLID principles)
import { 
  MatchDocumentDialog,
  TransactionDetailDialog,
  ManualProcessDialog,
  TrainAIDialog,
  BulkConfirmDialog,
  DeleteConfirmDialog,
  SplitTransactionDialog,
  UndoClassificationDialog,
  ReversalDialog
} from '../components/bankfeed';
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle,
  AlertCircle, 
  FileText, 
  Receipt,
  Upload,
  Link2,
  Loader2,
  Sparkles,
  RefreshCcw,
  RefreshCw,
  Brain,
  ArrowUpRight,
  ArrowDownRight,
  ArrowDownLeft,
  Eye,
  Check,
  X,
  Clock,
  ChevronRight,
  Zap,
  BarChart3,
  Search,
  Filter,
  Building2,
  Wallet,
  Landmark,
  Globe,
  FileUp,
  CloudDownload,
  Settings2,
  Activity,
  TrendingUp,
  Target,
  Ban,
  MoreVertical,
  ExternalLink,
  HelpCircle,
  Plug,
  Edit3,
  Scissors,
  Undo2,
  Lightbulb,
  BookOpen,
  Plus,
  Trash2,
  RotateCcw,
  Info,
  ThumbsUp,
  Briefcase,
  CreditCard,
  Scale,
  Layers,
  Package,
  GitCompare,
  ArrowLeftRight,
  ChevronDown,
  ChevronLeft,
  MoreHorizontal,
  CalendarDays,
  FileSpreadsheet,
  Table2 as TableIcon
} from 'lucide-react';
import { AccountingStartDateModal } from '../components/shared/AccountingStartDateModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useRealtimeContext, RealtimeEventTypes } from '../hooks/useRealtime';
import { 
  CLASSIFICATION_OPTIONS, 
  getPlainEnglishClassification,
  DEFAULT_STATS,
  DEFAULT_TRAINING_EXAMPLE,
  STATS_CACHE_DURATION,
  STORAGE_KEYS
} from './bankfeed/bankFeedUtils';
import { glToFamily, inferDocFromGl, detectSpecialFamily } from '../utils/glToFamily';
import { rowCurrencyOf } from '../components/bankfeed/RowFxEquivalent';
import { describeApprovedTx } from '../utils/describeApprovedTx';
import { OpeningBalanceNeededBanner } from '../components/banking/OpeningBalanceNeededBanner';
import StatementMultiUploadReviewDialog from '../components/banking/StatementMultiUploadReviewDialog';
import { InfoTip } from '../components/ui/info-tip';
import { notifyImportComplete } from '../utils/importToast';
// iter1238 P1c - progressive live-parsing feed for streaming-pipeline uploads.
// iter1238 Phase 8.11 — StreamingParsedFeed is no longer rendered
// inline on this page. It now mounts inside StreamingUploadModal
// triggered from the global upload progress banner. Import kept for
// grep-ability only.
// import StreamingParsedFeed from '../components/bankfeed/StreamingParsedFeed';

const BankFeedCenterPage = () => {
  const { user, business } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  // Global background jobs context - persists job tracking across pages
  const backgroundJobsCtx = useBackgroundJobs();
  const { uploadStatement } = useStatementUploader();
  
  // Backlog fee confirmation dialog (statement uploads with pre-registration-month transactions)
  const [backlogDialogJob, setBacklogDialogJob] = useState(null);
  const [backlogDialogBusy, setBacklogDialogBusy] = useState(false);

  // iter1238 Phase 8.13b · UX guards
  // Cancelling jobs: track cancel-in-flight so the button shows a
  // spinner and can't be clicked twice. Prevents the "clicked cancel,
  // nothing happens" perception (~500-800 ms API round-trip).
  const [cancellingJobIds, setCancellingJobIds] = useState(new Set());

  // Just-confirmed jobs: after user clicks Confirm on the backlog
  // dialog, the backend can briefly still return `awaiting_confirmation`
  // in the next poll before it flips to `processing`. Without this
  // guard the modal snaps back open. We block auto-open for 30 s per
  // jobId post-confirm.
  const justConfirmedRef = useRef(new Map()); // jobId -> timestamp

  // Toast dedup: only fire ONE toast per (jobId, status) transition,
  // even when multiple polling loops (page + BackgroundJobsContext
  // bridge) both see the same state change.
  const lastToastedStatusRef = useRef(new Map()); // jobId -> lastStatus
  const emitJobToast = (jobId, status, cb) => {
    if (lastToastedStatusRef.current.get(jobId) === status) return;
    lastToastedStatusRef.current.set(jobId, status);
    try { cb(); } catch (e) { console.error('toast cb failed:', e); }
  };

  
  // WebSocket for real-time job updates (optional - falls back to polling)
  const realtime = useRealtimeContext();
  const wsConnected = realtime?.connected || false;
  
  // Activity tracking for "new items" badges
  const { counts: activityCounts, lastSeen, markSeen } = useActivityTracker();
  
  // Mark module as seen on unmount so badges persist during visit
  useEffect(() => {
    return () => markSeen('bank_feeds');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Helper to check if a transaction is "new" since last visit
  const isNewTransaction = useCallback((tx) => {
    if (!lastSeen.bank_feeds) return false;
    return (tx.created_at || '') > lastSeen.bank_feeds;
  }, [lastSeen.bank_feeds]);
  
  // Compute fiscal year period for annual stats
  const fiscalPeriod = React.useMemo(() => {
    const fyEnd = parseInt(business?.financial_year_end || '12', 10);
    const now = new Date();
    let startYear = now.getFullYear();
    let startMonth = (fyEnd % 12) + 1; // Month after year-end is start of fiscal year
    
    // If we haven't reached the start month yet this year, we're in the previous fiscal year
    if (now.getMonth() + 1 < startMonth) {
      startYear -= 1;
    }
    
    let start = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
    // iter-phase3 F9 - backlog rows can predate the current fiscal year
    // (accounting start month in a prior FY). Widen the stats window so the
    // KPI strip matches what the table shows.
    const asd = business?.accounting_start_date;
    if (asd && asd < start) start = asd;
    const endYear = startMonth === 1 ? startYear : startYear + 1;
    const endMonth = fyEnd;
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    const end = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    
    return { start, end };
  }, [business?.financial_year_end, business?.accounting_start_date]);
  
  // ============================================
  // SECTION 1: STATE DECLARATIONS
  // ============================================

  // Phase 1 Refactor - Read-only reference data is centralized in this hook.
  // The hook owns its own mountedRef + isolated effects, so the parent page
  // only needs to call the refetchers in its bootstrap effect (loadData).
  const {
    connections, fetchConnections,
    bankAccounts, setBankAccounts, fetchBankAccounts,
    availableGlAccounts, fetchGlAccounts,
    aiSettings, setAiSettings, fetchAiSettings,
    openDocuments, fetchOpenDocuments,
    monoConfig, fetchMonoConfig,
  } = useBankFeedData();

  // State - stats / pending / processed / duplicate / failed / uploadedStatements /
  // potentialDuplicates are now sourced from useBankFeedTransactions (declared below
  // once the filter/pagination state is available).
  // reconSummary sourced from useBankFeedInsights hook (declared below).
  const [reconBankAccountId, setReconBankAccountId] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  // iter-bugfix (G-2): Plaid connections list - used for the reauth/expiring
  // banner inside PlaidBanksRefreshSection so Plaid users see attention cues
  // on Bank Transactions Center, matching Mono parity.
  // iter1358 · Smart Match / AI Assist education. Fetch the current AI
  // cycle usage so we know when to force-show the "AI Assist is paused"
  // variant of the SmartMatchEducationCard even if the user previously
  // dismissed the calm version.
  const [aiPaused, setAiPaused] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/billing/usage`);
        if (cancelled) return;
        const u = res.data || {};
        const used = Number(u.ai_calls_used || 0);
        const limit = Number(u.ai_calls_limit || 0);
        const suppress = Boolean(u.suppress_all);
        setAiPaused(suppress || (limit > 0 && used >= limit));
      } catch (_) { /* non-fatal - assume not paused */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [plaidConnections, setPlaidConnections] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/plaid/connections`);
        if (!cancelled) setPlaidConnections(Array.isArray(res.data) ? res.data : []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Phase 1 Refactor - read-only reference data (connections, banks, COA,
  // AI settings, open docs, mono config) is now sourced from useBankFeedData
  // hook. See /app/frontend/src/hooks/useBankFeedData.js for details.
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingId, setProcessingId] = useState(null);
  // Read ?section=... query param on initial mount to support deep-links
  // from external CTAs (e.g. Dashboard "AI got smarter" widget). Using a
  // lazy state initializer survives React 18 StrictMode unmount/remount
  // cycles where a useEffect+navigate() pattern would lose the param.
  const _initialSection = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const s = p.get('section');
      return ['ai-learning', 'connections', 'auto-discovery'].includes(s) ? s : null;
    } catch {
      return null;
    }
  })();
  const [activeTab, setActiveTab] = useState(_initialSection ? 'more' : 'transactions');
  const [closedPeriodRefresh, setClosedPeriodRefresh] = useState(0);
  const closedPeriodInfo = useClosedPeriodInfo(`${activeTab}-${closedPeriodRefresh}`);
  const [txFilter, setTxFilter] = useState('pending'); // Sub-filter for Transactions tab
  // Phase A - view mode toggle: 'table' (desktop default) or 'cards' (mobile default).
  // iter 971b - On mobile (<768px) the card view is far easier to scan/tap than
  // a table with 10+ columns, so default to cards on small screens. Users can
  // still toggle explicitly; the choice persists via localStorage regardless
  // of device.
  const [txViewMode, setTxViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(BF_VIEW_KEY);
      if (saved === 'table' || saved === 'cards') return saved;
    } catch { /* localStorage disabled */ }
    return IS_PHONE_VIEWPORT ? 'cards' : 'table';
  });
  useEffect(() => {
    try { localStorage.setItem(BF_VIEW_KEY, txViewMode); } catch {}
  }, [txViewMode]);
  // Phase A - comprehensive header filters (search, direction, bank, gl, source, tax, confidence, date range).
  const [tableFilters, setTableFilters] = useState({});
  // Tax columns are auto-sourced from business country + tax_preferences.
  const { ctx: taxCtx } = useBusinessTaxContext();
  // Phase B - Auto-approve high-confidence dialog state.
  const [autoApproveOpen, setAutoApproveOpen] = useState(false);
  // Phase B - Register the bank-feed quick-start tour exactly once.
  const tour = useTour();
  const tourRegister = tour?.registerTour;
  useEffect(() => {
    if (tourRegister) tourRegister(bankFeedQuickStartTour);
  }, [tourRegister]);
  // iter1246 · Auto-open the upload STARTER modal when the user arrives
  // with `?upload=1` (from the Home banner's "Upload statement" CTA).
  // The starter explains formats + limits + template downloads before
  // firing the OS file picker. Clear the param after opening so a
  // reload doesn't re-trigger.
  // iter1254 · depends on location.search (was mount-only) so the CTA
  // also works when the user is ALREADY on this page.
  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search || '');
      if (p.get('upload') === '1') {
        setShowUploadStarter(true);
        // Strip the param so navigating back doesn't spam the modal.
        p.delete('upload');
        const nextSearch = p.toString();
        navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [moreSection, setMoreSection] = useState(_initialSection); // For "More" dropdown content; pre-seeded from URL ?section= deep-link
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDirection, setFilterDirection] = useState('all');
  const [deletingStatementId, setDeletingStatementId] = useState(null);
  const [downloadingStatementId, setDownloadingStatementId] = useState(null);

  // In-page document creation dialog (replaces page navigation)
  const [docDialog, setDocDialog] = useState({ open: false, type: null, prefill: null });
  const [otherReceiptDialog, setOtherReceiptDialog] = useState({ open: false, prefill: null });
  const [otherPaymentDialog, setOtherPaymentDialog] = useState({ open: false, prefill: null });
  
  // Background processing state - allows users to navigate while processing
  const [backgroundJobs, setBackgroundJobs] = useState(() => {
    // Initialize from localStorage to persist across page navigation
    try {
      const savedJobs = localStorage.getItem(STORAGE_KEYS.ACTIVE_JOBS);
      return savedJobs ? JSON.parse(savedJobs) : [];
    } catch (e) {
      return [];
    }
  }); // Track active background jobs with progress
  // Job structure: { jobId, filename, startedAt, progress: 0-100, status: 'queued'|'processing'|'completed', totalTx: 0, processedTx: 0 }
  const uploadPollIntervalsRef = useRef(new Map()); // Store polling intervals by job ID
  const isMountedRef = useRef(true); // Track if component is mounted
  
  // Delete Confirmation Dialog State
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, filename }
  
  // AI Settings State - value/setter come from useBankFeedData (see top of component).
  const [savingAiSettings, setSavingAiSettings] = useState(false);
  
  // Bulk Selection State
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showReclassifyDialog, setShowReclassifyDialog] = useState(false);
  const [reclassifyTarget, setReclassifyTarget] = useState(null); // single item or 'bulk'
  const [selectedClassification, setSelectedClassification] = useState('');
  const [reclassifyReason, setReclassifyReason] = useState('');
  const [applyLearning, setApplyLearning] = useState(true); // Default: AI learns from corrections
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'approve' | 'reject'
  
  // Investment Match Dialog State
  const [showInvestmentMatchDialog, setShowInvestmentMatchDialog] = useState(false);
  const [investmentMatchTransaction, setInvestmentMatchTransaction] = useState(null);
  
  // Loan Match Dialog State
  const [showLoanMatchDialog, setShowLoanMatchDialog] = useState(false);
  const [taxPaymentTransaction, setTaxPaymentTransaction] = useState(null);
  const [loanMatchTransaction, setLoanMatchTransaction] = useState(null);
  
  // Prepayment Match Dialog State
  const [showPrepaymentMatchDialog, setShowPrepaymentMatchDialog] = useState(false);
  const [prepaymentMatchTransaction, setPrepaymentMatchTransaction] = useState(null);
  
  // Asset Disposal Dialog State
  const [showAssetDisposalDialog, setShowAssetDisposalDialog] = useState(false);
  const [assetDisposalTransaction, setAssetDisposalTransaction] = useState(null);
  
  // AI Learning & Corrections State - correctionHistory/suggestedCorrections/
  // loadingCorrections sourced from useBankFeedInsights hook (declared below).
  const [applyingCorrections, setApplyingCorrections] = useState(false);
  
  // Train AI State - trainingExamples/trainingStats/loadingTraining come from
  // useBankFeedInsights hook (declared below).
  const [showTrainDialog, setShowTrainDialog] = useState(false);
  const [newTrainingExample, setNewTrainingExample] = useState(DEFAULT_TRAINING_EXAMPLE);
  const [savingTraining, setSavingTraining] = useState(false);
  const [importingFromCorrections, setImportingFromCorrections] = useState(false);
  
  // Asset & Prepayment Dialog State - Jul 16: replaced by the canonical
  // SpecialItemModal (same full term form used on the special-item pages).
  const [specialTermsModal, setSpecialTermsModal] = useState({ open: false, transaction: null, itemType: null, family: null });

  // Phase 2.2 (Feb 2026) - Special Item Decision Modal state. Intercepts
  // Approve / Save when the resulting GL is a special-item family.
  // The transaction is held aside while the user picks New vs Match.
  const [specialItemModal, setSpecialItemModal] = useState({ open: false, transaction: null, docType: null });

  // ────────────────────────────────────────────────────────────────────
  // Phase #5 (user-prioritised) - Pending category edits queue.
  // Inline category dropdowns no longer save on every change; each edit
  // is staged here and committed atomically when the user clicks the
  // floating "Save N changes" pill (BankFeedSaveChangesBar). On save we
  // hit /bank-feeds/save-changes/{id} per row - that endpoint already
  // reverses the original journal, removes any auto-created documents,
  // rebuilds special-item schedules, and refreshes notifications, so the
  // cascade the user requested is handled server-side.
  // Shape: Map<txId, { glCode, glName, originalCode, isInflow, description }>
  // ────────────────────────────────────────────────────────────────────
  const [pendingGlEdits, setPendingGlEdits] = useState(new Map());
  const [savingPendingEdits, setSavingPendingEdits] = useState(false);

  // Period lock warning state
  const [periodLockDialog, setPeriodLockDialog] = useState({
    open: false,
    transactionDate: null,
    lockedPeriod: null,
    onPostToOriginalDate: null,
    onPostToToday: null,
  });
  
  // Training Warning State
  const [trainingWarnings, setTrainingWarnings] = useState([]);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);
  
  // GL Accounts (availableGlAccounts) - now sourced from useBankFeedData hook.
  
  // Split Transaction State
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitTransaction, setSplitTransaction] = useState(null);
  
  // Multi-Allocation State (for paying multiple invoices/bills at once)
  const [showMultiAllocDialog, setShowMultiAllocDialog] = useState(false);
  const [multiAllocTransaction, setMultiAllocTransaction] = useState(null);
  const [multiAllocSuggestions, setMultiAllocSuggestions] = useState(null);
  const [multiAllocLoading, setMultiAllocLoading] = useState(false);
  const [selectedAllocations, setSelectedAllocations] = useState([]);
  const [showWhtDiscountPanel, setShowWhtDiscountPanel] = useState(false);  // Show WHT/Discount inputs
  const [allocWhtAmounts, setAllocWhtAmounts] = useState({});  // { document_id: wht_amount }
  const [allocDiscountAmounts, setAllocDiscountAmounts] = useState({});  // { document_id: discount_amount }
  
  // Manual Processing Dialog State
  const [showManualProcessDialog, setShowManualProcessDialog] = useState(false);
  const [manualProcessItem, setManualProcessItem] = useState(null);
  const [manualClassification, setManualClassification] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualProcessing, setManualProcessing] = useState(false);
  
  // Reversal State
  const [showReversalDialog, setShowReversalDialog] = useState(false);
  const [reversalTransaction, setReversalTransaction] = useState(null);
  
  // Accounting Start Date Modal state
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [reversalReason, setReversalReason] = useState('');
  const [reversalNotes, setReversalNotes] = useState('');
  const [processingReversal, setProcessingReversal] = useState(false);
  // detectedReversals/loadingReversals/reversedTransactions sourced from
  // useBankFeedInsights hook (declared below).
  
  // Undo Classification State (for moving completed back to review)
  const [showUndoClassificationDialog, setShowUndoClassificationDialog] = useState(false);
  const [undoClassificationTransaction, setUndoClassificationTransaction] = useState(null);
  const [processingUndoClassification, setProcessingUndoClassification] = useState(false);
  // iter1432 Gap #10 · Cascade-undo preview state
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoPreviewLoading, setUndoPreviewLoading] = useState(false);
  const [cascadeReverseChecked, setCascadeReverseChecked] = useState(false);
  
  // Quick Approve State
  const [quickApproving, setQuickApproving] = useState(null); // transaction id being quick approved
  const [isApproving, setIsApproving] = useState(null); // for conversational approve button

  // Tax Toggle State (per-transaction VAT/WHT status)
  const [taxStatus, setTaxStatus] = useState({}); // { txId: { has_vat, has_wht, ... } }
  const [togglingTax, setTogglingTax] = useState(null); // txId being toggled
  
  // Bank accounts (bankAccounts) - now sourced from useBankFeedData hook.
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [filterBankAccountId, setFilterBankAccountId] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterGlCode, setFilterGlCode] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Period filter state (drives filterStartDate/filterEndDate for API calls)
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // Compute API date params from period selection
  useEffect(() => {
    const now = new Date();
    let start = '', end = '';
    switch (periodFilter) {
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
        end = now.toISOString().slice(0, 10);
        break;
      case 'last_month': {
        const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start = lm.toISOString().slice(0, 10);
        end = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
        break;
      }
      case 'this_quarter': {
        const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        start = qStart.toISOString().slice(0, 10);
        end = now.toISOString().slice(0, 10);
        break;
      }
      case 'this_year':
        start = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
        end = now.toISOString().slice(0, 10);
        break;
      case 'custom':
        start = customDateRange.start || '';
        end = customDateRange.end || '';
        break;
      default: // 'all'
        start = '';
        end = '';
    }
    setFilterStartDate(start);
    setFilterEndDate(end);
  }, [periodFilter, customDateRange]);
  
  // Pagination
  const [pendingPage, setPendingPage] = useState(1);
  const [processedPage, setProcessedPage] = useState(1);
  // Pagination totals + list-shaped data come from useBankFeedTransactions hook.
  const PAGE_SIZE = 200;

  // Phase 1 part 2 Refactor - list-shaped data (stats + pending/processed/duplicates/
  // failed/potentialDuplicates/uploadedStatements) lives in this hook. Filters and
  // page numbers are still owned by the page (they double as UI state).
  const {
    stats, setStats,
    pendingItems, setPendingItems, pendingTotalPages, pendingTotalCount,
    setPendingTotalPages, setPendingTotalCount,
    processedItems, setProcessedItems, processedTotalPages, processedTotalCount,
    setProcessedTotalPages, setProcessedTotalCount,
    duplicateItems, setDuplicateItems,
    failedItems, setFailedItems,
    potentialDuplicates, setPotentialDuplicates,
    uploadedStatements, setUploadedStatements,
    // iter1107 - awaiting bank bucket
    awaitingBankItems, setAwaitingBankItems, awaitingBankTotalCount,
    fetchStats, fetchPendingItems, fetchProcessedItems,
    fetchDuplicateItems, fetchFailedItems, fetchPotentialDuplicates,
    fetchUploadedStatements, fetchAwaitingBankItems,
  } = useBankFeedTransactions({
    pendingPage, processedPage,
    filterGlCode, filterStartDate, filterEndDate,
    fiscalPeriod,
  });

  // Phase #5 - Lookup map (id → tx) used by BankFeedSaveChangesBar to
  // resolve descriptions and other display fields for queued edits.
  const transactionsByIdMap = useMemo(() => {
    const map = new Map();
    for (const t of pendingItems || []) map.set(t.id, t);
    for (const t of processedItems || []) map.set(t.id, t);
    return map;
  }, [pendingItems, processedItems]);

  // Phase 1 part 3 - specialty fetchers (AI corrections, training, recon,
  // reversals). Page-level UI state (dialog flags, "applying" flags, etc.)
  // remains here; only the *data* + its fetchers move into the hook.
  const {
    correctionHistory, setCorrectionHistory,
    suggestedCorrections, setSuggestedCorrections, loadingCorrections,
    trainingExamples, setTrainingExamples,
    trainingStats, setTrainingStats, loadingTraining,
    reconSummary, setReconSummary,
    detectedReversals, setDetectedReversals, loadingReversals,
    reversedTransactions, setReversedTransactions,
    fetchCorrectionHistory, fetchSuggestedCorrections,
    fetchTrainingExamples, fetchTrainingStats,
    fetchReconSummary, fetchDetectedReversals, fetchReversedTransactions,
  } = useBankFeedInsights();

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  // Starter modal shown BEFORE the OS file picker (guide + limits +
  // template downloads). Every "Upload statement" CTA opens this first.
  const [showUploadStarter, setShowUploadStarter] = useState(false);
  const openUploadStarter = useCallback(() => setShowUploadStarter(true), []);
  // iter1382 · Delete-gap precheck + step-up state.
  const { requireStepUp } = useStepUp();
  const [deletePrecheck, setDeletePrecheck] = useState(null);
  // iter1382 · Connected-account precheck for the DOWNSTREAM upload
  // dialog (BankFeedCenterPage header CTA path — the starter modal
  // there has no bank_account_id context, so we gate here instead).
  const [downstreamConnectedPrecheck, setDownstreamConnectedPrecheck] = useState(null);
  const [downstreamConnectedAck, setDownstreamConnectedAck] = useState(false);
  const [showGapFillModal, setShowGapFillModal] = useState(null); // {bank_account_id, gap_start, gap_end, provider}
  const [pendingFile, setPendingFile] = useState(null);
  // iter1235 - multi-file review flow (>=2 files selected).
  const [showMultiUploadReview, setShowMultiUploadReview] = useState(false);
  const [pendingMultiFiles, setPendingMultiFiles] = useState([]);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  
  // FAD Safety Check State
  const [showFadSafetyModal, setShowFadSafetyModal] = useState(false);
  const [fadSafetyResult, setFadSafetyResult] = useState(null);
  const [pendingUploadAction, setPendingUploadAction] = useState(null); // Stores upload params to retry after safety confirmation
  
  // Phase G - Bulk category re-classification dialog
  const [showBulkCategoryDialog, setShowBulkCategoryDialog] = useState(false);

  // Phase H - Manual link / unlink + Bulk undo classification
  const [linkDialogTx, setLinkDialogTx] = useState(null);            // tx being linked
  // Phase #5/#6 follow-up - when the link dialog is opened from an
  // auto-created row's "Link to existing document…" menu item we route
  // the confirmation through the swap-link endpoint instead of plain
  // link. The dialog itself doesn't need to know; we just remember the
  // mode here and switch endpoints in onLinked.
  const [linkDialogMode, setLinkDialogMode] = useState('link');       // 'link' | 'swap'
  const [linkDialogFocus, setLinkDialogFocus] = useState(null);       // special-item family focus for SmartLink
  // iter1404 Phase 4b · In Books drill (per-tx journal + linked docs)
  const [inBooksTx, setInBooksTx] = useState(null);
  const [showBulkUndoDialog, setShowBulkUndoDialog] = useState(false);
  
  // Dialogs
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  // Sep 2026 · deep link: /bank-feeds?tx=<id> (global search, invoice/bill pages) or ?highlight=<id> (loans) lands on the
  // exact bucket + page holding the row, then scrolls to it and pulses the card / table row.
  const [deepLinkTarget, setDeepLinkTarget] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const txId = p.get('tx') || p.get('highlight');
    if (!txId) return;
    axios.get(`${API}/bank-feeds/locate/${txId}`).then((r) => {
      const loc = r.data || {};
      const bucket = { pending: 'pending', processed: 'processed', duplicates: 'duplicates', failed: 'failed', awaiting_bank: 'pending' }[loc.bucket] || 'pending';
      setActiveTab('transactions'); setMoreSection(null); setTxFilter(bucket);
      setSearchTerm(''); setFilterDirection('all'); setFilterBankAccountId('all'); setFilterSource('all'); setFilterGlCode('all');
      setPeriodFilter('all'); setCustomDateRange({ start: '', end: '' }); setTableFilters({});
      if (bucket === 'pending') setPendingPage(loc.page || 1);
      if (bucket === 'processed') setProcessedPage(loc.page || 1);
      setDeepLinkTarget({ id: txId, bucket, page: loc.page || 1, tries: 0 });
      toast.info(`Bank transaction ${loc.date || ''} · ${loc.description ? String(loc.description).slice(0, 40) : ''}`.trim(), { description: `Opened in ${bucket === 'pending' ? 'Review Needed' : bucket === 'processed' ? 'Completed' : bucket} · page ${loc.page || 1}`, duration: 4000 });
    }).catch(() => toast.error('That bank transaction could not be found in this business'));
    navigate('/bank-feeds', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  useEffect(() => {
    if (!deepLinkTarget) return;
    const sel = `[data-testid="pending-item-${deepLinkTarget.id}"], [data-testid="completed-item-${deepLinkTarget.id}"], [data-testid="tx-row-${deepLinkTarget.id}"]`;
    const el = document.querySelector(sel);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('akf-deeplink-highlight');
      el.setAttribute('data-deeplink-target', 'true');
      const t = setTimeout(() => el.classList.remove('akf-deeplink-highlight'), 9000);
      setDeepLinkTarget(null);
      return () => clearTimeout(t);
    }
    if (deepLinkTarget.tries < 40) {
      const t = setTimeout(() => setDeepLinkTarget((d) => (d ? { ...d, tries: d.tries + 1 } : d)), 400);
      return () => clearTimeout(t);
    }
    setDeepLinkTarget(null);
  }, [deepLinkTarget, pendingItems, processedItems, duplicateItems, failedItems]);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [matchDocumentType, setMatchDocumentType] = useState('invoice');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showUploadResult, setShowUploadResult] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showConnectionSetup, setShowConnectionSetup] = useState(false);
  const [connectionType, setConnectionType] = useState(null);
  
  // Column Mapping State (for when auto-detection fails)
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [columnMappingJob, setColumnMappingJob] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    date_col: null, description_col: null, debit_col: null,
    credit_col: null, amount_col: null, balance_col: null, reference_col: null
  });
  const [applyingMapping, setApplyingMapping] = useState(false);
  
  // Open Banking Setup State
  const [showOpenBankingSetup, setShowOpenBankingSetup] = useState(false);
  const [openBankingStep, setOpenBankingStep] = useState('intro');
  const [connectingBank, setConnectingBank] = useState(false);
  
  // fetchConnections is now sourced from useBankFeedData hook (top of component).
  // It is referenced here only to keep useMonoConnect's onSuccess callback wired.

  // Unified Mono connect hook - staged flow
  const {
    connectBank: monoConnectBank,
    confirmBacklog, dismissBacklog, rollbackConnection,
    connecting: monoConnecting, confirmingBacklog: confirmingBfcBacklog,
    syncStatus: bfcSyncStatus, syncMessage: bfcSyncMessage, backlogInfo: bfcBacklogInfo, resetState: bfcResetState,
    preflightData: bfcPreflightData, preflightLoading: bfcPreflightLoading
  } = useMonoConnect({ onSuccess: fetchConnections, user });
  
  // Cash Advance / Petty Cash State
  const [cashAdvanceGlAccount, setCashAdvanceGlAccount] = useState('');
  const [cashAdvanceExpenseAccount, setCashAdvanceExpenseAccount] = useState('6000');
  const [cashAdvancePaymentType, setCashAdvancePaymentType] = useState('other_payment');
  const [uploadingCashAdvance, setUploadingCashAdvance] = useState(false);
  const [cashAdvanceResult, setCashAdvanceResult] = useState(null);
  const cashAdvanceFileRef = useRef(null);

  // Get selected bank account's currency (for DOM account support)
  const selectedBankCurrency = React.useMemo(() => {
    if (!selectedBankAccountId || !bankAccounts.length) return business?.currency || 'NGN';
    const bank = bankAccounts.find(b => b.id === selectedBankAccountId);
    return bank?.currency || business?.currency || 'NGN';
  }, [selectedBankAccountId, bankAccounts, business]);

  // formatAmount helper - uses SELECTED BANK'S currency by default, but
  // callers can OVERRIDE per-call (e.g. the reconciliation summary card
  // renders each bank's variance in ITS OWN currency, not the currently-
  // selected bank's).
  const formatAmount = (amount, currencyOverride) => {
    return formatCurrency(amount, currencyOverride || selectedBankCurrency);
  };

  // Currency of a specific bank row: row → its bank account → selected bank → base.
  const txCcy = (tx) => tx?.currency || tx?.bank_currency
    || bankAccounts.find((b) => b.id === tx?.bank_account_id)?.currency || selectedBankCurrency;
  const formatTxAmount = (tx, amount = tx?.amount) => formatCurrency(amount, txCcy(tx));
  
  // Format in base currency (for FX equivalents).
  // iter1091 - dropped hardcoded 'NGN' fallback that caused foreign
  // businesses to see ₦ before the business profile loaded.
  const formatBaseAmount = (amount) => {
    return formatCurrency(amount, business?.currency);
  };

  // Auto-detect any bank account with active background processing
  // Shows progress tracker even when user hasn't selected a bank
  const [processingBankAccountId, setProcessingBankAccountId] = useState(null);
  const [processingBankName, setProcessingBankName] = useState('');
  
  useEffect(() => {
    let cancelled = false;
    const checkActive = async () => {
      try {
        const res = await axios.get(`${API}/bank-accounts/active-processing`);
        if (cancelled) return;
        const accounts = res.data?.accounts || [];
        if (accounts.length > 0) {
          setProcessingBankAccountId(accounts[0].id);
          setProcessingBankName(accounts[0].bank_name || '');
        } else {
          setProcessingBankAccountId(null);
        }
      } catch (e) {
        // Silent
      }
    };
    checkActive();
    // Re-check every 10 seconds (lighter than main poll)
    const interval = setInterval(checkActive, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // ============================================
  // URL PARAM HANDLING: Auto-navigate to specific filter
  // e.g., /bank-feed-center?filter=pending
  // ============================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filter = params.get('filter');
    if (filter && ['pending', 'processed', 'duplicates', 'failed', 'awaiting_terms'].includes(filter)) {
      setActiveTab('transactions');
      setTxFilter(filter);
      navigate('/bank-feeds', { replace: true });
    }
    const tab = params.get('tab');
    if (tab === 'reconciliation') {
      setActiveTab('reconciliation');
      const bankId = params.get('bank_account_id');
      if (bankId) setReconBankAccountId(bankId);
      navigate('/bank-feeds', { replace: true });
    }
    // Phase 4 (Feb 2026) - DEPRECATED dead-link handler. The
    // PendingTermsBanner has been removed; "Remind me later" now uses
    // quick_approve=true which creates a setup_status='pending' record
    // surfaced via the canonical OpenTasksWidget on Dashboard. This
    // handler is retained only so any old bookmarked `?action=set-terms`
    // links don't 404.
    if (params.get('action') === 'set-terms') {
      // strip and ignore - modern flow opens the loan/investment edit
      // page directly from Dashboard OpenTasksWidget.
      navigate('/bank-feeds', { replace: true });
    }
    // 'set-terms' action removed with the AssetPrepaymentInputDialog migration
    // Deep-link to the "More" dropdown sub-sections is now handled by the
    // lazy state initialiser at the top of the component (see
    // `_initialSection`). That survives React 18 StrictMode where this
    // useEffect + navigate-strip pattern would lose the param on remount.
    if (params.get('section')) {
      navigate('/bank-feeds', { replace: true });
    }
  }, []); // Run once on mount

  // ============================================
  // SECTION 2: DATA FETCHING CALLBACKS
  // All fetch functions check isMountedRef to prevent
  // state updates on unmounted components
  // ============================================

  // fetchStats sourced from useBankFeedTransactions hook.

  // Fetch notifications for processing completion
  const fetchNotifications = useCallback(async () => {
    // Don't fetch if component is unmounted
    if (!isMountedRef.current) return;
    
    try {
      const response = await axios.get(`${API}/bank-feeds/notifications?unread_only=true`);
      const notifications = response.data.notifications || [];
      
      // Show toast for new notifications
      for (const notif of notifications) {
        toast.success(notif.message, {
          duration: 10000,
          description: notif.title,
          action: {
            label: 'View',
            onClick: () => {
              if (isMountedRef.current) {
                setActiveTab('pending');
                fetchPendingItems();
                fetchStats();
              }
            }
          }
        });
        
        // Mark as read
        await axios.post(`${API}/bank-feeds/notifications/${notif.id}/read`);
      }
      
      // Refresh data if we got notifications and still mounted
      if (notifications.length > 0 && isMountedRef.current) {
        await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
      }
    } catch (error) {
      // Silent fail for notifications
    }
  }, []);

  // fetchPendingItems / fetchProcessedItems sourced from useBankFeedTransactions hook.


  // Reset to page 1 and re-fetch when server-side filters change
  const filtersKey = `${filterGlCode}|${filterStartDate}|${filterEndDate}`;
  const prevFiltersKeyRef = useRef(filtersKey);
  useEffect(() => {
    if (prevFiltersKeyRef.current === filtersKey) return; // initial load handled by loadData
    prevFiltersKeyRef.current = filtersKey;
    setPendingPage(1);
    setProcessedPage(1);
    fetchPendingItems(1);
    fetchProcessedItems(1);
  }, [filterGlCode, filterStartDate, filterEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Iter 809 (G7): SWR-style cache for tax-status to prevent n+1 fetches ──
  // 60s TTL per tx_id + in-flight de-dup so 50 cards mounting in one render
  // produce at most 50 *first* hits (and zero on subsequent scroll passes).
  const taxStatusCacheRef = React.useRef({});       // { txId: { data, ts } }
  const taxStatusInflightRef = React.useRef({});    // { txId: Promise }
  const taxStatusQueueRef = React.useRef([]);       // [{ txId, resolve }] awaiting the next bulk flush
  const taxStatusFlushRef = React.useRef(null);     // pending flush timer
  const TAX_STATUS_TTL_MS = 60_000;

  const fetchTaxStatus = useCallback(async (txId, force = false) => {
    const cache = taxStatusCacheRef.current;
    const now = Date.now();
    const cached = cache[txId];
    if (force) delete cache[txId];
    if (!force && cached && (now - cached.ts) < TAX_STATUS_TTL_MS) {
      return cached.data;
    }
    if (taxStatusInflightRef.current[txId]) {
      return taxStatusInflightRef.current[txId];
    }
    // Rows mounting in the same frame are coalesced into ONE bulk call
    // (200 rows used to fire 200 GETs and freeze the UI behind the
    // browser's per-origin connection limit).
    const p = new Promise((resolve) => {
      taxStatusQueueRef.current.push({ txId, resolve });
      if (!taxStatusFlushRef.current) {
        taxStatusFlushRef.current = setTimeout(async () => {
          taxStatusFlushRef.current = null;
          const batch = taxStatusQueueRef.current.splice(0);
          const ids = [...new Set(batch.map((b) => b.txId))];
          let results = {};
          try {
            if (ids.length === 1) {
              const response = await axios.get(`${API}/bank-feeds/tax-status/${ids[0]}`);
              results = { [ids[0]]: response.data };
            } else {
              for (let i = 0; i < ids.length; i += 200) {
                const response = await axios.post(`${API}/bank-feeds/tax-status/bulk`, { transaction_ids: ids.slice(i, i + 200) });
                Object.assign(results, response.data?.results || {});
              }
            }
          } catch { results = {}; }
          const stateUpdate = {};
          for (const id of ids) {
            if (results[id]) { cache[id] = { data: results[id], ts: Date.now() }; stateUpdate[id] = results[id]; }
            delete taxStatusInflightRef.current[id];
          }
          if (Object.keys(stateUpdate).length > 0) setTaxStatus(prev => ({ ...prev, ...stateUpdate }));
          for (const b of batch) b.resolve(results[b.txId] || null);
        }, 30);
      }
    });
    taxStatusInflightRef.current[txId] = p;
    return p;
  }, []);

  // Bulk variant (Iter 809 G7) - called when ≥5 visible tx rows need
  // fresh tax-status in the same frame. Falls back to per-row on error.
  const fetchTaxStatusBulk = useCallback(async (txIds) => {
    if (!txIds || txIds.length === 0) return {};
    const cache = taxStatusCacheRef.current;
    const now = Date.now();
    const missing = txIds.filter(id =>
      !cache[id] || (now - cache[id].ts) >= TAX_STATUS_TTL_MS
    );
    if (missing.length === 0) {
      return Object.fromEntries(txIds.map(id => [id, cache[id]?.data]));
    }
    try {
      const response = await axios.post(`${API}/bank-feeds/tax-status/bulk`, {
        transaction_ids: missing,
      });
      const results = response.data?.results || {};
      const stateUpdate = {};
      for (const [id, data] of Object.entries(results)) {
        cache[id] = { data, ts: Date.now() };
        stateUpdate[id] = data;
      }
      if (Object.keys(stateUpdate).length > 0) {
        setTaxStatus(prev => ({ ...prev, ...stateUpdate }));
      }
      return Object.fromEntries(txIds.map(id => [id, cache[id]?.data]));
    } catch {
      return {};
    }
  }, []);

  // Invalidates one or many tx ids in the tax-status cache. Used by
  // toggle endpoints AND by save-changes so the next render forces a
  // refetch (Iter 809 F5 - live re-evaluation on inline edits).
  const invalidateTaxStatus = useCallback((txIds) => {
    const ids = Array.isArray(txIds) ? txIds : [txIds];
    const cache = taxStatusCacheRef.current;
    for (const id of ids) delete cache[id];
  }, []);

  // Toggle VAT on a transaction
  const handleToggleVat = useCallback(async (txId) => {
    setTogglingTax(txId);
    try {
      const response = await axios.post(`${API}/bank-feeds/toggle-vat/${txId}`);
      if (response.data.success) {
        toast.success(`VAT ${response.data.action} successfully`);
        // Bust cache so the refresh below hits the server
        invalidateTaxStatus(txId);
        await fetchTaxStatus(txId);
        fetchProcessedItems();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle VAT');
    } finally {
      setTogglingTax(null);
    }
  }, [fetchTaxStatus, fetchProcessedItems, invalidateTaxStatus]);

  // Toggle WHT on a transaction
  const handleToggleWht = useCallback(async (txId) => {
    setTogglingTax(txId);
    try {
      const response = await axios.post(`${API}/bank-feeds/toggle-wht/${txId}`);
      if (response.data.success) {
        toast.success(`WHT ${response.data.action} successfully`);
        invalidateTaxStatus(txId);
        await fetchTaxStatus(txId);
        fetchProcessedItems();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle WHT');
    } finally {
      setTogglingTax(null);
    }
  }, [fetchTaxStatus, fetchProcessedItems, invalidateTaxStatus]);

  // fetchDuplicateItems / fetchFailedItems / fetchPotentialDuplicates sourced from
  // useBankFeedTransactions hook.

  // fetchAiSettings sourced from useBankFeedData hook.

  // fetchUploadedStatements sourced from useBankFeedTransactions hook.





  // fetchBankAccounts sourced from useBankFeedData hook.

  // fetchOpenDocuments sourced from useBankFeedData hook.

  // fetchCorrectionHistory / fetchSuggestedCorrections / fetchTrainingExamples /
  // fetchTrainingStats / fetchReconSummary / fetchDetectedReversals /
  // fetchReversedTransactions all sourced from useBankFeedInsights hook.
  //
  // fetchGlAccounts sourced from useBankFeedData hook (returns full COA).

  // ============================================
  // SECTION 3: EFFECTS (useEffect hooks)
  // Handles data loading, polling, and lifecycle
  // ============================================

  // Jul 2026 - Mono hosted reauth now returns HERE (/bank-feeds) instead of
  // /banks. Handles Mono's callback params (?status=linked&reason=...):
  // toast + reauth marker + server-side confirm fallback. Relink flows still
  // need the backfill modal that lives on /banks - hand those over untouched.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const monoStatus = params.get('status');
    const monoReason = params.get('reason');
    const ourReauth = params.get('reauth');
    const ok = monoStatus === 'linked' || monoStatus === 'successful' || ourReauth === 'success';
    const err = monoStatus === 'error' || monoStatus === 'failed' || ourReauth === 'error';
    const exited = ['closed', 'cancelled', 'exited'].includes(monoStatus || '');
    if (!ok && !err && !exited) { settleAbandonedMonoReturn(); return; }

    let baId = null;
    try {
      if (sessionStorage.getItem('mono_relink_bank_account_id')) {
        window.location.replace('/banks' + window.location.search);
        return;
      }
      baId = sessionStorage.getItem('mono_reauth_bank_account_id');
      sessionStorage.removeItem('mono_reauth_bank_account_id');
      sessionStorage.removeItem('mono_redirect_started_at');
    } catch (_) { /* ignore */ }
    if (!baId) baId = params.get('bank_account_id');

    if (ok) {
      clearConnectUnconfirmed(baId);
      try {
        if (baId) {
          const markers = JSON.parse(localStorage.getItem('mono_reauth_markers') || '{}');
          markers[baId] = new Date().toISOString();
          localStorage.setItem('mono_reauth_markers', JSON.stringify(markers));
        }
      } catch (_) { /* ignore */ }
      if (baId) {
        axios.post(`${API}/open-banking-feeds/mono/confirm-reauth-completed`,
          { bank_account_id: baId })
          .then((r) => {
            if (r?.data?.status === 'account_mismatch') {
              toast.error(r.data.message || 'Your bank login connected a different account. Please reconnect and pick the correct account.', { duration: 15000 });
            } else {
              toast.success('Bank reconnected: catching up your transactions…', { duration: 8000 });
            }
          })
          .catch(() => {
            toast.success('Bank reconnected: catching up your transactions…', { duration: 8000 });
          });
      } else {
        toast.success('Bank reconnected: catching up your transactions…', { duration: 8000 });
      }
    } else if (err) {
      recordConnectUnconfirmed(baId, 'reauth');
      toast.error(
        monoReason
          ? `Bank reconnection failed (${monoReason}). Please try again.`
          : 'Bank reconnection failed. Please try again.',
        { duration: 8000 }
      );
    } else {
      recordConnectUnconfirmed(baId, 'reauth');
      toast.info('Bank reconnection was cancelled. You can try again any time.', { duration: 5000 });
    }

    try {
      const url = new URL(window.location.href);
      ['status', 'reason', 'code', 'state', 'reauth', 'bank_account_id'].forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Instant-paint snapshot: persist everything the first paint needs (both
  // lists, tab counts, uploads + reconciliation badges) once loaded.
  useEffect(() => {
    if (loading) return;
    writeInstant('bankfeed', {
      stats, pendingItems, pendingTotalPages, pendingTotalCount,
      processedItems, processedTotalPages, processedTotalCount,
      bankAccounts, uploadedStatements, reconSummary,
    });
  }, [loading, stats, pendingItems, pendingTotalPages, pendingTotalCount,
      processedItems, processedTotalPages, processedTotalCount,
      bankAccounts, uploadedStatements, reconSummary]);

  const instantHydratedRef = useRef(false);
  useEffect(() => {
    const loadData = async () => {
      // Instant paint: hydrate from last-known snapshot, refresh silently
      // Hydrate from the snapshot only on first mount - later re-runs (page /
      // filter changes) must never overwrite live data with the snapshot.
      const firstRun = !instantHydratedRef.current;
      instantHydratedRef.current = true;
      const cached = firstRun ? readInstant('bankfeed') : null;
      if (cached) {
        if (cached.stats) setStats(cached.stats);
        if (Array.isArray(cached.pendingItems)) {
          setPendingItems(cached.pendingItems);
          if (cached.pendingTotalPages) setPendingTotalPages(cached.pendingTotalPages);
          if (typeof cached.pendingTotalCount === 'number') setPendingTotalCount(cached.pendingTotalCount);
        }
        if (Array.isArray(cached.processedItems)) {
          setProcessedItems(cached.processedItems);
          if (cached.processedTotalPages) setProcessedTotalPages(cached.processedTotalPages);
          if (typeof cached.processedTotalCount === 'number') setProcessedTotalCount(cached.processedTotalCount);
        }
        if (Array.isArray(cached.bankAccounts)) setBankAccounts(cached.bankAccounts);
        if (Array.isArray(cached.uploadedStatements)) setUploadedStatements(cached.uploadedStatements);
        if (cached.reconSummary) setReconSummary(cached.reconSummary);
        setLoading(false);
      } else if (firstRun) {
        setLoading(true);
      }
      
      // Critical data - load first (stats, pending items, tab badge counts)
      try {
        await Promise.all([
          fetchStats(),
          fetchPendingItems(),
          // iter1443 R5 (Henry post-ingest empty-list fix) — pull the
          // In Books list into the CRITICAL batch. Previously it was
          // scheduled with `backgroundFetches` (250 ms delay + 3-at-a-
          // time), so when the user landed on Bank Feeds right after
          // an autofeed ingestion, the KPI counter said "55 In Books"
          // (from `stats.completed`) while the In Books sub-tab still
          // showed "No Processed Transactions" for the first ~1-2 s
          // (until the background batch drained). The counter and the
          // list must land together.
          fetchProcessedItems(),
          fetchBankAccounts(),
          // Tab badges (Uploads count / Reconciliation unmatched count) must
          // paint with the page, not trail in behind a dozen queued requests.
          fetchUploadedStatements(),
          fetchReconSummary()
        ]);
      } catch (error) {
        console.error('Critical data load error:', error);
      }
      
      // Release loading state after critical data
      setLoading(false);
      
      // Non-critical data - load in background WITHOUT saturating the
      // browser's 6-connections-per-origin limit. iter1340: firing all
      // 14 requests at once queued every subsequent click (tab changes,
      // reconciliation, statements) behind them, making the page feel
      // frozen. Run them 3 at a time after a short breather so user
      // clicks always have free connections.
      const backgroundFetches = [
        fetchDuplicateItems,
        fetchFailedItems,
        fetchPotentialDuplicates,
        // iter1107 - awaiting-bank bucket (bank-pending rows).
        fetchAwaitingBankItems,
        fetchAiSettings,
        fetchConnections,
        fetchOpenDocuments,
        fetchCorrectionHistory,
        fetchSuggestedCorrections,
        fetchGlAccounts,
        fetchTrainingExamples,
        fetchTrainingStats,
        // Mono config for Connect Bank widget (sourced from useBankFeedData)
        fetchMonoConfig
      ];
      const CONCURRENCY = 3;
      setTimeout(async () => {
        for (let i = 0; i < backgroundFetches.length; i += CONCURRENCY) {
          // eslint-disable-next-line no-await-in-loop
          await Promise.allSettled(
            backgroundFetches.slice(i, i + CONCURRENCY).map((fn) => fn())
          );
        }
      }, 250);
    };
    
    loadData();
    // Mount-only by design: filters/pagination have their own effects and
    // handlers; re-running this batch on callback identity changes used
    // to fire every critical request 3x on page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stats depend on the fiscal period, which resolves once `business` loads.
  const statsIdentityRef = useRef(fetchStats);
  useEffect(() => {
    if (statsIdentityRef.current === fetchStats) return;
    statsIdentityRef.current = fetchStats;
    fetchStats();
  }, [fetchStats]);

  // Refresh the reconciliation summary whenever its tab is opened so the
  // numbers are current after actions taken elsewhere on the page.
  useEffect(() => {
    if (activeTab === 'reconciliation') {
      fetchReconSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // iter1257 - refetch uploads whenever the tab opens so In Books /
  // Review Needed counts reflect the user's latest review actions.
  useEffect(() => {
    if (activeTab === 'uploads') {
      fetchUploadedStatements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // iter1443 R5 (Henry post-ingest empty-list fix) — refetch the
  // Transactions lists AND stats every time the user opens the
  // Transactions tab. Without this, if new BFTs land while the user
  // is on the Dashboard (Plaid/Mono webhook, streaming ingestion,
  // etc.) and they later switch to Bank Feeds → Transactions, the
  // lists show empty even though the KPI counters read fresh — the
  // user is forced to hit the browser Refresh button just to see
  // their transactions. Mirrors the Uploads / Reconciliation
  // refetches above.
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    // Only on a REAL tab change (StrictMode re-runs and the initial mount are skipped).
    if (prevActiveTabRef.current === activeTab) return;
    prevActiveTabRef.current = activeTab;
    if (activeTab !== 'transactions') return;
    fetchStats();
    fetchPendingItems();
    fetchProcessedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // iter1443 R5 — cross-page background-job completion signal. The
  // global BackgroundJobsProvider polls upload jobs while the user is
  // on any page (Dashboard, Reports, …) but has no wire to trigger a
  // refetch on the Bank Feeds page. When the provider sees a job flip
  // to `completed` it now dispatches a `akf:bank-feed-refresh` window
  // event. This page listens and pulls fresh stats + lists so the
  // moment the user opens Bank Feeds after an ingestion completes,
  // everything is already up-to-date.
  useEffect(() => {
    const onRefresh = () => {
      if (!isMountedRef.current) return;
      Promise.all([
        fetchStats(),
        fetchPendingItems(),
        fetchProcessedItems(),
        fetchUploadedStatements(),
      ]).catch(() => { /* silent */ });
    };
    window.addEventListener('akf:bank-feed-refresh', onRefresh);
    return () => window.removeEventListener('akf:bank-feed-refresh', onRefresh);
  }, [fetchStats, fetchPendingItems, fetchProcessedItems, fetchUploadedStatements]);

  // Handle reclassifyItem from BankReconciliationCenter navigation
  // When user clicks "Record" → Investment/Asset Disposal/Loan Repayment in reconciliation center
  useEffect(() => {
    if (location.state?.reclassifyItem && location.state?.docType) {
      const { reclassifyItem, docType } = location.state;
      // Delay opening dialog to ensure page data is loaded
      const timer = setTimeout(() => {
        switch (docType) {
          case 'investment':
            setInvestmentMatchTransaction(reclassifyItem);
            setShowInvestmentMatchDialog(true);
            break;
          case 'asset_disposal':
            setAssetDisposalTransaction(reclassifyItem);
            setShowAssetDisposalDialog(true);
            break;
          case 'loan_repayment':
            setLoanMatchTransaction(reclassifyItem);
            setShowLoanMatchDialog(true);
            break;
          default:
            break;
        }
      }, 500);
      // Clear the navigation state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
    // Handle multiPayItem from BankReconciliationCenter navigation
    if (location.state?.multiPayItem) {
      const item = location.state.multiPayItem;
      const timer = setTimeout(() => {
        openMultiAllocDialog(item);
      }, 500);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location.state]);


  // Poll for notifications when there's an active upload job
  useEffect(() => {
    // Only poll if there are active background jobs
    if (backgroundJobs.length === 0) return;
    
    const pollInterval = setInterval(() => {
      if (isMountedRef.current) {
        fetchNotifications();
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(pollInterval);
  }, [fetchNotifications, backgroundJobs.length]);

  // WebSocket: Subscribe to real-time bank feed events
  useEffect(() => {
    if (!realtime?.subscribe) return;
    
    // Handler for bank feed processed events
    const handleBankFeedProcessed = (event) => {
      console.log('WebSocket: Bank feed processed', event);
      const { job_id, status, progress, total_transactions, processed_transactions } = event.data || {};
      
      if (job_id) {
        // Update background job state
        setBackgroundJobs(prev => prev.map(job => 
          job.jobId === job_id 
            ? { 
                ...job, 
                status: status || job.status,
                progress: progress || job.progress,
                totalTx: total_transactions || job.totalTx,
                processedTx: processed_transactions || job.processedTx
              }
            : job
        ));
        
        // If completed, refresh data
        if (status === 'completed') {
          toast.success('Bank statement processing complete!');
          Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
        }
      }
    };
    
    // Handler for bank feed uploaded events
    const handleBankFeedUploaded = (event) => {
      console.log('WebSocket: Bank feed uploaded', event);
      fetchUploadedStatements();
    };
    
    // Subscribe to events
    const unsubProcessed = realtime.subscribe(RealtimeEventTypes.BANK_FEED_PROCESSED, handleBankFeedProcessed);
    const unsubUploaded = realtime.subscribe(RealtimeEventTypes.BANK_FEED_UPLOADED, handleBankFeedUploaded);
    
    return () => {
      if (unsubProcessed) unsubProcessed();
      if (unsubUploaded) unsubUploaded();
    };
  }, [realtime, fetchStats, fetchPendingItems, fetchProcessedItems, fetchUploadedStatements]);

  // Cleanup all polling intervals when component unmounts
  // This allows users to navigate away while background processing continues
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear all upload polling intervals on unmount
      uploadPollIntervalsRef.current.forEach((interval) => {
        clearInterval(interval);
      });
      uploadPollIntervalsRef.current.clear();
    };
  }, []);

  // Persist background jobs to localStorage whenever they change
  useEffect(() => {
    if (backgroundJobs.length > 0) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_JOBS, JSON.stringify(backgroundJobs));
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_JOBS);
    }
  }, [backgroundJobs]);

  // Resume polling for active jobs on mount (when user returns to page)
  useEffect(() => {
    const resumePollingForActiveJobs = async () => {
      // Get any active jobs from localStorage
      const activeJobs = backgroundJobs.filter(j => 
        j.status === 'queued' || j.status === 'processing' ||
        j.status === 'awaiting_confirmation' || j.status === 'pending_payment'
      );
      
      for (const job of activeJobs) {
        // Skip if already polling
        if (uploadPollIntervalsRef.current.has(job.jobId)) continue;
        
        // Check job status from backend
        try {
          const statusResponse = await axios.get(`${API}/bank-feeds/upload-job-status/${job.jobId}`);
          const status = statusResponse.data;
          
          if (status.status === 'awaiting_confirmation') {
            // Paused for the user's backlog-fee decision - reopen the dialog
            setBackgroundJobs(prev => prev.map(j => j.jobId === job.jobId ? { ...j, status: 'awaiting_confirmation', statusMessage: status.status_message, billing: status.billing } : j));
            // iter1236 - only auto-open the dialog for RECENT jobs. Stale
            // awaiting_confirmation jobs from hours ago must not ambush
            // the user; they can still resume via the passive banner.
            if (isFreshEnoughForModal(job)) {
              setBacklogDialogJob({ jobId: job.jobId, filename: job.filename, billing: status.billing });
            }
            continue;
          }
          if (status.status === 'pending_payment') {
            // Legacy stale state from the old flow - clear it out honestly
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
            if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
            toast.info(`"${job.filename}" upload was interrupted by a platform update. Please re-upload the statement.`);
            continue;
          }
          if (status.status === 'cancelled') {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
            if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
            continue;
          }
          
          if (status.status === 'completed' || status.status === 'failed' || status.status === 'needs_mapping') {
            // Job already finished - update state
            if (status.status === 'completed') {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
              notifyImportComplete({ title: `"${job.filename}" imported`, added: status.high_confidence || 0, reviewNeeded: status.review_needed || 0, extra: (status.filtered_before_start_date || 0) > 0 ? `${status.filtered_before_start_date} before start month excluded.` : '', navigate });
              if (status.ai_fallback_used) toast.warning(`"${job.filename}": AI classification was degraded. Imported transactions use generic categories and need review.`, { duration: 10000 });
              // Refresh data
              fetchStats();
              fetchPendingItems();
              fetchProcessedItems();
              fetchUploadedStatements();
            } else if (status.status === 'needs_mapping') {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
              setColumnMappingJob({ jobId: job.jobId, filename: job.filename, rawPreview: status.raw_preview });
              setColumnMapping({ date_col: null, description_col: null, debit_col: null, credit_col: null, amount_col: null, balance_col: null, reference_col: null });
              setShowColumnMapping(true);
              toast.info(`"${job.filename}" needs manual column mapping.`);
            } else {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
              toast.error(`Upload "${job.filename}" failed. Please try again.`);
            }
          } else {
            // Job still running - resume polling with timeout safety
            let resumePollCount = 0;
            const MAX_RESUME_POLLS = 150; // 5 minutes at 2s intervals
            const pollInterval = setInterval(async () => {
              if (!isMountedRef.current) {
                clearInterval(pollInterval);
                uploadPollIntervalsRef.current.delete(job.jobId);
                return;
              }
              
              resumePollCount++;
              if (resumePollCount > MAX_RESUME_POLLS) {
                clearInterval(pollInterval);
                uploadPollIntervalsRef.current.delete(job.jobId);
                setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
                toast.info(`Processing "${job.filename}" is taking longer than expected. Refresh to check status.`);
                return;
              }
              
              try {
                const pollResponse = await axios.get(`${API}/bank-feeds/upload-job-status/${job.jobId}`);
                const pollStatus = pollResponse.data;
                
                // Update progress
                setBackgroundJobs(prev => prev.map(j => 
                  j.jobId === job.jobId ? {
                    ...j,
                    progress: pollStatus.progress_percent || j.progress,
                    status: pollStatus.status || 'processing',
                    statusMessage: pollStatus.status_message || j.statusMessage,
                    billing: pollStatus.billing || j.billing,
                    totalTx: pollStatus.total_transactions || j.totalTx,
                    processedTx: pollStatus.processed_transactions || j.processedTx
                  } : j
                ));
                
                if (pollStatus.status === 'awaiting_confirmation') {
                  clearInterval(pollInterval);
                  uploadPollIntervalsRef.current.delete(job.jobId);
                  // iter1236 - all auto-opens go through the shared age gate,
                  // even in polling loops where the job SHOULD be fresh (this
                  // poll only tracks jobs from the current session, but we
                  // enforce it here defensively so we don't re-introduce
                  // the P0 stale-modal regression when someone adds another
                  // caller).
                  if (isFreshEnoughForModal(job)) {
                    setBacklogDialogJob({ jobId: job.jobId, filename: job.filename, billing: pollStatus.billing });
                  }
                  toast.info(`"${job.filename}": your confirmation is needed before backlog transactions can be processed.`);
                  return;
                }
                if (pollStatus.status === 'cancelled') {
                  clearInterval(pollInterval);
                  uploadPollIntervalsRef.current.delete(job.jobId);
                  setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
                  if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
                  if (pollStatus.cancel_reason === 'payment_failed') {
                    notifyUploadCancelled(job.jobId, job.filename, { reason: 'payment_failed', message: pollStatus.error_message });
                  } else {
                    notifyUploadCancelled(job.jobId, job.filename);
                  }
                  return;
                }
                
                if (pollStatus.status === 'completed' || pollStatus.status === 'failed' || pollStatus.status === 'needs_mapping') {
                  clearInterval(pollInterval);
                  uploadPollIntervalsRef.current.delete(job.jobId);
                  
                  if (pollStatus.status === 'completed') {
                    setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
                    notifyImportComplete({ title: `"${job.filename}" imported`, added: pollStatus.high_confidence || 0, reviewNeeded: pollStatus.review_needed || 0, extra: (pollStatus.filtered_before_start_date || 0) > 0 ? `${pollStatus.filtered_before_start_date} before start month excluded.` : '', navigate });
                    fetchStats();
                    fetchPendingItems();
                    fetchProcessedItems();
                    fetchUploadedStatements();
                  } else if (pollStatus.status === 'needs_mapping') {
                    setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
                    setColumnMappingJob({ jobId: job.jobId, filename: job.filename, rawPreview: pollStatus.raw_preview });
                    setColumnMapping({ date_col: null, description_col: null, debit_col: null, credit_col: null, amount_col: null, balance_col: null, reference_col: null });
                    setShowColumnMapping(true);
                    toast.info(`"${job.filename}" needs manual column mapping.`);
                  } else {
                    setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
                    toast.error(`Upload "${job.filename}" failed. Please try again.`);
                  }
                }
              } catch (pollError) {
                console.error('Error polling resumed job:', pollError);
              }
            }, 2000);
            
            uploadPollIntervalsRef.current.set(job.jobId, pollInterval);
          }
        } catch (error) {
          console.error('Error checking job status on resume:', error);
          // Remove stale job if it can't be found
          if (error.response?.status === 404) {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
          }
        }
      }
    };
    
    // Resume polling on mount
    if (backgroundJobs.length > 0) {
      resumePollingForActiveJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // ============================================
  // SECTION 4: EVENT HANDLERS
  // User interaction handlers for transactions,
  // uploads, approvals, rejections, etc.
  // ============================================

  // ---- Backlog fee dialog + job cancel handlers (statement uploads) ----
  const startJobPolling = (jobId, filename) => {
    if (uploadPollIntervalsRef.current.has(jobId)) return;
    let polls = 0;
    const pollInterval = setInterval(async () => {
      if (!isMountedRef.current) { clearInterval(pollInterval); uploadPollIntervalsRef.current.delete(jobId); return; }
      polls++;
      if (polls > 300) { clearInterval(pollInterval); uploadPollIntervalsRef.current.delete(jobId); return; }
      try {
        const { data: s } = await axios.get(`${API}/bank-feeds/upload-job-status/${jobId}`);
        setBackgroundJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, progress: s.progress_percent || j.progress, status: s.status, statusMessage: s.status_message || j.statusMessage, failure: s.failure || j.failure, totalTx: s.total_transactions || j.totalTx, processedTx: s.processed_transactions || j.processedTx } : j));
        if (['completed', 'failed', 'cancelled', 'rejected', 'needs_mapping', 'awaiting_confirmation'].includes(s.status)) {
          clearInterval(pollInterval);
          uploadPollIntervalsRef.current.delete(jobId);
          if (s.status === 'completed') {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
            if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(jobId);
            emitJobToast(jobId, 'completed', () => {
              if (s.ai_fallback_used) {
                toast.warning(`"${filename}" processed, but AI classification was degraded: all ${s.review_needed || 0} transactions were imported with generic categories and need your review.`, { duration: 10000 });
              } else {
                notifyImportComplete({ title: `"${filename}" imported`, added: s.high_confidence || 0, reviewNeeded: s.review_needed || 0, navigate });
              }
              if (s.integrity_warning) {
                toast.warning(<div data-testid="upload-integrity-warning-toast"><strong>&quot;{filename}&quot; imported with a balance warning</strong><div className="text-sm mt-1">{s.integrity_warning}</div></div>, { duration: 20000 });
              }
            });
            Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems(), fetchUploadedStatements()]).catch(console.error);
          } else if (s.status === 'rejected') {
            // iter1238 Phase 8.13g P0.5 — surface the specific backend
            // rejection message (e.g. "This PDF looks like a scanned
            // image…") as a persistent toast so the user knows exactly
            // what to do next.
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
            if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(jobId);
            emitJobToast(jobId, 'rejected', () => {
              const msg = s.status_message || s.error || 'We could not read this PDF\'s layout. Please upload a text-based PDF, or a CSV/Excel export from your bank.';
              toast.error(`"${filename}" — ${msg}`, { duration: 15000 });
            });
            Promise.all([fetchStats(), fetchUploadedStatements()]).catch(console.error);
          } else if (s.status === 'failed') {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
            if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(jobId);
            emitJobToast(jobId, 'failed', () => {
              // iter1238 Phase 8.13b · read s.error before falling back to
              // s.error_message. Backend writes user-facing message to
              // `error` field; the older `error_message` alias was
              // sometimes absent producing an "Unknown error" toast.
              const msg = s.error || s.error_message || s.status_message || 'Unknown error';
              toast.error(`"${filename}" failed: ${msg}`);
            });
          } else if (s.status === 'cancelled') {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
            if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(jobId);
            emitJobToast(jobId, 'cancelled', () => notifyUploadCancelled(jobId, filename));
          } else if (s.status === 'needs_mapping') {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
            setColumnMappingJob({ jobId, filename, rawPreview: s.raw_preview });
            setShowColumnMapping(true);
          } else if (s.status === 'awaiting_confirmation') {
            // iter1236 - defence-in-depth. Only auto-open if job is fresh.
            // iter1238 Phase 8.13b - also skip if user JUST confirmed this
            // job within the last 30 s (race between confirm POST and next
            // poll tick where backend still returns awaiting_confirmation).
            const jobObj = backgroundJobs.find(j => j.jobId === jobId) || { startedAt: new Date().toISOString() };
            const confirmedAt = justConfirmedRef.current.get(jobId);
            const recentlyConfirmed = confirmedAt && (Date.now() - confirmedAt) < 30_000;
            if (isFreshEnoughForModal(jobObj) && !recentlyConfirmed) {
              setBacklogDialogJob({ jobId, filename, billing: s.billing });
            }
          }
        }
      } catch (e) { console.error('poll error:', e); }
    }, 2000);
    uploadPollIntervalsRef.current.set(jobId, pollInterval);
  };

  const handleBacklogConfirm = async (mode) => {
    if (!backlogDialogJob) return;
    const { jobId, filename } = backlogDialogJob;
    // iter1238 Phase 8.13b · Close the modal IMMEDIATELY on click and
    // record just-confirmed so the poll cycle can't re-open it. The
    // API call runs in the background — this makes the click feel
    // instant (was 500-1500ms of "nothing happened").
    justConfirmedRef.current.set(jobId, Date.now());
    setBacklogDialogJob(null);
    // Optimistic status flip so the progress banner updates instantly.
    setBackgroundJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, status: 'processing', statusMessage: 'Confirming…' } : j));
    setBacklogDialogBusy(true);
    try {
      const { data } = await axios.post(`${API}/bank-feeds/upload-jobs/${jobId}/confirm`, { mode });
      if (data.success) {
        toast.success(data.message);
        setBackgroundJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, status: 'processing', statusMessage: data.message } : j));
        startJobPolling(jobId, filename);
      } else if (data.declined) {
        setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
        if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(jobId);
        toast.error(<div><strong>Payment declined</strong><div className="text-sm mt-1">{data.message}</div></div>, { duration: 12000 });
      }
    } catch (err) {
      // Rollback optimistic flip on error so the user sees the real state.
      setBackgroundJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, status: 'awaiting_confirmation', statusMessage: 'Confirmation failed - please try again' } : j));
      // Also clear the just-confirmed guard so the modal can re-open.
      justConfirmedRef.current.delete(jobId);
      toast.error(err.response?.data?.detail || 'Failed to confirm. Please try again.');
    } finally {
      setBacklogDialogBusy(false);
    }
  };

  const handleBacklogCancel = async (explicitCancel) => {
    if (!backlogDialogJob) return;
    const { jobId, filename } = backlogDialogJob;
    // Also mark as just-confirmed so polling can't re-open the modal
    // after the user explicitly dismissed / cancelled it.
    justConfirmedRef.current.set(jobId, Date.now());
    setBacklogDialogJob(null);
    if (!explicitCancel) return; // dialog dismissed: job stays awaiting (Review button reopens)
    // iter1238 Phase 8.13b · Loading state so the user sees the cancel
    // action is in flight (was previously 500-800 ms of silence).
    setCancellingJobIds(prev => new Set(prev).add(jobId));
    setBackgroundJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, status: 'cancelling', statusMessage: 'Cancelling…' } : j));
    try {
      await axios.post(`${API}/bank-feeds/upload-jobs/${jobId}/cancel`);
      setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
      if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(jobId);
      emitJobToast(jobId, 'cancelled', () => notifyUploadCancelled(jobId, filename, { reason: 'user_cancelled_import' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel upload');
    } finally {
      setCancellingJobIds(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleCancelJob = async (job) => {
    // iter1238 Phase 8.13b · Immediate visual feedback while the API
    // call is in flight. Also optimistically flip the status pill to
    // "Cancelling…" so the user isn't left wondering.
    if (cancellingJobIds.has(job.jobId)) return; // already in flight
    setCancellingJobIds(prev => new Set(prev).add(job.jobId));
    setBackgroundJobs(prev => prev.map(j => j.jobId === job.jobId ? { ...j, status: 'cancelling', statusMessage: 'Cancelling…' } : j));
    try {
      await axios.post(`${API}/bank-feeds/upload-jobs/${job.jobId}/cancel`);
      const iv = uploadPollIntervalsRef.current.get(job.jobId);
      if (iv) { clearInterval(iv); uploadPollIntervalsRef.current.delete(job.jobId); }
      setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
      if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
      if (backlogDialogJob?.jobId === job.jobId) setBacklogDialogJob(null);
      emitJobToast(job.jobId, 'cancelled', () => notifyUploadCancelled(job.jobId, job.filename));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 404) {
        setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
        if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
      } else {
        toast.error(detail || 'Failed to cancel upload');
      }
    } finally {
      setCancellingJobIds(prev => {
        const next = new Set(prev);
        next.delete(job.jobId);
        return next;
      });
    }
  };

  // iter1230 - Bridge from the global BackgroundJobsContext to the
  // in-page BankFeedCenterPage state so a job registered from MyBanksPage
  // (or hydrated from the server on a fresh session) also shows up in
  // this page's polling loop AND auto-opens the backlog-fee dialog when
  // it enters awaiting_confirmation. Without this, tapping the global
  // banner's "Confirm backlog fee" CTA would land here but the dialog
  // would stay closed.
  useEffect(() => {
    if (!backgroundJobsCtx?.jobs?.length) return;
    setBackgroundJobs((prev) => {
      const known = new Set(prev.map((j) => j.jobId));
      const additions = [];
      for (const cj of backgroundJobsCtx.jobs) {
        if (known.has(cj.jobId)) continue;
        additions.push({
          jobId: cj.jobId,
          filename: cj.filename,
          startedAt: cj.startedAt || new Date().toISOString(),
          status: cj.status || 'queued',
          progress: cj.progress || 0,
          totalTx: cj.totalTx || 0,
          processedTx: cj.processedTx || 0,
          statusMessage: cj.statusMessage,
          billing: cj.billing || null,
        });
      }
      return additions.length ? [...prev, ...additions] : prev;
    });

    // iter1238 Phase 8.9 — Auto-opening modal REMOVED. Users control when
    // to review a pending backlog fee via the "Confirm / Cancel backlog"
    // dropdown on the Bank Account page. No more page-load ambush.
    // (Deprecated helper `isFreshEnoughForModal` still used by the manual
    // review path where user actively clicks a job.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundJobsCtx?.jobs]);

  const handleReviewJob = async (job) => {
    try {
      const { data: s } = await axios.get(`${API}/bank-feeds/upload-job-status/${job.jobId}`);
      // iter1238 Phase 8.8 — stale preview_stage jobs are auto-cleaned;
      // if the user reaches this path anyway, silently cancel and prompt
      // them to re-upload rather than showing wrong estimates.
      if (s.status === 'awaiting_confirmation' && s.billing && s.billing.preview_stage) {
        try { await axios.post(`${API}/bank-feeds/upload-jobs/${job.jobId}/cancel`); } catch { /* noop */ }
        setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
        if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
        toast.info(`"${job.filename}" was paused with an older estimator we retired. Please re-upload — the new flow will give you the exact count.`);
        return;
      }
      if (s.status === 'awaiting_confirmation') {
        setBacklogDialogJob({ jobId: job.jobId, filename: job.filename, billing: s.billing });
      } else if (s.status === 'pending_payment') {
        setBackgroundJobs(prev => prev.filter(j => j.jobId !== job.jobId));
        if (backgroundJobsCtx?.removeJob) backgroundJobsCtx.removeJob(job.jobId);
        toast.info(`"${job.filename}" upload was interrupted by a platform update. Please re-upload the statement.`);
      } else {
        startJobPolling(job.jobId, job.filename);
      }
    } catch {
      toast.error('Could not load job details');
    }
  };

  // Delete uploaded statement - show custom confirmation dialog
  const handleDeleteStatement = async (statementId, filename) => {
    setDeleteTarget({ id: statementId, filename });
    setDeletePrecheck(null);
    // iter1382 · Precheck: does this delete leave a gap the OB feed
    // has already continued past? Runs BEFORE the dialog opens so we
    // can show the right severity of warning + require step-up.
    try {
      const { data } = await axios.get(
        `${API}/bank-feeds/uploaded-statements/${statementId}/delete-gap-precheck`
      );
      setDeletePrecheck(data);
    } catch (_) {
      setDeletePrecheck(null);
    }
    setShowDeleteDialog(true);
  };

  // iter1257 - Download the original uploaded statement file (GridFS-backed)
  const handleDownloadStatement = async (statement) => {
    setDownloadingStatementId(statement.id);
    try {
      const res = await axios.get(
        `${API}/bank-feeds/uploaded-statements/${statement.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = statement.filename || 'statement';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('The original file is not available for this upload.');
    } finally {
      setDownloadingStatementId(null);
    }
  };
  
  // Confirm delete statement
  const confirmDeleteStatement = async () => {
    if (!deleteTarget) return;
    const { id: statementId, filename } = deleteTarget;

    // iter1382 · Step-up gate. If precheck flagged this delete as
    // gap-creating AND reconciliation is clean, require a fresh
    // biometric/password proof before proceeding. Web users
    // automatically get password; PWA/native get biometric.
    if (deletePrecheck?.requires_step_up) {
      const ok = await requireStepUp({
        reason: `Delete "${filename}" — this will create a gap in your bank feed.`,
        force: true, // always re-verify for this destructive path
      });
      if (!ok) {
        toast.info('Delete cancelled.');
        return;
      }
    }

    setShowDeleteDialog(false);
    setDeletingStatementId(statementId);
    const _willGap = !!deletePrecheck?.will_create_gap;
    const _gapContext = _willGap ? {
      bank_account_id: deletePrecheck?.bank_account_id,
      gap_start: deletePrecheck?.my_period_start,
      gap_end: deletePrecheck?.ob_first_row_date,
    } : null;
    
    try {
      const response = await axios.delete(`${API}/bank-feeds/uploaded-statements/${statementId}`, {
        timeout: 300000 // 5 minute timeout for delete operations
      });
      if (response.data.success) {
        // Background task started - show info message, not success
        toast.info(`Deleting "${filename}"... This runs in background and may take a moment.`, {
          duration: 5000
        });
        
        // Poll for deletion completion using the new status endpoint
        const pollDeletionStatus = async () => {
          let attempts = 0;
          const maxAttempts = 60; // 2 minutes max (2s interval)
          
          const checkStatus = async () => {
            attempts++;
            try {
              // Use the dedicated status endpoint
              const statusRes = await axios.get(`${API}/bank-feeds/uploaded-statements/${statementId}/status`);
              const status = statusRes.data.status;
              
              if (status === 'deleted') {
                // Deletion complete
                toast.success(`Statement "${filename}" deleted successfully!`);
                await Promise.all([
                  fetchStats(),
                  fetchPendingItems(),
                  fetchProcessedItems(),
                  fetchUploadedStatements()
                ]);
                setDeletingStatementId(null);
                // iter1382 · If this delete created a gap the OB feed
                // has already continued past, auto-open the gap-fill
                // modal so the user can either re-upload the missing
                // period OR restore from stored OB rows.
                if (_gapContext) setShowGapFillModal(_gapContext);
                return;
              } else if (status === 'delete_failed') {
                toast.error(`Failed to delete "${filename}": ${statusRes.data.error || 'Unknown error'}`);
                setDeletingStatementId(null);
                return;
              } else if (status === 'deleting') {
                // Still deleting, check again
                if (attempts < maxAttempts) {
                  setTimeout(checkStatus, 2000);
                } else {
                  toast.warning(`Deletion of "${filename}" is taking longer than expected. Please refresh the page.`);
                  setDeletingStatementId(null);
                }
              } else {
                // Status is 'exists' - unexpected, stop polling
                toast.warning(`Unexpected status for "${filename}". Please refresh the page.`);
                setDeletingStatementId(null);
              }
            } catch (err) {
              // If status endpoint returns 404, statement might be deleted
              if (err.response?.status === 404) {
                toast.success(`Statement "${filename}" deleted successfully!`);
                await Promise.all([
                  fetchStats(),
                  fetchPendingItems(),
                  fetchProcessedItems(),
                  fetchUploadedStatements()
                ]);
                if (_gapContext) setShowGapFillModal(_gapContext);
              }
              setDeletingStatementId(null);
            }
          };
          
          // Start polling after a short delay
          setTimeout(checkStatus, 2000);
        };
        
        pollDeletionStatus();
      }
    } catch (error) {
      if (!showPeriodLockToast(error, navigate, { id: `stmt-delete-${statementId}` })) {
        toast.error(error.response?.data?.detail || 'Failed to start deletion');
      }
      setDeletingStatementId(null);
    } finally {
      setDeleteTarget(null);
    }
  };

  // AI Settings toggle handler
  const handleToggleMediumConfidence = async (enabled) => {
    setSavingAiSettings(true);
    try {
      const response = await axios.put(`${API}/bank-feeds/ai-settings?auto_post_medium_confidence=${enabled}`);
      if (response.data.success) {
        setAiSettings(prev => ({ ...prev, auto_post_medium_confidence: enabled }));
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to update AI settings');
    } finally {
      setSavingAiSettings(false);
    }
  };

  // Duplicate transaction handlers
  const handleProcessDuplicateAnyway = async (transactionId) => {
    try {
      const response = await axios.post(`${API}/bank-feeds/duplicates/${transactionId}/process-anyway`);
      if (response.data.success) {
        toast.success('Transaction moved to Review Needed');
        await Promise.all([fetchStats(), fetchDuplicateItems(), fetchPendingItems()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process transaction');
    }
  };

  const handleDismissDuplicate = async (transactionId) => {
    try {
      const response = await axios.post(`${API}/bank-feeds/duplicates/${transactionId}/dismiss`);
      if (response.data.success) {
        toast.success('Duplicate dismissed');
        await Promise.all([fetchStats(), fetchDuplicateItems()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss duplicate');
    }
  };

  // Failed transaction handlers
  const handleRetryFailed = async (transactionId) => {
    try {
      const response = await axios.post(`${API}/bank-feeds/failed/${transactionId}/retry`);
      if (response.data.success) {
        toast.success('Transaction queued for retry');
        await Promise.all([fetchStats(), fetchFailedItems(), fetchPendingItems()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry transaction');
    }
  };

  const handleDismissFailed = async (transactionId) => {
    try {
      const response = await axios.post(`${API}/bank-feeds/failed/${transactionId}/dismiss`);
      if (response.data.success) {
        toast.success('Failed transaction dismissed');
        await Promise.all([fetchStats(), fetchFailedItems()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss transaction');
    }
  };

  // Resolve potential duplicate
  const handleResolvePotentialDuplicate = async (transactionId, action) => {
    try {
      setProcessingId(transactionId);
      const response = await axios.post(`${API}/bank-feeds/potential-duplicates/${transactionId}/resolve`, { action });
      if (response.data.success) {
        const messages = {
          skip: 'Transaction skipped. Existing journal retained',
          correct_bank: 'Journal corrected with correct bank',
          process_normally: 'Transaction moved to Review for normal processing'
        };
        toast.success(messages[action] || 'Resolved');
        await Promise.all([fetchStats(), fetchPotentialDuplicates(), fetchPendingItems()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve');
    } finally {
      setProcessingId(null);
    }
  };

  // Create inter-bank transfer
  const handleCreateTransfer = async (transferData) => {
    try {
      const response = await axios.post(`${API}/bank-transfer`, transferData);
      if (response.data.id) {
        toast.success(response.data.message);
        setShowTransferModal(false);
        await fetchStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  // Period lock check helper - returns true if locked (and shows dialog), false if not locked
  const checkPeriodLock = async (transactionDate, onOriginalDate, onToday) => {
    if (!transactionDate) return false;
    try {
      const res = await axios.get(`${API}/accounting/periods/check-date/${transactionDate}`);
      if (res.data && !res.data.allowed) {
        setPeriodLockDialog({
          open: true,
          transactionDate,
          lockedPeriod: transactionDate.substring(0, 7),
          onPostToOriginalDate: onOriginalDate,
          onPostToToday: onToday,
        });
        return true; // locked
      }
    } catch {
      // If check fails (404, network), proceed without blocking
    }
    return false; // not locked
  };

  // Quick Approve - uses AI's suggested classification
  // June 2026 - approve-time duplicate guard (409 existing_match_found)
  const [existingMatchPrompt, setExistingMatchPrompt] = useState(null); // {item, match, retryParams}
  const [existingMatchBusy, setExistingMatchBusy] = useState(false);

  const handleApproveError = (error, item, retryParams) => {
    const det = error?.response?.data?.detail;
    if (error?.response?.status === 409 && det?.code === 'existing_match_found') {
      setExistingMatchPrompt({ item, match: det.match || {}, retryParams: retryParams || { quick_approve: true } });
      return;
    }
    toast.error((typeof det === 'string' && det) || 'Failed to approve');
  };

  const linkToExistingEntry = async () => {
    if (!existingMatchPrompt) return;
    setExistingMatchBusy(true);
    try {
      await axios.post(`${API}/bank-feeds/link-to-existing/${existingMatchPrompt.item.id}`, null, {
        params: { journal_id: existingMatchPrompt.match.journal_id },
      });
      toast.success('Linked to the existing entry - no duplicate posting created');
      setExistingMatchPrompt(null);
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to link');
    } finally {
      setExistingMatchBusy(false);
    }
  };

  const forcePostAnyway = async () => {
    if (!existingMatchPrompt) return;
    setExistingMatchBusy(true);
    try {
      await axios.post(`${API}/bank-feeds/approve/${existingMatchPrompt.item.id}`, null, {
        params: { ...(existingMatchPrompt.retryParams || {}), force: true },
      });
      toast.success('Transaction posted');
      setExistingMatchPrompt(null);
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to post');
    } finally {
      setExistingMatchBusy(false);
    }
  };

  // iter1100 - Post-approve toast that surfaces auto-learn outcomes with
  // an Undo action. Handles the four `learning.status` shapes from
  // /bank-feeds/approve: created | reinforced | conflict | skipped.
  const _showLearningToast = (learning) => {
    if (!learning || !learning.status) return;
    if (learning.status === 'created' && learning.rule_id) {
      toast.success(
        `Learned: future ${learning.counterparty} → ${learning.gl_code || learning.classification}`,
        {
          duration: 8000,
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                await axios.delete(`${API}/ai-corrections/training-examples/${learning.rule_id}`);
                toast.success('Rule removed. Future transactions will ask again.');
              } catch {
                toast.error('Could not undo the rule.');
              }
            },
          },
        },
      );
    } else if (learning.status === 'reinforced' && learning.times_matched) {
      toast.info(
        `Reinforced rule for ${learning.counterparty} (matched ${learning.times_matched}x)`,
        { duration: 4000 },
      );
    } else if (learning.status === 'conflict') {
      toast.warning(
        `You classified ${learning.counterparty} differently before (${learning.existing_gl}). Kept the old rule.`,
        { duration: 6000 },
      );
    }
  };

  // Bulk / auto approvals must not introduce AI tax assumptions - only user-toggled VAT/WHT.
  const bulkTaxGuardRef = useRef(false);
  const handleQuickApprove = async (itemOrId) => {
    // Check if accounting start date is set - block save if not
    if (business && !business.accounting_start_date) {
      setShowStartDateModal(true);
      return;
    }
    
    // Support both transaction object and ID
    const transactionId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
    const item = typeof itemOrId === 'string' 
      ? filteredPendingItems.find(i => i.id === itemOrId) 
      : itemOrId;
    
    if (!item) {
      toast.error('Transaction not found');
      return;
    }

    // iter1094-fix-uncategorized (Aug 2026, corrected) — refuse quick-approve
    // ONLY when the row has no GL code chosen. Having a specific GL code
    // (either staged via the inline dropdown, saved on the row from a
    // Re-classify, or set by AI) is by itself proof that the transaction
    // has been categorised - the sub_category field is a type hint (e.g.
    // "expense", "software_subscription"), NOT the category. Previously
    // we mistakenly treated ai_sub_category='expense' as "uncategorised"
    // and blocked approvals of properly-classified rows (e.g. an outflow
    // reassigned to Software & Subscriptions still had sub_category='expense').
    const stagedEditForCheck = pendingGlEdits.get(item.id);
    const hasStagedGl = !!(stagedEditForCheck && stagedEditForCheck.glCode);
    const hasGlOnRow = !!(item.gl_code || item.ai_gl_debit || item.ai_gl_credit);
    const explicitCategory = (item.category || '').toString().trim().toLowerCase();
    const isExplicitlyUncategorised = explicitCategory === 'uncategorized' || explicitCategory === 'uncategorised';
    if (!hasStagedGl && (!hasGlOnRow || isExplicitlyUncategorised)) {
      toast.error('Pick a category before approving', {
        description: 'Uncategorized transactions cannot be posted. Choose a category (or use Re-classify) then approve.',
      });
      return;
    }

    // iter921L - Staged-GL-edit commit (was previously only in handleApprove).
    // The actual Approve button in Review Needed routes through THIS handler.
    // If the user inline-edited the GL category on this row, we MUST:
    //   1. Commit the change via /reclassify with the inferred document_type
    //      AND sub_category derived from the new GL (single source of truth
    //      via inferDocFromGl). Without this, the backend keeps the AI's
    //      stale sub_category → wrong document type + wrong toast wording.
    //   2. Mutate the in-memory item so downstream Special-Item routing AND
    //      the toast text see the new values.
    //   3. Let the new family decide whether to open the linkage modal,
    //      NOT the stale ai_sub_category (was opening the Investment modal
    //      for equity inflows after the user changed GL - exactly the bug
    //      the user reported about capital_contribution).
    const stagedEdit = pendingGlEdits.get(item.id);
    const userOverrodeGl = !!stagedEdit;
    let inferredFromNewGl = null;
    if (stagedEdit) {
      inferredFromNewGl = inferDocFromGl(
        stagedEdit.glCode,
        item.direction,
        availableGlAccounts,
      );
      try {
        const reclassifyBody = {
          new_classification: inferredFromNewGl.sub_category,
          new_document_type: inferredFromNewGl.document_type,
          reason: 'Inline GL change committed via Approve',
          apply_learning: true,
        };
        if (stagedEdit.isInflow) reclassifyBody.new_gl_credit = stagedEdit.glCode;
        else reclassifyBody.new_gl_debit = stagedEdit.glCode;
        await axios.post(`${API}/bank-feeds/reclassify/${item.id}`, reclassifyBody);
        if (stagedEdit.isInflow) {
          item.ai_gl_credit = stagedEdit.glCode;
          item.ai_gl_credit_name = stagedEdit.glName;
        } else {
          item.ai_gl_debit = stagedEdit.glCode;
          item.ai_gl_debit_name = stagedEdit.glName;
        }
        item.ai_document_type = inferredFromNewGl.document_type;
        item.ai_sub_category = inferredFromNewGl.sub_category;
        item.classification = inferredFromNewGl.sub_category;
        setPendingGlEdits((prev) => {
          if (!prev.has(item.id)) return prev;
          const next = new Map(prev);
          next.delete(item.id);
          return next;
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not apply the GL change. Approval aborted.');
        return;
      }
    }

    // Phase 3 (Feb 2026) - special-item intercept. For loan/investment/
    // fixed_asset/prepayment/loan_receivable transactions, the row
    // Approve button MUST open SpecialItemDecisionModal so the user can
    // choose Match-existing vs Track-as-new. Bypassing into quick_approve
    // here would skip the candidate-matching UX and post journals into
    // the wrong GL (the AI's "best guess" instead of the user-confirmed
    // tracked item).
    //
    // iter921L - when the user overrode the GL, the SOURCE OF TRUTH is the
    // family inferred from the NEW GL, NOT the AI's stale ai_sub_category.
    // This is what stops the Investment modal from popping up after the
    // user re-categorises an inflow to an equity GL (capital contribution).
    const subCategory = userOverrodeGl
      ? inferredFromNewGl.sub_category
      : (item.ai_sub_category || item.sub_category || item.classification);
    const docTypeFromAI = userOverrodeGl
      ? inferredFromNewGl.document_type
      : (item.ai_document_type || item.document_type);
    // Sep 2026 - the GL shown on the row is authoritative: AI labels only
    // count when the GL's account class is compatible (an equity receipt
    // tagged "loan" no longer opens the Loan modal).
    const effectiveGl = stagedEdit?.glCode || (item.direction === 'inflow' ? item.ai_gl_credit : item.ai_gl_debit) || item.gl_code;
    const specialFamily = detectSpecialFamily({
      glCode: effectiveGl, direction: item.direction, docType: docTypeFromAI, subCategory, chartOfAccounts: availableGlAccounts,
    });
    if (specialFamily) {
      setSpecialItemModal({ open: true, transaction: item, docType: specialFamily });
      return;
    }
    
    setIsApproving(transactionId);
    setQuickApproving(transactionId);

    // iter1094-doc-sync (Aug 2026) - Include the staged GL edit as query
    // params so the backend uses the user's chosen category instead of
    // falling through to the hard-coded fallback deep in doc_creator
    // (which was landing every uncategorised-then-reclassified row on
    // GL 6000 - Salaries & Wages).
    const stagedEditForApprove = pendingGlEdits.get(item.id);
    // Precedence: staged edit → the category the row DISPLAYS (ai_gl_*) → legacy gl_code mirror.
    const stagedGlCode = stagedEditForApprove?.glCode
      || (item.direction === 'outflow' ? item.ai_gl_debit : item.ai_gl_credit)
      || item.gl_code;
    const glQuery = (stagedGlCode ? `&gl_code=${encodeURIComponent(stagedGlCode)}` : '') + (bulkTaxGuardRef.current ? '&user_tax_only=true' : '');
    const clearStagedEditOnSuccess = () => {
      if (stagedEditForApprove) {
        setPendingGlEdits((prev) => {
          const next = new Map(prev);
          next.delete(item.id);
          return next;
        });
      }
    };

    // Check period lock - show dialog if locked
    const today = new Date().toISOString().split('T')[0];
    const txDate = item.date;
    const isLocked = await checkPeriodLock(
      txDate,
      async () => {
        // Post to original date (override lock)
        try {
          const response = await axios.post(`${API}/bank-feeds/approve/${transactionId}?quick_approve=true&allow_locked_period=true${glQuery}`, {
            classification: item.classification || item.ai_sub_category,
            quick_approve: true
          });
          if (response.data.success) {
            toast.success(`Recorded as ${describeApprovedTx(item)}`);
            // iter1100-fix - fire learning toast on locked-period override path too
            _showLearningToast(response.data.learning);
            clearStagedEditOnSuccess();
            await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
          } else {
            toast.error(response.data.message || 'Failed to process transaction. It has been returned to review.');
          }
        } catch (error) {
          handleApproveError(error, item, { quick_approve: true });
        } finally {
          setQuickApproving(null);
          setIsApproving(null);
        }
      },
      async () => {
        // Post to today's date
        try {
          const response = await axios.post(`${API}/bank-feeds/approve/${transactionId}?quick_approve=true&override_date=${today}${glQuery}`, {
            classification: item.classification || item.ai_sub_category,
            quick_approve: true
          });
          if (response.data.success) {
            toast.success(`Recorded as ${describeApprovedTx(item)} (posted to today)`);
            // iter1100-fix - fire learning toast on locked-period override-to-today path too
            _showLearningToast(response.data.learning);
            clearStagedEditOnSuccess();
            await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
          } else {
            toast.error(response.data.message || 'Failed to process transaction. It has been returned to review.');
          }
        } catch (error) {
          handleApproveError(error, item, { quick_approve: true });
        } finally {
          setQuickApproving(null);
          setIsApproving(null);
        }
      }
    );

    if (isLocked) return; // Dialog is showing, callbacks will handle it

    try {
      const response = await axios.post(`${API}/bank-feeds/approve/${transactionId}?quick_approve=true${glQuery}`, {
        classification: item.classification || item.ai_sub_category,
        quick_approve: true
      });

      if (response.data.success) {
        toast.success(`Recorded as ${describeApprovedTx(item)}`);
        // iter1100 - if the backend auto-learned a rule from this approval,
        // show a friendly toast with a one-click Undo so users see what
        // was learned and can reverse it instantly.
        _showLearningToast(response.data.learning);
        clearStagedEditOnSuccess();
        await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
      } else {
        toast.error(response.data.message || 'Failed to process transaction. It has been returned to review.');
      }
    } catch (error) {
      handleApproveError(error, item, { quick_approve: true });
    } finally {
      setQuickApproving(null);
      setIsApproving(null);
    }
  };

  // Open Manual Process Dialog
  const openManualProcessDialog = (item) => {
    setManualProcessItem(item);
    setManualClassification(item.classification || '');
    setManualDescription(item.description || '');
    setShowManualProcessDialog(true);
  };

  // Handle Manual Processing Submission
  const handleManualProcess = async () => {
    // Check if accounting start date is set - block save if not
    if (business && !business.accounting_start_date) {
      setShowStartDateModal(true);
      return;
    }
    
    if (!manualProcessItem || !manualClassification) {
      toast.error('Please select a classification');
      return;
    }
    
    setManualProcessing(true);
    try {
      const response = await axios.post(`${API}/bank-feeds/process-manual`, {
        transaction_id: manualProcessItem.id,
        classification: manualClassification,
        description: manualDescription,
        amount: manualProcessItem.amount,
        direction: manualProcessItem.direction,
        date: manualProcessItem.date,
        bank_account_id: manualProcessItem.bank_account_id
      });
      
      if (response.data.success || response.data.journal_id) {
        toast.success('Transaction processed manually');
        setShowManualProcessDialog(false);
        setManualProcessItem(null);
        setManualClassification('');
        setManualDescription('');
        await Promise.all([fetchStats(), fetchFailedItems(), fetchProcessedItems()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Manual processing failed');
    } finally {
      setManualProcessing(false);
    }
  };

  // File selection handler - opens dialog for bank account selection
  const handleFileSelect = (event) => {
    // iter1235 - Multi-file upload with pre-confirm review. When the
    // user picks 1 file we still use the single-file inline dialog
    // (unchanged UX). Two or more files → open the review dialog.
    const filesList = Array.from(event.target.files || []);
    if (!filesList.length) return;

    const validExtensions = ['.csv', '.txt', '.pdf', '.xlsx', '.xls', '.ofx', '.qfx'];
    const rejected = [];
    const accepted = [];
    filesList.forEach((f) => {
      const okType = validExtensions.some((ext) => f.name.toLowerCase().endsWith(ext));
      if (!okType) rejected.push(f.name);
      else accepted.push(f);
    });
    if (rejected.length) {
      toast.error(
        `Unsupported file${rejected.length > 1 ? 's' : ''}: ${rejected.join(', ')}. Use CSV, PDF, Excel, OFX, or QFX.`
      );
    }
    if (!accepted.length) {
      // Reset the file input so re-selecting the same file re-triggers.
      if (event.target) event.target.value = '';
      return;
    }

    if (accepted.length === 1) {
      // Single-file: keep the existing bank-account-picker dialog.
      setPendingFile(accepted[0]);
      setShowUploadDialog(true);
    } else {
      // Multi-file: open the review dialog.
      setPendingMultiFiles(accepted);
      setShowMultiUploadReview(true);
    }
    // Reset the input so picking the same set again re-triggers.
    if (event.target) event.target.value = '';
  };

  // Handle Cash Advance / Petty Cash file upload
  const handleCashAdvanceFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!cashAdvanceGlAccount) {
      toast.error('Please select a Cash Source Account first');
      return;
    }
    
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!hasValidExtension) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    setUploadingCashAdvance(true);
    setCashAdvanceResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('gl_account_code', cashAdvanceGlAccount);
    formData.append('expense_gl_code', cashAdvanceExpenseAccount);
    formData.append('payment_type', cashAdvancePaymentType);
    
    try {
      const response = await axios.post(`${API}/bank-feeds/upload-cash-advance-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCashAdvanceResult(response.data);
      toast.success(response.data.message || 'Cash advance processed successfully!');
      
      // Refresh data
      fetchStats();
      fetchPendingItems();
      fetchProcessedItems();
      
    } catch (error) {
      console.error('Cash advance upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process cash advance file');
    } finally {
      setUploadingCashAdvance(false);
      if (cashAdvanceFileRef.current) {
        cashAdvanceFileRef.current.value = '';
      }
    }
  };

  // Validate statement against selected bank account
  const handleValidateStatement = async () => {
    if (!pendingFile || !selectedBankAccountId) return;
    
    setValidating(true);
    setValidationResult(null);
    
    const formData = new FormData();
    formData.append('file', pendingFile);
    formData.append('bank_account_id', selectedBankAccountId);
    
    try {
      const response = await axios.post(`${API}/bank-feeds/validate-statement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setValidationResult(response.data);
      
      if (!response.data.is_valid) {
        setShowValidationWarning(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Check FAD safety before processing uploads
  const checkFadSafety = async (startDate) => {
    if (!startDate) return { can_proceed: true }; // If no date, allow proceeding
    
    try {
      const response = await axios.post(
        `${API}/opening-position/validate-bank-connection`,
        null,
        {
          params: { start_date: startDate }
        }
      );
      return response.data;
    } catch (error) {
      console.error('FAD safety check failed:', error);
      // On error, allow proceeding (fail open for better UX)
      return { can_proceed: true };
    }
  };

  // Extract start date from validation result's date_range
  const extractStartDate = (dateRange) => {
    if (!dateRange) return null;
    // Format: "2024-01-01 to 2024-01-31"
    const match = dateRange.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  // Handle FAD safety confirmation and proceed with upload
  const handleFadSafetyConfirm = async () => {
    setShowFadSafetyModal(false);
    if (pendingUploadAction) {
      await executeUpload(pendingUploadAction.skipValidation);
    }
    setPendingUploadAction(null);
    setFadSafetyResult(null);
  };

  // Close FAD safety modal without proceeding
  const handleFadSafetyClose = () => {
    setShowFadSafetyModal(false);
    setPendingUploadAction(null);
    setFadSafetyResult(null);
  };

  // Execute the actual upload (called after safety checks pass)
  const executeUpload = async (skipValidation = false) => {
    if (!pendingFile || !selectedBankAccountId) {
      toast.error('Please select a bank account');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let jobId;
    let filename;
    try {
      // iter1236 - shared upload helper (see hooks/useStatementUploader).
      // Owns the multipart POST + global BackgroundJobsContext register +
      // typed error surfaces. Local page state (dialogs, on-page banner
      // polling) stays here.
      const result = await uploadStatement(pendingFile, {
        bankAccountId: selectedBankAccountId,
        skipValidation,
        onProgress: (pct) => setUploadProgress(pct),
      });
      jobId = result.jobId;
      filename = result.filename;
    } catch (error) {
      if (error?.name === 'StatementAuthRefreshError') {
        toast.info('Your session was refreshed. Please click Process Upload again.');
        setUploading(false);
        setUploadProgress(0);
        return;
      }
      if (error?.name === 'StatementValidationMismatchError') {
        setShowValidationWarning(true);
        setValidationResult({ is_valid: false, mismatch_reason: error.mismatchReason });
        setUploading(false);
        setUploadProgress(0);
        return;
      }
      const detail = error?.userMessage
        || error?.response?.data?.detail
        || 'Failed to upload file';
      const message = (typeof detail === 'string' && detail)
        ? detail
        : (detail?.message || 'Failed to upload file');
      toast.error(message);
      setUploading(false);
      setUploadProgress(0);
      return;
    }

    // Close dialog IMMEDIATELY - processing happens in background
    setShowUploadDialog(false);
    setShowValidationWarning(false);
    setPendingFile(null);
    setSelectedBankAccountId('');
    setValidationResult(null);
    setUploading(false);
    setUploadProgress(0);

    // Add to LOCAL background jobs list for on-page live banner
    setBackgroundJobs(prev => [...prev, {
      jobId,
      filename,
      startedAt: new Date(),
      progress: 0,
      status: 'queued',
      totalTx: 0,
      processedTx: 0
    }]);

    toast.info(
      <div>
        <strong>Processing "{filename}"</strong>
        <div className="text-sm mt-1">
          You can continue using the app. We'll notify you when it's done.
        </div>
      </div>,
      { duration: 5000 }
    );

    // Poll for job completion - store interval in ref so it can be cleaned up
    const pollInterval = setInterval(async () => {
      // CRITICAL: Stop polling and state updates if component unmounted
      if (!isMountedRef.current) {
        clearInterval(pollInterval);
        uploadPollIntervalsRef.current.delete(jobId);
        return;
      }

      try {
        const statusResponse = await axios.get(`${API}/bank-feeds/upload-job-status/${jobId}`);
        const status = statusResponse.data;
          
          // Update job progress in real-time
          if (isMountedRef.current && status.status !== 'completed') {
            setBackgroundJobs(prev => prev.map(j => 
              j.jobId === jobId 
                ? { 
                    ...j, 
                    progress: status.progress_percent || 0,
                    status: status.status || 'processing',
                    statusMessage: status.status_message || j.statusMessage,
                    billing: status.billing || j.billing,
                    totalTx: status.total_transactions || 0,
                    processedTx: status.processed_transactions || 0
                  }
                : j
            ));
          }
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            uploadPollIntervalsRef.current.delete(jobId);
            
            // Only update state if still mounted
            if (isMountedRef.current) {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
              
              // Remove from global context (it will have shown its own notification if user navigated away)
              if (backgroundJobsCtx?.removeJob) {
                backgroundJobsCtx.removeJob(jobId);
              }
            }
            
            // Show completion notification ONLY if user is on this page
            // (Global context handles notifications when user is on other pages)
            if (isMountedRef.current) {
              const summary = status.summary || {};
              const filteredCount = status.filtered_before_start_date || summary.filtered_before_start_date || 0;
              const totalParsed = summary.total_transactions || status.total_transactions || 0;
              const reason = summary.reason || '';
              
              if (reason === 'all_before_start_date' || (filteredCount > 0 && filteredCount >= totalParsed)) {
                // ALL transactions filtered - show specific reason
                toast.warning(
                  <div>
                    <strong>"{filename}" · No Transactions Imported</strong>
                    <div className="text-sm mt-1">
                      All {filteredCount} transactions in this statement are dated <strong>before your accounting start month</strong>.
                      <br />
                      <span className="text-amber-700">Only transactions on or after your start date can be imported.</span>
                    </div>
                  </div>,
                  { duration: 12000 }
                );
              } else {
                toast.success(
                  <div>
                    <strong>"{filename}" Complete!</strong>
                    <div className="text-sm mt-1">
                      {summary.valid_transactions || status.total_transactions} transactions processed
                      <br />
                      {status.high_confidence} auto-processed, {status.review_needed} need review
                      {filteredCount > 0 && (
                        <><br /><span className="text-amber-600">{filteredCount} transactions before start date excluded</span></>
                      )}
                      {status.gl_overrides_applied > 0 && (
                        <><br />{status.gl_overrides_applied} GL corrections applied</>
                      )}
                    </div>
                  </div>,
                  { duration: 8000 }
                );
              }
              
              // Refresh data since user is on this page
              Promise.all([
                fetchStats(),
                fetchPendingItems(),
                fetchProcessedItems(),
                fetchUploadedStatements()
              ]).catch(console.error);
            }
            
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            uploadPollIntervalsRef.current.delete(jobId);
            if (isMountedRef.current) {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
              // Remove from global context
              if (backgroundJobsCtx?.removeJob) {
                backgroundJobsCtx.removeJob(jobId);
              }
              toast.error(`"${filename}" failed: ${status.error_message || 'Unknown error'}`);
            }
          } else if (status.status === 'needs_mapping') {
            clearInterval(pollInterval);
            uploadPollIntervalsRef.current.delete(jobId);
            if (isMountedRef.current) {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
              if (backgroundJobsCtx?.removeJob) {
                backgroundJobsCtx.removeJob(jobId);
              }
              setColumnMappingJob({ jobId, filename, rawPreview: status.raw_preview });
              setColumnMapping({ date_col: null, description_col: null, debit_col: null, credit_col: null, amount_col: null, balance_col: null, reference_col: null });
              setShowColumnMapping(true);
              toast.info(`"${filename}" needs manual column mapping: columns could not be auto-detected.`);
            }
          } else if (status.status === 'awaiting_confirmation') {
            // Paused - backlog fee needs the user's decision. Stop polling;
            // the confirmation dialog resolution restarts processing.
            clearInterval(pollInterval);
            uploadPollIntervalsRef.current.delete(jobId);
            if (isMountedRef.current) {
              // iter1236 - defence-in-depth freshness check
              const jobObj = backgroundJobs.find(j => j.jobId === jobId) || { startedAt: new Date().toISOString() };
              if (isFreshEnoughForModal(jobObj)) {
                setBacklogDialogJob({ jobId, filename, billing: status.billing });
              }
              toast.info(`"${filename}": your confirmation is needed before backlog transactions can be processed.`);
            }
          } else if (status.status === 'cancelled') {
            clearInterval(pollInterval);
            uploadPollIntervalsRef.current.delete(jobId);
            if (isMountedRef.current) {
              setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
              if (backgroundJobsCtx?.removeJob) {
                backgroundJobsCtx.removeJob(jobId);
              }
              if (status.cancel_reason === 'payment_failed') {
                notifyUploadCancelled(jobId, filename, { reason: 'payment_failed', message: status.error_message });
              } else {
                notifyUploadCancelled(jobId, filename);
              }
            }
          }
          // Still processing - continue polling
        } catch (pollError) {
          console.error('Error polling job status:', pollError);
          // Track polling failures but don't show error immediately - network hiccups happen
          // The timeout will eventually handle truly stuck jobs
        }
      }, 2000); // Poll every 2 seconds
      
      // Store the interval for cleanup
      uploadPollIntervalsRef.current.set(jobId, pollInterval);
      
      // Stop polling after 5 minutes max
      setTimeout(() => {
        if (uploadPollIntervalsRef.current.has(jobId)) {
          clearInterval(uploadPollIntervalsRef.current.get(jobId));
          uploadPollIntervalsRef.current.delete(jobId);
          if (isMountedRef.current) {
            setBackgroundJobs(prev => prev.filter(j => j.jobId !== jobId));
            // Show informative message instead of silent failure
            toast.info(
              <div>
                <strong>Processing Taking Longer Than Expected</strong>
                <div className="text-sm mt-1">
                  The file "{filename}" is still being processed in the background.
                  <br />
                  Please check the Uploads tab in a few minutes for the result.
                </div>
              </div>,
              { duration: 10000 }
            );
          }
        }
      }, 900000);  // 15 minute timeout to match backend
  };

  // Process file upload after bank account selection - NOW USES BACKGROUND PROCESSING
  // Users can navigate away while processing - polling continues in background
  // This function checks FAD safety before proceeding
  const handleProcessUpload = async (skipValidation = false) => {
    setClosedPeriodRefresh((n) => n + 1);
    if (!pendingFile || !selectedBankAccountId) {
      toast.error('Please select a bank account');
      return;
    }

    // If we have validation result with date_range, check FAD safety
    if (validationResult?.date_range) {
      const startDate = extractStartDate(validationResult.date_range);
      if (startDate) {
        const safetyResult = await checkFadSafety(startDate);
        
        // If there are warnings or confirmation needed, show the modal
        if (safetyResult.requires_confirmation || (safetyResult.warnings && safetyResult.warnings.length > 0)) {
          setFadSafetyResult({
            ...safetyResult,
            startDate: startDate
          });
          setPendingUploadAction({ skipValidation });
          setShowFadSafetyModal(true);
          return;
        }
        
        // If blocked by policy, show error
        if (!safetyResult.can_proceed) {
          toast.error('Upload blocked: This statement contains transactions before your Financial Anchor Date. Please change your bank feed policy in Opening Balance Settings.');
          return;
        }
      }
    }
    
    // No safety issues or no date info - proceed with upload
    await executeUpload(skipValidation);
  };

  // Legacy file upload handler (kept for backward compatibility)
  const handleFileUpload = async (event) => {
    handleFileSelect(event);
  };

  // Approve a pending item
  const handleApprove = async (item, documentId = null, docType = null) => {
    // Phase 1.2 - Review Needed no longer has a separate "Save Changes" step.
    // If the user staged a GL edit on this row, commit it FIRST (so the
    // approve / journal-post uses the new GL), then continue. The reclassify
    // endpoint also updates the *_name fields so the UI reflects the change
    // immediately on refetch.
    const stagedEdit = pendingGlEdits.get(item.id);
    const userOverrodeGl = !!stagedEdit;  // iter921i: track for routing below
    // iter921j - infer the canonical document_type + sub_category from the
    // NEW GL code so /reclassify updates ai_document_type and
    // ai_sub_category to match the user's intent. Without this, the
    // backend kept the AI's ORIGINAL document_type (e.g. other_receipt
    // stayed other_receipt even after the user changed the inflow GL to a
    // revenue account that should produce an invoice).
    let inferredFromNewGl = null;
    if (stagedEdit) {
      inferredFromNewGl = inferDocFromGl(
        stagedEdit.glCode,
        item.direction,
        availableGlAccounts,
      );
      try {
        const reclassifyBody = {
          new_classification: inferredFromNewGl.sub_category,
          new_document_type: inferredFromNewGl.document_type,
          reason: 'Inline GL change committed via Approve',
          apply_learning: true,
        };
        if (stagedEdit.isInflow) reclassifyBody.new_gl_credit = stagedEdit.glCode;
        else reclassifyBody.new_gl_debit = stagedEdit.glCode;
        await axios.post(`${API}/bank-feeds/reclassify/${item.id}`, reclassifyBody);
        // Mutate the in-memory item so downstream special-item detection and
        // approve calls see the new GL AND the new doc-type/sub-category.
        if (stagedEdit.isInflow) {
          item.ai_gl_credit = stagedEdit.glCode;
          item.ai_gl_credit_name = stagedEdit.glName;
        } else {
          item.ai_gl_debit = stagedEdit.glCode;
          item.ai_gl_debit_name = stagedEdit.glName;
        }
        item.ai_document_type = inferredFromNewGl.document_type;
        item.ai_sub_category = inferredFromNewGl.sub_category;
        item.classification = inferredFromNewGl.sub_category;
        setPendingGlEdits((prev) => {
          if (!prev.has(item.id)) return prev;
          const next = new Map(prev);
          next.delete(item.id);
          return next;
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not apply the GL change. Approval aborted.');
        return;
      }
    }

    // Check if this is a special transaction type that needs user input
    // iter921j - when the user overrode the GL, trust the inferred family
    // from the NEW GL (NOT the AI's stale ai_sub_category). This makes
    // changing a fixed_asset_purchase row to a regular expense GL correctly
    // skip the Fixed-Asset linkage modal, and changing a regular bill row
    // to a Fixed-Asset GL correctly opens the linkage modal.
    const subCategory = userOverrodeGl
      ? inferredFromNewGl.sub_category
      : (item.ai_sub_category || item.sub_category || item.classification);
    // iter921i - when the user overrode the GL, we MUST detect special-item
    // family from the new GL code (not from the AI's stale sub_category).
    // `glToFamily()` consults the live chart_of_accounts and returns a
    // family string ('fixed_asset' | 'prepayment' | 'loan' | 'investment' |
    // 'loan_receivable' | null) so we route into the linkage modal when
    // the new GL belongs to a special-item account class.
    // Sep 2026 - the GL decides the special-item family (see detectSpecialFamily).
    const newGlCode = stagedEdit?.glCode || (item.direction === 'inflow' ? item.ai_gl_credit : item.ai_gl_debit) || item.gl_code;
    const specialFamily = detectSpecialFamily({
      glCode: newGlCode, direction: item.direction, subCategory, chartOfAccounts: availableGlAccounts,
      docType: docType || (userOverrodeGl ? inferredFromNewGl?.document_type : item.ai_document_type),
    });
    if (specialFamily) {
      // Phase 2.2 (Feb 2026) - open SpecialItemDecisionModal so the user can
      // decide: new tracked item / match existing / cancel.
      setSpecialItemModal({ open: true, transaction: item, docType: specialFamily });
      return;
    }
    
    // Standard approval flow
    setProcessingId(item.id);

    // iter921i - when the user overrode the GL, the original AI-matched
    // document is no longer relevant (the user is saying "no, post against
    // this GL instead"). Force the quick_approve path so the backend
    // builds a fresh document from the updated ai_gl_debit/credit (which
    // /reclassify just set above). Without this, the backend would still
    // post against the stale AI-matched document → the new GL never
    // reaches the journal → "Uncategorised" after refresh.
    const useMatchedDoc = !userOverrodeGl && (documentId || item.matched_document_id);
    const matchParams = useMatchedDoc
      ? {
          match_type: docType || item.match_type,
          document_id: documentId || item.matched_document_id,
        }
      : { quick_approve: true };
    
    // Check period lock before posting
    const today = new Date().toISOString().split('T')[0];
    const txDate = item.date;
    const locked = await checkPeriodLock(
      txDate,
      async () => {
        try {
          await axios.post(`${API}/bank-feeds/approve/${item.id}`, null, {
            params: {
              ...matchParams,
              allow_locked_period: true
            }
          });
          toast.success('Transaction approved (posted to original date)');
          await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
          setShowMatchDialog(false);
          setShowDetailDialog(false);
        } catch (error) {
          handleApproveError(error, item, matchParams);
        } finally {
          setProcessingId(null);
        }
      },
      async () => {
        try {
          await axios.post(`${API}/bank-feeds/approve/${item.id}`, null, {
            params: {
              ...matchParams,
              override_date: today
            }
          });
          toast.success('Transaction approved (posted to today)');
          await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
          setShowMatchDialog(false);
          setShowDetailDialog(false);
        } catch (error) {
          handleApproveError(error, item, matchParams);
        } finally {
          setProcessingId(null);
        }
      }
    );
    
    if (locked) return;
    
    try {
      await axios.post(`${API}/bank-feeds/approve/${item.id}`, null, {
        params: matchParams
      });
      
      toast.success('Transaction approved');
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
      setShowMatchDialog(false);
      setShowDetailDialog(false);
    } catch (error) {
      handleApproveError(error, item, matchParams);
    } finally {
      setProcessingId(null);
    }
  };

  // Handle Asset/Prepayment processing with user inputs
  // Jul 16 - canonical special-item term flow. Opens the SAME full-term
  // modal used on the Fixed Assets / Loans / Investments / Prepayments pages,
  // then: 1) creates+posts via process-asset-prepayment (basic fields),
  // 2) applies the remaining detailed terms via each family's own
  // terms-update endpoint (which recalculates schedules).
  const openSpecialTermsModal = (tx, family) => {
    const itemType =
      family === 'fixed_asset' ? 'fixed_asset' :
      family === 'prepayment' ? 'prepaid_expense' :
      family === 'deferred_revenue' ? 'deferred_revenue' :
      (family === 'loan' || family === 'loan_received') ? 'loan' :
      'investment'; // investment + loan_receivable (tracked as investment loan_given)
    setSpecialTermsModal({
      open: true,
      transaction: {
        id: tx.id,
        description: tx.description || tx.ai_purpose,
        counterparty: tx.counterparty || tx.ai_counterparty,
        amount: tx.amount,
        date: tx.date,
        direction: tx.direction,
        ai_gl_debit: tx.ai_gl_debit,
        ai_gl_debit_name: tx.ai_gl_debit_name,
        ai_gl_credit: tx.ai_gl_credit,
      },
      itemType,
      family,
    });
  };

  const applyDetailedSpecialTerms = async (created, terms) => {
    const id = created?.document_id;
    const type = created?.document_type;
    if (!id || !type) return;
    const clean = (o) => { Object.keys(o).forEach(k => { if (o[k] === undefined || o[k] === null || o[k] === '') delete o[k]; }); return o; };
    try {
      if (type === 'fixed_asset') {
        await axios.put(`${API}/assets/${id}`, clean({
          name: terms.name,
          useful_life_months: terms.useful_life_months,
          depreciation_method: terms.depreciation_method,
          salvage_value: terms.salvage_value != null ? Math.round(terms.salvage_value) : undefined,
          location: terms.location,
          serial_number: terms.serial_number,
          notes: terms.notes,
          user_confirmed: true,
        }));
      } else if (type === 'prepayment') {
        await axios.put(`${API}/prepayments/schedule/${id}`, clean({
          description: terms.name,
          vendor_name: terms.vendor_name,
          spread_over_months: terms.coverage_months,
          expense_gl_code: terms.expense_gl_code,
          notes: terms.notes,
          user_confirmed: true,
        }));
      } else if (type === 'loan') {
        const p = clean({
          loan_name: terms.name,
          lender_name: terms.lender_name,
          loan_type: terms.loan_type,
          annual_interest_rate: terms.interest_rate ?? undefined,
          loan_term_months: terms.loan_term_months,
          payment_frequency: terms.payment_frequency,
          first_payment_date: terms.first_payment_date,
          terms_type: terms.terms_type,
          other_borrowing_costs: terms.other_borrowing_costs?.length ? terms.other_borrowing_costs : undefined,
          fixed_repayments_type: terms.fixed_repayments_type,
          uniform_repayment_amount: terms.uniform_repayment_amount,
          notes: terms.notes,
        });
        if (terms.terms_type === 'fixed_repayments') {
          p.repayment_type = terms.fixed_repayments_type === 'variable' ? 'fixed_variable' : 'fixed_uniform';
          if (terms.fixed_repayments_type === 'variable' && terms.variable_repayments?.length) {
            p.variable_repayments = terms.variable_repayments;
          }
        }
        await axios.put(`${API}/loans/${id}/update-terms`, p);
      } else if (type === 'investment') {
        await axios.put(`${API}/investments-v2/${id}`, clean({
          investment_name: terms.name,
          institution_name: terms.institution_name,
          investment_type: terms.investment_type,
          expected_return_rate: terms.expected_return_rate != null ? parseFloat(terms.expected_return_rate) : undefined,
          maturity_date: terms.maturity_date,
          return_frequency: terms.interest_frequency,
          notes: terms.notes,
        }));
      }
    } catch (err) {
      toast.warning('Item created & posted, but some detailed terms could not be applied: open it from its dashboard to finish setting them.');
    }
  };

  const handleSpecialTermsSave = async (_lineItemId, terms) => {
    const tx = specialTermsModal.transaction;
    if (!tx) return;
    setProcessingId(tx.id);

    // Basic fields the create endpoint understands; the rest are applied
    // right after creation via applyDetailedSpecialTerms.
    const basic = {};
    if (terms.type === 'fixed_asset') {
      basic.asset_category = terms.category;
      basic.useful_life_months = terms.useful_life_months;
    } else if (terms.type === 'prepaid_expense') {
      basic.prepayment_type = terms.prepayment_type;
      basic.coverage_months = terms.coverage_months;
      basic.expense_gl_code = terms.expense_gl_code || undefined;
    } else if (terms.type === 'loan') {
      basic.lender_name = terms.lender_name;
      basic.loan_term_months = terms.loan_term_months;
      basic.interest_rate = terms.interest_rate ?? 0;
      basic.loan_type = terms.loan_type;
    } else if (terms.type === 'investment') {
      basic.institution_name = terms.institution_name;
      basic.investment_type = terms.investment_type;
      basic.expected_return_rate = terms.expected_return_rate ?? 0;
    } else if (terms.type === 'deferred_revenue') {
      basic.recognition_months = terms.recognition_months;
      basic.recognition_start_date = terms.recognition_start_date || undefined;
      basic.revenue_gl_code = terms.revenue_gl_code || undefined;
      basic.deferred_gl_code = tx.ai_gl_credit || undefined;
      basic.customer_name = terms.customer_name || undefined;
      basic.revenue_type = terms.deferred_type || undefined;
    }

    const docTypeNames = { fixed_asset: 'Fixed Asset', prepayment: 'Prepaid Expense', loan: 'Loan', investment: 'Investment', deferred_revenue: 'Deferred Revenue' };
    const createAndFinish = async (extraParams = {}, suffix = '') => {
      const response = await axios.post(`${API}/bank-feeds/process-asset-prepayment/${tx.id}`, { ...basic, ...extraParams });
      await applyDetailedSpecialTerms(response.data, terms);
      const name = docTypeNames[response.data.document_type] || 'Document';
      toast.success(`${name} created with your terms & posted${suffix}`);
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
      setSpecialTermsModal({ open: false, transaction: null, itemType: null, family: null });
    };

    try {
      const today = new Date().toISOString().split('T')[0];
      const locked = await checkPeriodLock(
        tx.date,
        async () => {
          try { await createAndFinish({ allow_locked_period: true }, ' (original date)'); }
          catch (err) { toast.error(err?.response?.data?.detail || 'Failed to process'); }
          finally { setProcessingId(null); }
        },
        async () => {
          try { await createAndFinish({ override_date: today }, ' (posted to today)'); }
          catch (err) { toast.error(err?.response?.data?.detail || 'Failed to process'); }
          finally { setProcessingId(null); }
        }
      );
      if (locked) return;
      await createAndFinish();
    } catch (error) {
      const raw = error?.response?.data?.detail;
      const detail = typeof raw === 'string' ? raw : (Array.isArray(raw) ? raw.map((d) => d?.msg || JSON.stringify(d)).join('; ') : '');
      toast.error('Terms not saved', {
        description: `${detail || 'The server could not create the item.'} The transaction stays in Review Needed - open it and press Approve to try again.`,
        duration: 9000,
      });
      error.toastShown = true; // SpecialItemModal must not stack a second generic toast
      throw error; // let SpecialItemModal keep its error state
    } finally {
      setProcessingId(null);
    }
  };

  // Reject a pending item
  const handleReject = async (item, reason = null) => {
    setProcessingId(item.id);
    try {
      await axios.post(`${API}/bank-feeds/reject/${item.id}`, null, {
        params: { reason }
      });
      
      toast.success('Transaction rejected');
      await Promise.all([fetchStats(), fetchPendingItems()]);
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  // ==================== BULK OPERATIONS ====================
  
  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Select/Deselect all filtered items
  const toggleSelectAll = () => {
    if (selectedItems.size === filteredPendingItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredPendingItems.map(item => item.id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedItems(new Set());
  };
  // A selection made on one list must never carry over to the other (Review Needed ↔ In Books) or another tab.
  useEffect(() => { setSelectedItems(new Set()); }, [txFilter, activeTab]);

  // Get selected items data
  const getSelectedItemsData = () => {
    // Pull from BOTH pending and processed lists - bulk re-classify can target
    // either, and getSelectedItemsData() used to silently miss Completed rows
    // (which made the dialog mis-count and skip the journal-reversal warning).
    const pool = [...pendingItems, ...processedItems];
    return pool.filter(item => selectedItems.has(item.id));
  };

  // Bulk Approve - handles BOTH matched-doc rows (settlement payment) AND
  // auto-doc-creation rows (quick_approve with AI classification). The old
  // behaviour only handled matched-doc rows and silently dropped the rest,
  // which made Bulk Approve effectively unusable for the AI-classified RN
  // queue (the bulk of real usage). See iter 708.
  const handleBulkApprove = async () => {
    const selected = getSelectedItemsData();
    // Skip already-completed rows defensively (cross-tab selection edge case)
    const candidates = selected.filter(item => item.status !== 'completed');
    if (candidates.length === 0) {
      toast.error('Nothing to approve in the current selection.');
      return;
    }
    const specialRows = candidates.filter((item) => {
      const staged = pendingGlEdits.get(item.id);
      const gl = staged?.glCode || (item.direction === 'outflow' ? item.ai_gl_debit : item.ai_gl_credit);
      return specialDocTypeForGl(item, gl) || SPECIAL_DOC_TYPES.includes(item.ai_document_type || '');
    });
    if (specialRows.length > 0) {
      toast.error(`${specialRows.length} selected transaction${specialRows.length === 1 ? ' is' : 's are'} categorised as special items (loans, prepayments, investments, fixed assets, deferred revenue). Approve those individually so you can match them to an existing item or set up a new one.`, { duration: 9000 });
      return;
    }

    setBulkProcessing(true);
    let successCount = 0;
    let failCount = 0;
    const skipped = [];  // Track rows the backend returned `success: false` for
    // iter1100-fix - accumulate learning outcomes across the bulk loop so we
    // can surface ONE summary toast + "View" action at the end (per-row
    // toasts would spam sonner on bulk approve). Undo-per-rule stays
    // available in the AI Learning tab.
    const learnedRules = [];   // status === 'created'
    let reinforcedCount = 0;   // status === 'reinforced'
    let conflictCount = 0;     // status === 'conflict'

    for (const item of candidates) {
      try {
        let resp;
        if (item.matched_document_id) {
          // Matched-doc settlement path.
          resp = await axios.post(`${API}/bank-feeds/approve/${item.id}`, null, {
            params: {
              match_type: item.match_type,
              document_id: item.matched_document_id,
              user_tax_only: true,
            },
          });
        } else {
          // Auto-doc-creation path - quick approve using AI classification.
          resp = await axios.post(
            `${API}/bank-feeds/approve/${item.id}?quick_approve=true&user_tax_only=true`,
            {
              classification: item.classification || item.ai_sub_category,
              quick_approve: true,
            },
          );
        }
        // Backend can return 200 with `success: false` when doc creation
        // fails Pydantic validation etc. - count it as a skip, not a success.
        if (resp?.data?.success === false) {
          skipped.push({ id: item.id, reason: resp.data.message || 'Unknown' });
          failCount++;
        } else {
          successCount++;
          const learning = resp?.data?.learning;
          if (learning?.status === 'created' && learning.rule_id) {
            learnedRules.push(learning);
          } else if (learning?.status === 'reinforced') {
            reinforcedCount++;
          } else if (learning?.status === 'conflict') {
            conflictCount++;
          }
        }
      } catch (error) {
        failCount++;
        console.error(`Failed to approve ${item.id}:`, error);
        const det = error?.response?.data?.detail;
        const reason = det?.code === 'existing_match_found'
          ? 'Looks already recorded in your books - open the transaction to link it'
          : (typeof det === 'string' ? det : error.message);
        skipped.push({ id: item.id, reason });
      }
    }

    setBulkProcessing(false);
    setShowBulkConfirmDialog(false);
    clearSelection();

    await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);

    if (failCount === 0) {
      toast.success(`Successfully approved ${successCount} transaction${successCount === 1 ? '' : 's'}`);
    } else if (successCount > 0) {
      toast.warning(`Approved ${successCount} of ${successCount + failCount}, ${failCount} skipped`);
    } else {
      toast.error(`All ${failCount} transaction${failCount === 1 ? '' : 's'} failed`);
    }
    if (skipped.length > 0) {
      console.warn('Bulk approve skipped rows:', skipped);
    }

    // iter1100-fix - single aggregated learning summary. Undo-per-rule is
    // available on the AI Learning tab (RecentlyLearnedRules component).
    const learnedNewCount = learnedRules.length;
    if (learnedNewCount > 0 || reinforcedCount > 0 || conflictCount > 0) {
      const parts = [];
      if (learnedNewCount > 0) parts.push(`${learnedNewCount} new rule${learnedNewCount === 1 ? '' : 's'}`);
      if (reinforcedCount > 0) parts.push(`reinforced ${reinforcedCount}`);
      if (conflictCount > 0) parts.push(`${conflictCount} kept old`);
      toast.info(`AI learned: ${parts.join(' · ')}`, {
        duration: 8000,
        action: {
          label: 'View',
          onClick: () => { setActiveTab('more'); setMoreSection('ai-learning'); },
        },
      });
    }
  };

  // Bulk Reject
  const handleBulkReject = async () => {
    const itemsToReject = getSelectedItemsData();
    
    setBulkProcessing(true);
    let successCount = 0;
    let failCount = 0;

    for (const item of itemsToReject) {
      try {
        await axios.post(`${API}/bank-feeds/reject/${item.id}`, null, {
          params: { reason: 'Bulk rejected by user' }
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to reject ${item.id}:`, error);
      }
    }

    setBulkProcessing(false);
    setShowBulkConfirmDialog(false);
    clearSelection();
    
    await Promise.all([fetchStats(), fetchPendingItems()]);
    
    if (failCount === 0) {
      toast.success(`Successfully rejected ${successCount} transactions`);
    } else {
      toast.warning(`Rejected ${successCount}, failed ${failCount} transactions`);
    }
  };

  // Phase H - Open the link-document picker for a single transaction.
  // Optional second arg `{ mode: 'swap' | 'relink' }`:
  //   'swap'   → LEGACY LinkDocumentDialog (kept for backward compat)
  //   'relink' → iter1411 In-Books relink via SmartLinkDocumentModal
  //   (default) 'link' → fresh Smart Link on a Review-Needed row
  const openLinkDialogForTx = useCallback((tx, opts = {}) => {
    setLinkDialogTx(tx);
    const m = opts.mode;
    setLinkDialogMode(m === 'swap' ? 'swap' : m === 'relink' ? 'relink' : 'link');
    setLinkDialogFocus(opts.focusType || null);
  }, []);

  // Special-item family implied by a GL for this row (null = ordinary income/expense).
  const SPECIAL_DOC_TYPES = ['fixed_asset', 'prepayment', 'loan', 'loan_received', 'investment', 'loan_receivable', 'deferred_revenue'];
  const specialDocTypeForGl = useCallback((item, glCode) => {
    if (!glCode) return null;
    const fam = glToFamily(glCode, item?.direction, availableGlAccounts);
    return SPECIAL_DOC_TYPES.includes(fam) ? (fam === 'loan_received' ? 'loan' : fam) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableGlAccounts]);
  const isSpecialItemRow = (item) => SPECIAL_DOC_TYPES.includes(item?.document_type || item?.created_document_type || '');

  // Match groups / partial-match chains: the backend answers 409 requires_group_confirm with the
  // other bank lines involved. Ask once, then retry with confirm_group so the whole group undoes.
  const undoWithGroupConfirm = async (txId, body) => {
    try {
      return await axios.post(`${API}/bank-feeds/undo-classification/${txId}`, body);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === 'requires_group_confirm') {
        const members = (d.members || []).map(m => `${m.date || ''} · ${(m.description || '').slice(0, 40)} · ${Math.abs(m.amount || 0).toLocaleString()}`).join('\n');
        const ok = await confirmAction({
          name: 'Undo the whole match group',
          impact: `${d.message}\n\nBank lines involved:\n${members}`,
          destructiveLabel: 'Undo all of them',
        });
        if (!ok) throw Object.assign(new Error('cancelled'), { cancelled: true });
        return await axios.post(`${API}/bank-feeds/undo-classification/${txId}`, { ...body, confirm_group: true });
      }
      throw err;
    }
  };

  // Full cascade undo (doc, journals, schedules, depreciation/amortisation/accrual postings).
  const undoRowCascade = async (txId, reason) => {
    await undoWithGroupConfirm(txId, { reason, cascade_reverse_downstream: true });
  };

  // Phase H - Unlink handler. Routes the user through the proper backend
  // endpoint depending on whether the linked doc was CREATED (undo) or
  // MATCHED (simple unlink). Reuses confirmAction for destructive paths.
  const handleUnlinkTx = useCallback(async (tx) => {
    if (!tx?.id) return;
    const isCreated = !!tx.created_document_id;
    const message = isCreated
      ? `This will reverse the journal and delete the ${tx.created_document_type || 'document'} created by this transaction, sending it back to Review Needed. Proceed?`
      : (tx.status && ['completed', 'processed', 'matched'].includes(tx.status))
        ? 'This will reverse the payment journal and send the transaction back to Review Needed. The linked document stays put. Proceed?'
        : 'Remove the auto-match? The transaction stays in Review Needed.';
    const ok = await confirmAction({
      name: isCreated
        ? `Transaction (${tx.created_document_type || 'doc'} ${tx.created_document_ref || ''})`
        : `Transaction link`,
      impact: message,
      destructiveLabel: isCreated ? 'Send back to Review' : 'Unlink',
    });
    if (!ok) return;
    try {
      if (isCreated) {
        const undoResp = await undoWithGroupConfirm(tx.id, {
          reason: 'User unlinked from bank-feed table',
          cascade_reverse_downstream: true,
        });
        toast.success('Sent back to Review Needed.');
        if (undoResp.data?.inventory_warning) {
          toast.warning(undoResp.data.inventory_warning, { duration: 9000 });
        }
      } else {
        await axios.post(`${API}/bank-feeds/unlink/${tx.id}`);
        toast.success('Unlinked.');
      }
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    } catch (err) {
      if (err?.cancelled) return;
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Unlink failed.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats, fetchPendingItems, fetchProcessedItems]);

  // Phase H - Bulk undo classification (send N completed rows back to review).
  const openBulkUndoDialog = useCallback(() => {
    if (selectedItems.size === 0) {
      toast.error('Select at least one transaction first.');
      return;
    }
    setShowBulkUndoDialog(true);
  }, [selectedItems]);

  const handleBulkUndoConfirm = useCallback(async () => {
    const ids = Array.from(selectedItems);
    setShowBulkUndoDialog(false);
    try {
      const { data } = await axios.post(`${API}/bank-feeds/bulk-undo-classification`, {
        transaction_ids: ids,
        reason: 'Bulk send-back to review from table',
      });
      if ((data?.failed_count ?? 0) === 0) {
        toast.success(`Sent ${data.succeeded_count} transaction${data.succeeded_count === 1 ? '' : 's'} back to Review Needed.`);
      } else {
        toast.warning(`${data.succeeded_count} sent back; ${data.failed_count} failed.`);
      }
      clearSelection();
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk send-back failed.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, fetchStats, fetchPendingItems, fetchProcessedItems]);

  // Open Bulk Reclassify Dialog
  const openBulkReclassifyDialog = () => {
    setReclassifyTarget('bulk');
    setSelectedClassification('');
    setReclassifyReason('');
    setShowReclassifyDialog(true);
  };

  // Phase G - Open the new in-place bulk category dialog. This re-categorises
  // selected transactions atomically (reverses + reposts journals for completed
  // rows) without ever routing through the legacy document-creation flow.
  const openBulkCategoryDialog = () => {
    if (selectedItems.size === 0) {
      toast.error('Select at least one transaction first.');
      return;
    }
    setShowBulkCategoryDialog(true);
  };

  // Phase G - Submit handler used by <BulkReclassifyDialog>. Hits the backend
  // /bulk-reclassify endpoint and refreshes data afterwards. Returns the
  // server response so the dialog can show success/failure detail.
  const handleBulkCategorySubmit = useCallback(async ({ gl_code, reason }) => {
    const ids = Array.from(selectedItems);
    const rows = ids.map((id) => transactionsByIdMap.get(id)).filter(Boolean);
    const specialTarget = rows.some((row) => specialDocTypeForGl(row, gl_code));
    // Only what is ACTUALLY in the books counts: a completed row posted as a plain receipt/payment is not a
    // special item even if the AI's suggestion (ai_document_type) said "investment" — the GL the user picks
    // wins over the AI label, exactly as in the single-row flow.
    const specialRows = rows.filter((row) => isSpecialItemRow(row));
    if (specialTarget || specialRows.length > 0) {
      const why = specialTarget
        ? 'that category is a special item (loan, prepayment, investment, fixed asset or deferred revenue)'
        : `${specialRows.length} selected transaction${specialRows.length === 1 ? ' is' : 's are'} special items`;
      const blockMsg = `Bulk re-classify isn't available because ${why}. Each special item needs its own match-existing / track-as-new decision — re-classify those rows one at a time.`;
      toast.error(blockMsg, { duration: 9000 });
      return { failed_count: ids.length, succeeded_count: 0, blocked: true, failed: ids.map((id) => ({ id, reason: blockMsg })) };
    }
    try {
      const { data } = await axios.post(`${API}/bank-feeds/bulk-reclassify`, {
        transaction_ids: ids,
        gl_code,
        reason: reason || null,
        apply_learning: true,
      });
      if ((data?.failed_count ?? 0) === 0) {
        toast.success(`Re-classified ${data.succeeded_count} transactions to ${data.target_gl_name}.`);
        clearSelection();
      } else {
        toast.warning(`${data.succeeded_count} succeeded, ${data.failed_count} failed. See dialog for details.`);
      }
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
      return data;
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Bulk re-classify failed.';
      toast.error(msg);
      return { succeeded_count: 0, failed_count: ids.length, failed: ids.map(id => ({ id, reason: msg })) };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, fetchStats, fetchPendingItems, fetchProcessedItems]);

  // Open Single Item - Navigate to Document Creation Page
  const openReclassifyDialog = (item) => {
    // Show document type selection dialog
    setReclassifyTarget(item);
    setShowReclassifyDialog(true);
  };
  
  // Open Investment Match Dialog
  const openInvestmentMatchDialog = (item) => {
    setInvestmentMatchTransaction(item);
    setShowReclassifyDialog(false);
    setShowInvestmentMatchDialog(true);
  };
  
  // Handle Investment Match Complete
  const handleInvestmentMatchComplete = () => {
    setShowInvestmentMatchDialog(false);
    setInvestmentMatchTransaction(null);
    fetchPendingItems();
    fetchStats();
    fetchProcessedItems();
  };
  
  const openTaxPaymentDialog = (item) => {
    setShowReclassifyDialog(false);
    setTaxPaymentTransaction(item);
  };
  const handleTaxPaymentComplete = () => {
    setTaxPaymentTransaction(null);
    fetchPendingItems();
    fetchStats();
    fetchProcessedItems();
  };

  // Open Loan Match Dialog
  const openLoanMatchDialog = (item) => {
    setLoanMatchTransaction(item);
    setShowReclassifyDialog(false);
    setShowLoanMatchDialog(true);
  };
  
  // Handle Loan Match Complete
  const handleLoanMatchComplete = () => {
    setShowLoanMatchDialog(false);
    setLoanMatchTransaction(null);
    fetchPendingItems();
    fetchStats();
    fetchProcessedItems();
  };
  
  // Open Prepayment Match Dialog
  const openPrepaymentMatchDialog = (item) => {
    setPrepaymentMatchTransaction(item);
    setShowReclassifyDialog(false);
    setShowPrepaymentMatchDialog(true);
  };
  
  // Handle Prepayment Match Complete
  const handlePrepaymentMatchComplete = () => {
    setShowPrepaymentMatchDialog(false);
    setPrepaymentMatchTransaction(null);
    fetchPendingItems();
    fetchStats();
    fetchProcessedItems();
  };
  
  // Open Asset Disposal Dialog
  const openAssetDisposalDialog = (item) => {
    setAssetDisposalTransaction(item);
    setShowReclassifyDialog(false);
    setShowAssetDisposalDialog(true);
  };
  
  // Handle Asset Disposal Complete
  const handleAssetDisposalComplete = () => {
    setShowAssetDisposalDialog(false);
    setAssetDisposalTransaction(null);
    fetchPendingItems();
    fetchStats();
    fetchProcessedItems();
  };
  
  // Open in-page Bank-Feed document dialog (replaces page navigation
  // so the user never leaves the bank feed context).
  const navigateToDocumentCreation = (docType) => {
    const item = reclassifyTarget;
    if (!item) return;

    // Build prefilled data for the document
    const prefillData = {
      amount: item.amount,
      date: item.date,
      description: item.description,
      counterparty: item.counterparty || item.ai_counterparty,
      reference: item.reference,
      bank_feed_transaction_id: item.id,
      gl_debit: item.ai_gl_debit,
      gl_credit: item.ai_gl_credit,
      bank_account_id: item.bank_account_id,
      currency: item.currency || bankAccounts.find(b => b.id === item.bank_account_id)?.currency || business?.currency
    };

    // Keep sessionStorage path for backwards compat with any deep-link
    // flow we haven't migrated yet.
    sessionStorage.setItem('bankFeedPrefill', JSON.stringify(prefillData));

    setShowReclassifyDialog(false);

    // Open the right in-page dialog.
    switch (docType) {
      case 'invoice':
        setDocDialog({ open: true, type: 'invoice', prefill: prefillData });
        break;
      case 'bill':
        setDocDialog({ open: true, type: 'bill', prefill: prefillData });
        break;
      case 'other_receipt':
        setOtherReceiptDialog({ open: true, prefill: prefillData });
        break;
      case 'other_payment':
        setOtherPaymentDialog({ open: true, prefill: prefillData });
        break;
      default:
        toast.error('Invalid document type');
    }
  };

  // Called by any in-page dialog after a document is saved successfully.
  // Refresh both the pending and processed feed lists, plus stats.
  const handleInPageDocSaved = () => {
    fetchPendingItems();
    fetchProcessedItems();
    fetchStats();
    toast.success('Document saved: bank feed updated');
  };

  // State for viewing document details
  const [showDocumentDetailDialog, setShowDocumentDetailDialog] = useState(false);
  const [documentDetail, setDocumentDetail] = useState(null);
  const [loadingDocumentDetail, setLoadingDocumentDetail] = useState(false);

  // Navigate to view an existing document
  const navigateToDocument = async (docType, docId) => {
    if (!docType || !docId) {
      toast.error('Document not found');
      return;
    }
    
    switch (docType) {
      case 'invoice':
        navigate(`/invoices/${docId}`);
        break;
      case 'bill':
        navigate(`/bills/${docId}`);
        break;
      case 'other_receipt':
      case 'other_payment':
        // Fetch document details and show in a dialog
        // API routes: /other-transactions/receipts/{id} and /other-transactions/payments/{id}
        setLoadingDocumentDetail(true);
        try {
          const endpoint = docType === 'other_receipt' 
            ? `/other-transactions/receipts/${docId}` 
            : `/other-transactions/payments/${docId}`;
          const response = await axios.get(`${API}${endpoint}`);
          setDocumentDetail({
            ...response.data,
            document_type: docType
          });
          setShowDocumentDetailDialog(true);
        } catch (error) {
          toast.error(`Failed to load ${docType.replace('_', ' ')} details`);
        } finally {
          setLoadingDocumentDetail(false);
        }
        break;
      // Special Items - navigate to their respective pages
      case 'loan':
        navigate(`/advanced-view-hub?tab=loans&selected=${docId}`);
        break;
      case 'fixed_asset':
        navigate(`/advanced-view-hub?tab=assets&selected=${docId}`);
        break;
      case 'prepayment':
        navigate(`/advanced-view-hub?tab=prepayments&selected=${docId}`);
        break;
      case 'investment':
        navigate(`/advanced-view-hub?tab=investments&selected=${docId}`);
        break;
      case 'deferred_revenue':
        navigate(`/advanced-view-hub?tab=deferred-revenue&selected=${docId}`);
        break;
      default:
        // Try to handle unknown types by checking if it's a special item pattern
        if (docType?.includes('loan')) {
          navigate(`/advanced-view-hub?tab=loans&selected=${docId}`);
        } else if (docType?.includes('asset')) {
          navigate(`/advanced-view-hub?tab=assets&selected=${docId}`);
        } else if (docType?.includes('prepay')) {
          navigate(`/advanced-view-hub?tab=prepayments&selected=${docId}`);
        } else if (docType?.includes('invest')) {
          navigate(`/advanced-view-hub?tab=investments&selected=${docId}`);
        } else if (docType?.includes('deferred')) {
          navigate(`/advanced-view-hub?tab=deferred-revenue&selected=${docId}`);
        } else {
          toast.error(`Unknown document type: ${docType}`);
        }
    }
  };

  // Handle Reclassification (for bulk operations)
  const handleReclassify = async () => {
    if (!selectedClassification) {
      toast.error('Please select a classification');
      return;
    }

    if (!reclassifyReason.trim()) {
      toast.error('Please provide a reason for reclassification');
      return;
    }

    setBulkProcessing(true);
    let successCount = 0;
    let failCount = 0;

    const itemsToReclassify = reclassifyTarget === 'bulk' 
      ? getSelectedItemsData() 
      : [reclassifyTarget];

    for (const item of itemsToReclassify) {
      try {
        // Use the new reclassify endpoint with learning support
        await axios.post(`${API}/bank-feeds/reclassify/${item.id}`, {
          new_classification: selectedClassification,
          new_document_type: item.ai_document_type,
          new_gl_debit: item.ai_gl_debit,
          new_gl_credit: item.ai_gl_credit,
          reason: reclassifyReason,
          apply_learning: applyLearning
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to reclassify ${item.id}:`, error);
      }
    }

    setBulkProcessing(false);
    setShowReclassifyDialog(false);
    setReclassifyReason('');
    setSelectedClassification('');
    setApplyLearning(true); // Reset to default
    clearSelection();
    
    await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    
    if (failCount === 0) {
      const learningMsg = applyLearning ? ' AI will learn from this correction.' : '';
      toast.success(`Successfully reclassified ${successCount} transactions.${learningMsg}`);
    } else {
      toast.warning(`Reclassified ${successCount}, failed ${failCount} transactions`);
    }
  };

  // Initiate bulk action with confirmation
  const initiateBulkAction = (action) => {
    setBulkAction(action);
    setShowBulkConfirmDialog(true);
  };

  // Execute confirmed bulk action
  const executeBulkAction = () => {
    if (bulkAction === 'approve') {
      handleBulkApprove();
    } else if (bulkAction === 'reject') {
      handleBulkReject();
    }
  };

  // ==================== AI CORRECTIONS ====================
  
  // Apply a single suggested correction
  const handleApplyCorrection = async (suggestion) => {
    setApplyingCorrections(true);
    try {
      const response = await axios.post(`${API}/ai-corrections/apply-corrections`, {
        corrections: [{
          transaction_id: suggestion.transaction_id,
          new_classification: suggestion.suggested_classification,
          new_gl_code: suggestion.suggested_gl_code
        }]
      });
      
      if (response.data.success) {
        toast.success('Correction applied successfully');
        // Refresh data
        await Promise.all([
          fetchSuggestedCorrections(),
          fetchCorrectionHistory(),
          fetchPendingItems()
        ]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply correction');
    } finally {
      setApplyingCorrections(false);
    }
  };

  // Apply all suggested corrections
  const handleApplyAllCorrections = async () => {
    if (suggestedCorrections.length === 0) return;
    
    setApplyingCorrections(true);
    try {
      const corrections = suggestedCorrections.map(s => ({
        transaction_id: s.transaction_id,
        new_classification: s.suggested_classification,
        new_gl_code: s.suggested_gl_code
      }));
      
      const response = await axios.post(`${API}/ai-corrections/apply-corrections`, {
        corrections
      });
      
      if (response.data.success) {
        toast.success(`Applied ${response.data.applied} corrections`);
        await Promise.all([
          fetchSuggestedCorrections(),
          fetchCorrectionHistory(),
          fetchPendingItems()
        ]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply corrections');
    } finally {
      setApplyingCorrections(false);
    }
  };

  // ==================== SPLIT TRANSACTIONS ====================
  
  // Open split dialog for a transaction
  const openSplitDialog = (item) => {
    setSplitTransaction(item);
    setShowSplitDialog(true);
  };

  // ==================== MULTI-ALLOCATION ====================
  
  // Open multi-allocation dialog and fetch suggestions
  const openMultiAllocDialog = async (item) => {
    setMultiAllocTransaction(item);
    setMultiAllocLoading(true);
    setShowMultiAllocDialog(true);
    
    try {
      const direction = item.direction === 'inflow' ? 'inflow' : 'outflow';
      const response = await axios.post(
        `${API}/cash-settlement/suggest-multi-allocation`,
        null,
        {
          params: {
            amount: Math.abs(item.amount),
            direction: direction,
            description: item.description,
            transaction_date: item.date
          },
        }
      );
      setMultiAllocSuggestions(response.data);
      // Pre-select all suggestions
      setSelectedAllocations(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching multi-allocation suggestions:', error);
      toast.error('Failed to get allocation suggestions');
      setShowMultiAllocDialog(false);
    } finally {
      setMultiAllocLoading(false);
    }
  };

  // Apply multi-allocation
  const multiAllocAllowLockedRef = useRef(false);
  const applyMultiAllocation = async () => {
    if (!multiAllocTransaction || selectedAllocations.length === 0) return;
    
    setMultiAllocLoading(true);
    try {
      const docType = selectedAllocations[0]?.document_type;
      
      // Period lock check before applying allocation
      if (!multiAllocAllowLockedRef.current) {
        const txDate = multiAllocTransaction.date;
        const isLocked = await checkPeriodLock(
          txDate,
          async () => {
            // Post to original date - override lock
            multiAllocAllowLockedRef.current = true;
            await applyMultiAllocation();
            multiAllocAllowLockedRef.current = false;
          },
          async () => {
            // Post to today
            multiAllocAllowLockedRef.current = true;
            multiAllocTransaction.date = new Date().toISOString().split('T')[0];
            await applyMultiAllocation();
            multiAllocAllowLockedRef.current = false;
          }
        );
        if (isLocked) { setMultiAllocLoading(false); return; }
      }
      
      // Calculate totals including WHT and discounts
      const totalWht = Object.values(allocWhtAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
      const totalDiscount = Object.values(allocDiscountAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
      const totalAllocation = selectedAllocations.reduce((sum, a) => sum + a.suggested_allocation, 0);
      
      // For bank feed: bank amount = allocation - wht - discount (for inflows)
      // The bank shows what actually came in/went out
      const bankAmount = Math.abs(multiAllocTransaction.amount);
      
      const payload = {
        total_amount: bankAmount,  // Actual bank amount
        total_wht: totalWht,
        total_discount: totalDiscount,
        payment_date: multiAllocTransaction.date,
        payment_method: 'bank_transfer',
        bank_account_id: multiAllocTransaction.bank_account_id,
        reference: multiAllocTransaction.reference || multiAllocTransaction.description?.slice(0, 50),
        bank_feed_transaction_id: multiAllocTransaction.id,  // Pass bank feed ID for auto-completion
        allocations: selectedAllocations.map(a => ({
          document_id: a.document_id,
          document_type: a.document_type,
          amount: a.suggested_allocation,
          wht_amount: parseFloat(allocWhtAmounts[a.document_id]) || 0,
          discount_amount: parseFloat(allocDiscountAmounts[a.document_id]) || 0
        }))
      };
      
      const lockParam = multiAllocAllowLockedRef.current ? '?allow_locked_period=true' : '';
      await axios.post(
        `${API}/accrual-payments/bulk-allocation/apply${lockParam}`,
        payload,
      );
      
      const whtMsg = totalWht > 0 ? ` (WHT: ${formatTxAmount(multiAllocTransaction, totalWht)})` : '';
      const discountMsg = totalDiscount > 0 ? ` (Discount: ${formatTxAmount(multiAllocTransaction, totalDiscount)})` : '';
      toast.success(`Allocated to ${selectedAllocations.length} ${docType}s successfully${whtMsg}${discountMsg}`);
      
      setShowMultiAllocDialog(false);
      setMultiAllocTransaction(null);
      setMultiAllocSuggestions(null);
      setSelectedAllocations([]);
      setAllocWhtAmounts({});
      setAllocDiscountAmounts({});
      setShowWhtDiscountPanel(false);
      
      // Refresh transactions
      fetchPendingItems();
      fetchStats();
      fetchProcessedItems();
    } catch (error) {
      console.error('Error applying multi-allocation:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply allocation');
    } finally {
      setMultiAllocLoading(false);
    }
  };

  // Toggle allocation selection
  const toggleAllocationSelection = (allocation) => {
    setSelectedAllocations(prev => {
      const exists = prev.find(a => a.document_id === allocation.document_id);
      if (exists) {
        return prev.filter(a => a.document_id !== allocation.document_id);
      } else {
        return [...prev, allocation];
      }
    });
  };

  // Navigate to document creation for split transaction
  const navigateToSplitDocumentCreation = (docType) => {
    const item = splitTransaction;
    if (!item) return;
    
    // Build prefilled data for the document - similar to "Not Quite Right" but with split flag
    const prefillData = {
      amount: item.amount,
      date: item.date,
      description: item.description,
      counterparty: item.counterparty || item.ai_counterparty,
      reference: item.reference,
      bank_feed_transaction_id: item.id,
      gl_debit: item.ai_gl_debit,
      gl_credit: item.ai_gl_credit,
      bank_account_id: item.bank_account_id,
      currency: item.currency || bankAccounts.find(b => b.id === item.bank_account_id)?.currency || business?.currency,
      // SPLIT TRANSACTION FLAG - tells the document page to enable split mode
      split_transaction: true,
      original_status: item.status // Track if this was completed or review_needed
    };
    
    // Store prefill data in sessionStorage
    sessionStorage.setItem('bankFeedPrefill', JSON.stringify(prefillData));
    
    setShowSplitDialog(false);
    setSplitTransaction(null);
    
    // Open the same in-page dialogs as "Not Quite Right" (split mode via prefill flag)
    switch (docType) {
      case 'invoice':
        setDocDialog({ open: true, type: 'invoice', prefill: prefillData });
        break;
      case 'bill':
        setDocDialog({ open: true, type: 'bill', prefill: prefillData });
        break;
      case 'other_receipt':
        setOtherReceiptDialog({ open: true, prefill: prefillData });
        break;
      case 'other_payment':
        setOtherPaymentDialog({ open: true, prefill: prefillData });
        break;
      default:
        toast.error('Invalid document type');
    }
  };

  // ==================== TRAIN AI ====================
  
  // Create a new training example
  const handleCreateTrainingExample = async () => {
    if (!newTrainingExample.description || !newTrainingExample.classification || !newTrainingExample.gl_code) {
      toast.error('Please fill in description, classification, and GL account');
      return;
    }
    
  setSavingTraining(true);
    try {
      const response = await axios.post(`${API}/ai-corrections/training-examples`, newTrainingExample);
      
      // Check if validation warnings need confirmation
      if (response.data.needs_confirmation) {
        setTrainingWarnings(response.data.warnings || []);
        setShowWarningConfirm(true);
        setSavingTraining(false);
        return;
      }
      
      if (response.data.success) {
        // Show any acknowledged warnings
        if (response.data.warnings_acknowledged) {
          toast.info('Training rule created with noted concerns', { duration: 4000 });
        } else {
          toast.success('Training rule created! AI will learn from this.');
        }
        setShowTrainDialog(false);
        setTrainingWarnings([]);
        setNewTrainingExample({
          description: '',
          counterparty: '',
          direction: 'outflow',
          classification: '',
          gl_code: '',
          gl_name: '',
          amount_range: 'any',
          notes: ''
        });
        await Promise.all([fetchTrainingExamples(), fetchTrainingStats()]);
      }
    } catch (error) {
      // Handle validation errors
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.errors) {
        toast.error(errorDetail.errors.join('\n'), { duration: 6000 });
      } else {
        toast.error(errorDetail || 'Failed to create training example');
      }
    } finally {
      setSavingTraining(false);
    }
  };

  // Confirm training with warnings
  const handleConfirmTrainingWithWarnings = async () => {
    setSavingTraining(true);
    try {
      const response = await axios.post(`${API}/ai-corrections/training-examples?force=true`, newTrainingExample);
      
      if (response.data.success) {
        toast.info('Training rule created (warnings acknowledged)');
        setShowTrainDialog(false);
        setShowWarningConfirm(false);
        setTrainingWarnings([]);
        setNewTrainingExample({
          description: '',
          counterparty: '',
          direction: 'outflow',
          classification: '',
          gl_code: '',
          gl_name: '',
          amount_range: 'any',
          notes: ''
        });
        await Promise.all([fetchTrainingExamples(), fetchTrainingStats()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create training example');
    } finally {
      setSavingTraining(false);
    }
  };

  // Delete a training example
  const handleDeleteTrainingExample = async (exampleId) => {
    if (!window.confirm('Delete this training example? The AI will no longer use this rule.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/ai-corrections/training-examples/${exampleId}`);
      toast.success('Training example deleted');
      await fetchTrainingExamples();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete training example');
    }
  };

  // Toggle training example active status
  const handleToggleTrainingExample = async (exampleId, currentlyActive) => {
    try {
      await axios.put(`${API}/ai-corrections/training-examples/${exampleId}`, {
        is_active: !currentlyActive
      });
      toast.success(currentlyActive ? 'Training rule deactivated' : 'Training rule activated');
      await fetchTrainingExamples();
    } catch (error) {
      toast.error('Failed to update training example');
    }
  };

  // Import training from corrections
  const handleImportFromCorrections = async () => {
    setImportingFromCorrections(true);
    try {
      const response = await axios.post(`${API}/ai-corrections/training-examples/import-from-corrections`, {
        min_occurrences: 2
      });
      
      if (response.data.success) {
        const { examples_created, skipped_existing, patterns_found } = response.data;
        toast.success(`Imported ${examples_created} training rules from ${patterns_found} patterns`);
        if (skipped_existing > 0) {
          toast.info(`${skipped_existing} patterns already had rules`);
        }
        await Promise.all([fetchTrainingExamples(), fetchTrainingStats()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import from corrections');
    } finally {
      setImportingFromCorrections(false);
    }
  };

  // Prefill training from a transaction
  const prefillTrainingFromTransaction = (item) => {
    setNewTrainingExample({
      description: item.description || '',
      counterparty: item.counterparty || '',
      direction: item.direction || 'outflow',
      classification: item.classification || '',
      gl_code: item.gl_code || '',
      gl_name: item.gl_name || '',
      amount_range: Math.abs(item.amount) < 1000 ? 'small' : Math.abs(item.amount) < 10000 ? 'medium' : 'large',
      notes: ''
    });
    setShowTrainDialog(true);
  };

  // ==================== REVERSAL HANDLERS ====================
  
  // Open reversal dialog
  const openReversalDialog = (item) => {
    setReversalTransaction(item);
    setReversalReason('');
    setReversalNotes('');
    setShowReversalDialog(true);
  };

  // Mark transaction as reversed
  const handleMarkReversed = async () => {
    if (!reversalTransaction || !reversalReason) {
      toast.error('Please select a reason for the reversal');
      return;
    }
    
    setProcessingReversal(true);
    try {
      const response = await axios.post(`${API}/bank-reversals/mark-reversed`, {
        transaction_id: reversalTransaction.id,
        reason: reversalReason,
        notes: reversalNotes
      });
      
      if (response.data.success) {
        toast.success('Transaction marked as reversed. Journal entries have been reversed.');
        setShowReversalDialog(false);
        setReversalTransaction(null);
        setReversalReason('');
        setReversalNotes('');
        await Promise.all([
          fetchStats(),
          fetchProcessedItems(),
          fetchReversedTransactions()
        ]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse transaction');
    } finally {
      setProcessingReversal(false);
    }
  };

  // Link reversal pair from detected reversals
  const handleLinkReversalPair = async (original_id, reversal_id) => {
    try {
      const response = await axios.post(`${API}/bank-reversals/link-reversal`, null, {
        params: { original_id, reversal_id }
      });
      
      if (response.data.success) {
        toast.success('Transactions linked as reversal pair');
        await Promise.all([
          fetchStats(),
          fetchProcessedItems(),
          fetchDetectedReversals(),
          fetchReversedTransactions()
        ]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link reversal pair');
    }
  };

  // Dismiss detected reversal (false positive)
  const dismissDetectedReversal = (index) => {
    setDetectedReversals(prev => prev.filter((_, i) => i !== index));
  };

  // ==================== UNDO CLASSIFICATION HANDLERS ====================
  
  // Open undo classification dialog
  const openUndoClassificationDialog = async (item) => {
    setUndoClassificationTransaction(item);
    setShowUndoClassificationDialog(true);
    // iter1432 Gap #10 · Fetch cascade-undo preview so the dialog
    // can warn the user if there are downstream postings (interest
    // accruals, depreciation, revenue recognition) that will ALSO
    // be reversed if they opt in.
    setCascadeReverseChecked(false);
    setUndoPreview(null);
    try {
      // Find the settlement id linked to this tx (if any)
      const settlement = await axios.get(
        `${API}/bank-feeds/transactions/${item.id}/settlement-info`,
      ).catch(() => null);
      const settlementId = settlement?.data?.settlement_id;
      if (settlementId) {
        setUndoPreviewLoading(true);
        const prev = await axios.get(
          `${API}/bank-feeds/settlements/${settlementId}/undo-preview`,
        );
        setUndoPreview(prev.data);
      }
    } catch (e) {
      // Preview is best-effort — undo path still works without it.
      console.debug('undo preview failed', e);
    } finally {
      setUndoPreviewLoading(false);
    }
  };

  // Handle undo classification - moves transaction back to Review Needed
  const handleUndoClassification = async () => {
    if (!undoClassificationTransaction) return;
    
    setProcessingUndoClassification(true);
    try {
      const response = await undoWithGroupConfirm(undoClassificationTransaction.id, {
        reason: 'User requested re-classification',
        delete_documents: true,
        delete_schedules: true,
        // Cascade rule: downstream postings (accruals, depreciation, amortisation,
        // settlements) always go with the classification.
        cascade_reverse_downstream: true,
      });
      
      if (response.data.success) {
        const cascaded = response.data.deleted_items?.cascade_reversed_journals?.length || 0;
        if (cascaded > 0) {
          toast.success(`Transaction reversed. ${cascaded} downstream postings also reversed.`);
        } else {
          toast.success('Transaction moved back to Review Needed. You can now re-classify it.');
        }
        if (response.data.inventory_warning) {
          toast.warning(response.data.inventory_warning, { duration: 9000 });
        }
        setShowUndoClassificationDialog(false);
        setUndoClassificationTransaction(null);
        setUndoPreview(null);
        setCascadeReverseChecked(false);
        await Promise.all([
          fetchStats(),
          fetchPendingItems(),
          fetchProcessedItems()
        ]);
      }
    } catch (error) {
      if (error?.cancelled) return;
      const d = error.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to undo classification');
    } finally {
      setProcessingUndoClassification(false);
    }
  };

  // Reversal reason options - for actual bank reversals (bounced checks, chargebacks, etc.)
  const reversalReasons = [
    { value: 'payment_returned', label: 'Payment Returned' },
    { value: 'bounced_check', label: 'Bounced Check (NSF)' },
    { value: 'duplicate_reversal', label: 'Duplicate Transaction' },
    { value: 'refund', label: 'Refund' },
    { value: 'unauthorized', label: 'Unauthorized Transaction' },
    { value: 'bank_error', label: 'Bank Error' },
    { value: 'chargeback', label: 'Chargeback' },
    { value: 'fraud', label: 'Fraud' },
    { value: 'other', label: 'Other' }
  ];

  // Open match dialog
  const openMatchDialogFor = (item) => {
    setSelectedItem(item);
    setMatchDocumentType(item.direction === 'inflow' ? 'invoice' : 'bill');
    setSelectedDocument(null);
    setShowMatchDialog(true);
  };

  // Get status badge
  const getStatusBadge = (status, confidence = 0) => {
    switch (status) {
      case 'matched':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Matched</Badge>;
      case 'created':
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">In Books</Badge>;
      case 'review_needed':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Review Needed</Badge>;
      case 'duplicate':
        return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Duplicate</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Rejected</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Error</Badge>;
      case 'reversed':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Reversed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get direction icon
  const getDirectionIcon = (direction) => {
    if (direction === 'inflow') {
      return <ArrowDownRight className="w-4 h-4 text-green-600" />;
    }
    return <ArrowUpRight className="w-4 h-4 text-red-600" />;
  };

  // Get source icon
  const getSourceIcon = (source) => {
    switch (source) {
      case 'file_upload':
        return <FileUp className="w-4 h-4" />;
      case 'mono':
      case 'plaid':
      case 'stitch':
        return <Landmark className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  // Get source badge with label and icon
  const getSourceBadge = (source) => {
    if (!source) return null;
    
    const sourceConfig = {
      file_upload: { label: 'Statement Upload', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: <FileUp className="w-3 h-3" /> },
      mono: { label: 'Bank Sync', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: <Landmark className="w-3 h-3" /> },
      plaid: { label: 'Bank Sync', color: 'bg-green-100 text-green-700 border-green-200', icon: <Landmark className="w-3 h-3" /> },
      stitch: { label: 'Bank Sync', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: <Landmark className="w-3 h-3" /> },
    };
    
    const config = sourceConfig[source] || { label: source.replace(/_/g, ' '), color: 'bg-gray-100 text-gray-700 border-gray-200', icon: <Activity className="w-3 h-3" /> };
    
    return (
      <Badge variant="outline" className={`text-xs gap-1 ${config.color}`} data-testid={`source-badge-${source}`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  // Helper to get bank account name from ID
  const getBankLabel = (bankAccountId) => {
    if (!bankAccountId) return null;
    const acct = bankAccounts.find(b => b.id === bankAccountId);
    if (!acct) return null;
    const name = acct.bank_name || 'Bank';
    const last4 = acct.account_number ? `***${acct.account_number.slice(-4)}` : '';
    return (
      <Badge variant="outline" className="text-xs gap-1 bg-slate-50 text-slate-600 border-slate-200" data-testid={`bank-badge-${bankAccountId}`}>
        <Landmark className="w-3 h-3" />
        {name} {last4}
      </Badge>
    );
  };

  // ============================================
  // SECTION 4B: COMPUTED/FILTERED DATA
  // Memoized/filtered data for rendering
  // ============================================

  // Filter items
  const _matchTableFilters = (item) => {
    if (!tableFilters?.confidence || tableFilters.confidence === 'all') return true;
    const c = item.confidence ?? item.ai_confidence ?? 0;
    if (tableFilters.confidence === 'high') return c >= 0.80;
    if (tableFilters.confidence === 'med') return c >= 0.65 && c < 0.80;
    if (tableFilters.confidence === 'low') return c < 0.65;
    return true;
  };

  const filteredPendingItems = pendingItems.filter(item => {
    const matchesSearch = !searchTerm || 
      (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.counterparty || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDirection = filterDirection === 'all' || item.direction === filterDirection;
    const matchesBank = filterBankAccountId === 'all' || item.bank_account_id === filterBankAccountId;
    const matchesSource = filterSource === 'all' || (filterSource === 'connected' ? ['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source) : !['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source));

    // iter971k-C P1 - tax-eligibility chip filter. Matches when the tax is
    // already applied (auto by AI or manual by user) OR when the eligibility
    // engine suggests it (visible via `taxStatus` cache).
    const taxFilter = tableFilters?.tax;
    let matchesTax = true;
    if (taxFilter === 'vat') {
      const ts = taxStatus?.[item.id];
      matchesTax = !!(item.vat_applied || ts?.has_vat || ts?.suggested_vat);
    } else if (taxFilter === 'wht') {
      const ts = taxStatus?.[item.id];
      matchesTax = !!(item.wht_applied || ts?.has_wht || ts?.suggested_wht);
    } else if (taxFilter === 'none') {
      const ts = taxStatus?.[item.id];
      matchesTax = !(item.vat_applied || item.wht_applied || ts?.has_vat || ts?.has_wht);
    }

    return matchesSearch && matchesDirection && matchesBank && matchesSource && matchesTax && _matchTableFilters(item);
  });

  const filteredProcessedItems = processedItems.filter(item => {
    const matchesSearch = !searchTerm || 
      (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.counterparty || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDirection = filterDirection === 'all' || item.direction === filterDirection;
    const matchesBank = filterBankAccountId === 'all' || item.bank_account_id === filterBankAccountId;
    const matchesSource = filterSource === 'all' || (filterSource === 'connected' ? ['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source) : !['mono', 'plaid', 'stripe', 'open_banking'].includes(item.source));

    // iter971k-C P1 - mirror the tax-eligibility filter on Completed rows.
    const taxFilter = tableFilters?.tax;
    let matchesTax = true;
    if (taxFilter === 'vat') {
      const ts = taxStatus?.[item.id];
      matchesTax = !!(item.vat_applied || ts?.has_vat || ts?.suggested_vat);
    } else if (taxFilter === 'wht') {
      const ts = taxStatus?.[item.id];
      matchesTax = !!(item.wht_applied || ts?.has_wht || ts?.suggested_wht);
    } else if (taxFilter === 'none') {
      const ts = taxStatus?.[item.id];
      matchesTax = !(item.vat_applied || item.wht_applied || ts?.has_vat || ts?.has_wht);
    }

    return matchesSearch && matchesDirection && matchesBank && matchesSource && matchesTax && _matchTableFilters(item);
  });

  // iter971k-C P1 - chip counts. Uses the same match logic as the filter
  // so the chip label always matches the resulting row count.
  const _isVatCandidate = (item) => {
    const ts = taxStatus?.[item.id];
    return !!(item.vat_applied || ts?.has_vat || ts?.suggested_vat);
  };
  const _isWhtCandidate = (item) => {
    const ts = taxStatus?.[item.id];
    return !!(item.wht_applied || ts?.has_wht || ts?.suggested_wht);
  };
  const pendingVatCandidates = pendingItems.filter(_isVatCandidate).length;
  const pendingWhtCandidates = pendingItems.filter(_isWhtCandidate).length;
  const processedVatCandidates = processedItems.filter(_isVatCandidate).length;
  const processedWhtCandidates = processedItems.filter(_isWhtCandidate).length;
  const pendingPotentialInputVat = pendingItems.reduce((sum, item) => {
    if (!_isVatCandidate(item)) return sum;
    return sum + Number(item.vat_amount || taxStatus?.[item.id]?.vat_amount || 0);
  }, 0);
  const processedPotentialInputVat = processedItems.reduce((sum, item) => {
    if (!_isVatCandidate(item)) return sum;
    return sum + Number(item.vat_amount || taxStatus?.[item.id]?.vat_amount || 0);
  }, 0);

  // Phase #5 - Stage an inline category edit instead of saving immediately.
  // The actual cascade (journal reversal + repost + document/schedule/
  // notification refresh) happens server-side when the user clicks
  // "Save N changes" in BankFeedSaveChangesBar, which calls
  // handleCommitPendingEdits below.
  //
  // The "Category" column always shows the non-bank side (debit for
  // outflows, credit for inflows) - we track which side to update on save.
  const handleInlineGlChange = useCallback(async (tx, account) => {
    if (!account?.code) return;
    // iter1101-fix - PREFER tx.direction when set. The old logic
    // `direction === 'inflow' || amount > 0` incorrectly flipped
    // Plaid-ingested outflows (Plaid stores amounts as POSITIVE with a
    // separate direction flag) to isInflow=true, which caused every
    // reclassify via this picker to stage the GL on the WRONG side
    // (credit for outflows). Only fall back to amount when direction is
    // missing entirely — matches the convention already used in
    // BankReconciliationCenter.jsx (line 87).
    const isInflow = tx.direction
      ? tx.direction === 'inflow'
      : (tx.amount || 0) > 0;
    const currentSide = isInflow ? tx.ai_gl_credit : tx.ai_gl_debit;
    if (account.code === currentSide) {
      // Reverting to the original GL - remove from pending if present.
      setPendingGlEdits((prev) => {
        if (!prev.has(tx.id)) return prev;
        const next = new Map(prev);
        next.delete(tx.id);
        return next;
      });
      return;
    }
    setPendingGlEdits((prev) => {
      const next = new Map(prev);
      next.set(tx.id, {
        glCode: account.code,
        glName: account.name,
        originalCode: currentSide,
        isInflow,
        description: tx.description,
        status: tx.status,
      });
      return next;
    });
  }, []);

  // Phase 2.1 (Feb 2026) - Apply an AI suggestion (from description edit)
  // as a staged GL edit. Reuses the same handleInlineGlChange path so the
  // visual "• Will apply on Approve" / "• Unsaved + Save" pattern is the
  // single source of truth - no separate "AI-suggested" state to track.
  const handleApplyAiSuggestion = useCallback(async (tx, suggestion) => {
    if (!suggestion?.suggested_gl_code) return;
    const account = {
      code: suggestion.suggested_gl_code,
      name: suggestion.suggested_gl_name || suggestion.suggested_gl_code,
    };
    await handleInlineGlChange(tx, account);
    toast.success(`AI suggestion applied: ${account.name}. Click Approve to save & post.`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleInlineGlChange]);

  // Phase #5 - Commit all queued category edits. Each row uses the
  // canonical save-changes endpoint for completed rows or the reclassify
  // endpoint for pending rows; the server cascades to journals,
  // documents, schedules and notifications. Per-row failures are
  // collected so successful ones are still applied.
  const handleCommitPendingEdits = useCallback(async () => {
    if (pendingGlEdits.size === 0) return;
    const specialEdits = [...pendingGlEdits.entries()].filter(([txId, edit]) => {
      const row = [...pendingItems, ...processedItems].find((r) => r.id === txId);
      return specialDocTypeForGl(row || { direction: edit.isInflow ? 'inflow' : 'outflow' }, edit.glCode) || isSpecialItemRow(row);
    });
    if (specialEdits.length > 0) {
      if (pendingGlEdits.size === 1) {
        // A single change is a per-row save: open the special-item decision flow.
        const [txId] = specialEdits[0];
        const row = [...pendingItems, ...processedItems].find((r) => r.id === txId);
        if (row) { await handleSaveRowRef.current?.(row); return; }
      }
      toast.error(`${specialEdits.length} of your ${pendingGlEdits.size} changes involve special items (loans, prepayments, investments, fixed assets, deferred revenue). Bulk save can't apply those — each needs its own match-existing / track-as-new decision. Save them one row at a time.`, { duration: 9000 });
      return;
    }
    setSavingPendingEdits(true);
    const successes = [];
    const failures = [];
    try {
      for (const [txId, edit] of pendingGlEdits.entries()) {
        try {
          const isCompleted = ['completed', 'matched', 'processed', 'created'].includes(edit.status);
          if (isCompleted) {
            const payload = {
              reason: 'Bulk save from category queue',
            };
            if (edit.isInflow) payload.gl_credit = edit.glCode;
            else payload.gl_debit = edit.glCode;
            const resp = await axios.post(`${API}/bank-feeds/save-changes/${txId}`, payload);
            if (resp.data?.inventory_warning) {
              toast.warning(resp.data.inventory_warning, { duration: 9000 });
            }
            // Iter 809 (F5) - surface auto tax re-eval to user
            if (resp.data?.classification_changed) {
              invalidateTaxStatus(txId);
              const diff = resp.data?.tax_suggestion_diff;
              if (diff && (diff.suggested_vat || diff.suggested_wht) && !diff.can_apply_vat === false) {
                toast.info(
                  `Tax suggestion updated for ${edit.glCode}: ${diff.suggested_vat ? 'VAT' : ''}${diff.suggested_vat && diff.suggested_wht ? ' + ' : ''}${diff.suggested_wht ? 'WHT' : ''} now applicable.`,
                  { duration: 4500 }
                );
              }
            }
          } else {
            const reclassifyBody = {
              new_classification: 'expense',
              reason: 'Bulk save from category queue',
              apply_learning: true,
            };
            if (edit.isInflow) reclassifyBody.new_gl_credit = edit.glCode;
            else reclassifyBody.new_gl_debit = edit.glCode;
            await axios.post(`${API}/bank-feeds/reclassify/${txId}`, reclassifyBody);
          }
          successes.push(txId);
        } catch (err) {
          const msg = err?.response?.data?.detail || err?.message || 'Update failed';
          failures.push({ txId, msg });
        }
      }

      if (successes.length > 0) {
        // Clear successful ones from the queue
        setPendingGlEdits((prev) => {
          const next = new Map(prev);
          for (const id of successes) next.delete(id);
          return next;
        });
        toast.success(
          `${successes.length} ${successes.length === 1 ? 'change' : 'changes'} saved. Journals reversed and re-posted with cascades to documents, schedules and notifications.`,
          { duration: 5000 }
        );
      }
      if (failures.length > 0) {
        toast.error(
          `${failures.length} ${failures.length === 1 ? 'change' : 'changes'} failed. First error: ${failures[0].msg}`,
          { duration: 6000 }
        );
      }

      // Refresh derived data
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    } finally {
      setSavingPendingEdits(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGlEdits, fetchStats, fetchPendingItems, fetchProcessedItems, invalidateTaxStatus]);

  const handleDiscardPendingEdits = useCallback(() => {
    setPendingGlEdits(new Map());
    toast.info('Pending changes discarded: table reverted.');
  }, []);

  // Phase 1.3 (Feb 2026) - per-row Save Changes for the Completed tab.
  // Replaces the global "Save N changes" bar for the single-row case so a
  // user editing one Completed transaction can save inline without leaving
  // their row. Server-side semantics identical to handleCommitPendingEdits
  // (reverse + repost via /bank-feeds/save-changes/{id}).
  const handleSaveRowRef = useRef(null);
  const handleSaveRow = useCallback(async (tx) => {
    const edit = pendingGlEdits.get(tx.id);
    if (!edit) return;
    setSavingPendingEdits(true);
    try {
      const isCompleted = ['completed', 'matched', 'processed', 'created'].includes(edit.status);
      if (!isCompleted) {
        toast.info('Click Approve to save and post this transaction.');
        return;
      }
      // Special-item category → open the Match-existing / Track-as-new decision
      // first; nothing is posted until that flow completes (it cascades the old
      // classification away itself).
      const newSpecialType = specialDocTypeForGl(tx, edit.glCode);
      if (newSpecialType) {
        const inferred = inferDocFromGl(edit.glCode, tx.direction, availableGlAccounts);
        setSpecialItemModal({ open: true, transaction: tx, docType: newSpecialType, inBooks: true,
          glEdit: { ...edit, sub_category: inferred?.sub_category } });
        return;
      }
      // Special item → ordinary category: full cascade first, then post the new category.
      if (isSpecialItemRow(tx)) {
        await undoRowCascade(tx.id, `Reclassified from ${tx.document_type} to ${edit.glCode}`);
        const inferred = inferDocFromGl(edit.glCode, tx.direction, availableGlAccounts);
        const body = { new_classification: inferred?.sub_category, new_document_type: inferred?.document_type, reason: 'In Books reclassify from special item', apply_learning: true };
        if (edit.isInflow) body.new_gl_credit = edit.glCode; else body.new_gl_debit = edit.glCode;
        await axios.post(`${API}/bank-feeds/reclassify/${tx.id}`, body);
        await axios.post(`${API}/bank-feeds/approve/${tx.id}`, null, { params: { quick_approve: true, allow_locked_period: true, gl_code: edit.glCode, user_tax_only: true } });
        setPendingGlEdits((prev) => { const n = new Map(prev); n.delete(tx.id); return n; });
        toast.success('Previous special-item treatment removed (schedule and postings) and the transaction re-posted to the new category.');
        await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
        return;
      }
      const payload = { reason: 'Inline single-row save from Completed tab' };
      if (edit.isInflow) payload.gl_credit = edit.glCode;
      else payload.gl_debit = edit.glCode;
      const resp = await axios.post(`${API}/bank-feeds/save-changes/${tx.id}`, payload);
      if (resp.data?.inventory_warning) {
        toast.warning(resp.data.inventory_warning, { duration: 9000 });
      }
      // Iter 809 (F5) - live tax re-eval pop on row-level save
      if (resp.data?.classification_changed) {
        invalidateTaxStatus(tx.id);
        const diff = resp.data?.tax_suggestion_diff;
        if (diff && (diff.suggested_vat || diff.suggested_wht)) {
          toast.info(
            `Tax suggestion updated based on new category${diff.suggested_vat ? '. VAT recommended' : ''}${diff.suggested_wht ? '. WHT recommended' : ''}.`,
            { duration: 4500 }
          );
        }
      }
      setPendingGlEdits((prev) => {
        if (!prev.has(tx.id)) return prev;
        const next = new Map(prev);
        next.delete(tx.id);
        return next;
      });
      toast.success('Changes saved: journal reversed and re-posted with the new GL.');
      await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the row.');
    } finally {
      setSavingPendingEdits(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGlEdits, fetchStats, fetchPendingItems, fetchProcessedItems, invalidateTaxStatus]);
  handleSaveRowRef.current = handleSaveRow;

  // Phase 2.1 (Feb 2026) - Description-driven AI re-classify.
  // After a user edits the description, we ask the backend what GL the
  // AI would now suggest. Non-blocking; returns null on any error so the
  // description save still succeeds.
  const handleRequestAiSuggestion = useCallback(async (tx, newDescription) => {
    try {
      const res = await axios.post(
        `${API}/bank-feeds/reclassify-by-description/${tx.id}`,
        { new_description: newDescription },
      );
      return res?.data || null;
    } catch (err) {
      const status = err?.response?.status;
      if (status && status !== 502) {
        toast.error(err?.response?.data?.detail || 'AI suggestion failed.');
      }
      return null;
    }
  }, []);

  // Phase D - inline description edit. PATCHes the bank-feed staging row and
  // mirrors the new description onto the linked document. Journals are not
  // re-posted because descriptions are cosmetic to the journal entry.
  const handleInlineDescriptionChange = useCallback(async (tx, newDescription) => {
    try {
      await axios.patch(`${API}/bank-feeds/transactions/${tx.id}/description`, {
        description: newDescription,
      });
      toast.success('Description updated');
      await Promise.all([fetchPendingItems(), fetchProcessedItems()]);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 423) {
        toast.error(msg || 'Period is locked: unlock first.');
      } else {
        toast.error(msg || 'Failed to update description');
      }
      throw err; // let the cell stay in edit mode
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPendingItems, fetchProcessedItems]);

  // Jul 17 - contact/counterparty name fix (bank statements garble names)
  const handleInlineCounterpartyChange = useCallback(async (tx, newName) => {
    try {
      await axios.patch(`${API}/bank-feeds/transactions/${tx.id}/counterparty`, {
        counterparty: newName,
      });
      toast.success('Contact name updated');
      await Promise.all([fetchPendingItems(), fetchProcessedItems()]);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to update contact name');
      throw err;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPendingItems, fetchProcessedItems]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full py-20">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#108981] mx-auto mb-4" />
            <p className="text-muted-foreground">Loading Bank Transactions Center...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ============================================
  // SECTION 5: MAIN RENDER
  // The main JSX return with all tabs and dialogs
  // ============================================

  return (
    <DashboardLayout>
      <div className="space-y-4 lg:space-y-8 p-3 lg:p-6" data-testid="bank-feed-center">
        {/* Classification Progress Tracker - shows for selected bank OR auto-detected processing bank (lazy) */}
        {(selectedBankAccountId || processingBankAccountId) && (
          <Suspense fallback={null}>
            <ClassificationProgressTracker
              bankAccountId={selectedBankAccountId || processingBankAccountId}
              bankName={processingBankName || undefined}
              onComplete={() => { fetchStats(); fetchPendingItems(); fetchProcessedItems(); setProcessingBankAccountId(null); }}
            />
          </Suspense>
        )}
        {/* Header (Phase 3 cont. - extracted) */}
        <BankFeedHeader
          loading={loading}
          uploading={uploading}
          uploadProgress={uploadProgress}
          fileInputRef={fileInputRef}
          onFileUpload={handleFileUpload}
          onOpenUploadStarter={openUploadStarter}
          onOpenTransfer={() => setShowTransferModal(true)}
          onRefreshCache={fetchStats}
        />

        {/* iter 990 - prompt when a manual account needs its opening balance */}
        <OpeningBalanceNeededBanner />

        {/* Backlog fee confirmation is now rendered exclusively by
            `GlobalUploadBacklogModalProvider` (mounted at DashboardLayout
            level). The page previously rendered its OWN copy of
            `<UploadBacklogFeeDialog>` driven by page-local
            `backlogDialogJob` state — the exact "two modals opening,
            only one closes" bug users have hit repeatedly. Since
            BankFeedCenterPage always renders inside DashboardLayout
            (which provides `BackgroundJobsProvider`), the global
            provider observes the same `awaiting_confirmation` job via
            polling and shows a single, mutex-coordinated dialog.
            Page-local state (`backlogDialogJob`, `handleBacklogConfirm`,
            `handleBacklogCancel`) remains for now to keep the many
            call-sites working — the mounted dialog is what was creating
            the duplicate render, and that is now gone. Feb 2026 (iter1247). */}


        {/* Bank Feed Sync Progress Banner */}
        <BankFeedSyncBanner bankAccounts={bankAccounts} />

        {/* iter971kI - Orphan-transaction recovery. Surfaces mono_transactions
            rows that were fetched from the bank but never bridged to
            bank_feed_transactions (Henatos root cause). One-click self-heal. */}
        <OrphanRecoveryCard bankAccounts={bankAccounts} />

        {/* Per-account "Refresh Now" tiles for Mono-connected banks. Each
            tile carries its own reauth / stale / fetch-failed signal so we
            never show a duplicate global banner here: see Phase 4k.
            iter959 post-signoff: use the picker variant everywhere (Home,
            Bank Transactions Center, Bank Accounts) so the "Update bank transactions"
            CTA is a single consistent surface. */}
        <MonoBanksRefreshSection
          bankAccounts={bankAccounts}
          onRefreshComplete={() => { fetchPendingItems(); fetchStats(); }}
        />

        {/* iter-bugfix (G-2): Plaid parity - same per-account tile experience
            for US/UK Plaid banks. */}
        <PlaidBanksRefreshSection
          bankAccounts={bankAccounts}
          plaidConnections={plaidConnections}
          onRefreshComplete={() => { fetchPendingItems(); fetchStats(); }}
        />

        {/* iter1358 · Smart Match + AI Assist education. Sits above the
            KPI strip. Silently dismisses to localStorage once seen; forces
            visibility (and switches to the "paused" variant) when the
            tenant is at their AI cap for this cycle. */}
        {aiPaused && <SmartMatchEducationCard aiPaused={aiPaused} />}

        {/* Stats Cards (Phase 3 - extracted) */}
        <BankFeedKPIStrip
          stats={stats}
          reconSummary={reconSummary}
          activityCounts={activityCounts}
          uploadedStatements={uploadedStatements}
          onOpenCompleted={() => { setActiveTab('transactions'); setTxFilter('processed'); markSeen('bf_completed'); }}
          onOpenReviewNeeded={() => { setActiveTab('transactions'); setTxFilter('pending'); markSeen('bf_review'); }}
          onOpenDuplicates={() => { setActiveTab('transactions'); setTxFilter('duplicates'); markSeen('bf_duplicates'); }}
          onOpenStatements={() => { setActiveTab('uploads'); }}
          onOpenReconciliation={() => setActiveTab('reconciliation')}
        />

        {/* iter1238 P1c - Streaming Bank Feed hydration.
        {/* iter1238 Phase 8.11 · StreamingParsedFeed removed from
            inline position — it now renders inside StreamingUploadModal
            triggered from the global upload progress banner's "View
            progress" button. This gives the user a scrollable, closable
            modal available from every page, not just Bank Feeds. */}

        {/* Sep 2026 · closed-period banner: shown only while rows are held */}
        {closedPeriodInfo?.held_total > 0 && <ClosedPeriodNotice info={closedPeriodInfo} />}

        {/* Main Content Tabs - Simplified Navigation */}
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); if (val !== 'transactions') setMoreSection(null); }} className="space-y-6">
          <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 lg:-mx-6 lg:px-6">
            <TabsList className="bg-gray-100 inline-flex min-w-max">
              <TabsTrigger value="overview" className="gap-2 whitespace-nowrap" data-testid="dashboard-tab">
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="transactions" className="gap-2 whitespace-nowrap" data-testid="transactions-tab">
                <Activity className="w-4 h-4" />
                Transactions
                {(stats.review_needed + stats.failed + potentialDuplicates.length) > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs">
                    {stats.review_needed + stats.failed + potentialDuplicates.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="uploads" className="gap-2 whitespace-nowrap" data-testid="uploads-tab">
                <FileUp className="w-4 h-4" />
                Uploads
                {uploadedStatements.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs">
                    {uploadedStatements.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="reconciliation" className="gap-2 whitespace-nowrap" data-testid="reconciliation-tab">
                <Scale className="w-4 h-4" />
                Reconciliation
                {reconSummary && reconSummary.total_unmatched > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs bg-violet-100 text-violet-700">
                    {reconSummary.total_unmatched}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="transfers" className="gap-2 whitespace-nowrap" data-testid="transfers-tab">
                <ArrowLeftRight className="w-4 h-4" />
                Transfers
              </TabsTrigger>
            </TabsList>

            {/* Train AI - surfaced as a primary action (iter 742) so the core
                learning loop is discoverable without diving into the More menu */}
            <Button
              variant={activeTab === 'more' && moreSection === 'ai-learning' ? 'secondary' : 'outline'}
              size="sm"
              className="gap-1.5 whitespace-nowrap relative"
              data-testid="train-ai-tab"
              onClick={() => { setActiveTab('more'); setMoreSection('ai-learning'); }}
            >
              <Brain className="w-4 h-4 text-purple-600" />
              Train AI
              {suggestedCorrections.length > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-0.5 px-1.5 h-5 bg-amber-100 text-amber-700 text-[10px]"
                  data-testid="train-ai-tab-badge"
                >
                  {suggestedCorrections.length}
                </Badge>
              )}
            </Button>

            {/* More Dropdown for advanced/config sections */}
            <DropdownMenu onOpenChange={(open) => { if (open && activeTab === 'more') { setMoreSection(null); setActiveTab('transactions'); } }}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={moreSection ? 'secondary' : 'outline'}
                  size="sm"
                  className="gap-1.5 whitespace-nowrap relative"
                  data-testid="more-menu-btn"
                >
                  <Settings2 className="w-4 h-4" />
                  More
                  {/* iter 741 - surface suggested-rules count so users
                      discover the new SuggestedRulesPanel without
                      having to open the menu first. */}
                  {suggestedCorrections.length > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 px-1.5 h-5 bg-amber-100 text-amber-700 text-[10px]"
                      data-testid="more-menu-badge"
                    >
                      {suggestedCorrections.length}
                    </Badge>
                  )}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {/* iter1251 · "Connections" removed — Bank Accounts page
                    already surfaces connected banks with more context
                    (last-synced, reconnect CTA, per-account balances).
                    Duplicating it here forced users to keep two
                    read-only lists in sync. */}
                <DropdownMenuItem
                  onSelect={() => { setActiveTab('more'); setMoreSection('ai-learning'); }}
                  data-testid="more-ai-learning"
                >
                  <Lightbulb className="w-4 h-4 mr-2" />
                  AI Learning
                  {suggestedCorrections.length > 0 && (
                    <Badge variant="secondary" className="ml-auto bg-amber-100 text-amber-700 text-xs">
                      {suggestedCorrections.length}
                    </Badge>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => { setActiveTab('more'); setMoreSection('auto-discovery'); }}
                  data-testid="more-auto-discovery"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Auto-Discovery
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Slim Export bar - filters moved into the comprehensive TransactionTableHeader below */}
          {activeTab === 'transactions' && (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-btn">
                    <CloudDownload className="w-4 h-4 mr-1.5" />
                    Export
                    <ChevronDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="export-csv"
                    onClick={async () => {
                      const params = new URLSearchParams({ format: 'csv' });
                      if (txFilter === 'pending') params.set('status_filter', 'review_needed');
                      else if (txFilter === 'processed') params.set('status_filter', 'completed');
                      if (filterBankAccountId !== 'all') params.set('bank_account_id', filterBankAccountId);
                      if (filterSource !== 'all') params.set('source', filterSource);
                      if (filterDirection !== 'all') params.set('direction', filterDirection);
                      if (filterGlCode !== 'all') params.set('gl_code', filterGlCode);
                      if (filterStartDate) params.set('start_date', filterStartDate);
                      if (filterEndDate) params.set('end_date', filterEndDate);
                      try {
                        const resp = await axios.get(`${API}/bank-feeds/export?${params}`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([resp.data]));
                        const a = document.createElement('a'); a.href = url;
                        a.download = `bank_feed_export_${filterStartDate || 'all'}_${filterEndDate || 'all'}.csv`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('CSV exported successfully');
                      } catch { toast.error('Export failed'); }
                    }}
                  >
                    <TableIcon className="w-4 h-4 mr-2" />
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="export-xlsx"
                    onClick={async () => {
                      const params = new URLSearchParams({ format: 'xlsx' });
                      if (txFilter === 'pending') params.set('status_filter', 'review_needed');
                      else if (txFilter === 'processed') params.set('status_filter', 'completed');
                      if (filterBankAccountId !== 'all') params.set('bank_account_id', filterBankAccountId);
                      if (filterSource !== 'all') params.set('source', filterSource);
                      if (filterDirection !== 'all') params.set('direction', filterDirection);
                      if (filterGlCode !== 'all') params.set('gl_code', filterGlCode);
                      if (filterStartDate) params.set('start_date', filterStartDate);
                      if (filterEndDate) params.set('end_date', filterEndDate);
                      try {
                        const resp = await axios.get(`${API}/bank-feeds/export?${params}`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([resp.data]));
                        const a = document.createElement('a'); a.href = url;
                        a.download = `bank_feed_export_${filterStartDate || 'all'}_${filterEndDate || 'all'}.xlsx`;
                        document.body.appendChild(a); a.click(); a.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('Excel exported successfully');
                      } catch { toast.error('Export failed'); }
                    }}
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Suspense fallback={<TabLoader />}>
              <BankFeedOverviewTab
                fileInputRef={fileInputRef}
                onOpenUploadStarter={openUploadStarter}
                onOpenBankingClick={() => setShowOpenBankingSetup(true)}
                connections={connections}
                business={business}
                aiSettings={aiSettings}
                onToggleAutoApprove={(val) => handleToggleMediumConfidence(val)}
                savingAiSettings={savingAiSettings}
                processedItems={processedItems}
                isNewTransaction={isNewTransaction}
                formatAmount={formatAmount}
                getDirectionIcon={getDirectionIcon}
                getStatusBadge={getStatusBadge}
              />
            </Suspense>
          </TabsContent>

          {/* Unified Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4" data-testid="transactions-tab-content">
            {stats && stats.total !== ((stats.completed || 0) + (stats.review_needed || 0) + (stats.awaiting_terms || 0) + (stats.awaiting_bank || 0) + (stats.duplicates || 0) + (stats.failed || 0)) && (
              <div className="flex items-center justify-between gap-3 px-3.5 py-2 rounded-lg border border-rose-200 bg-rose-50" data-testid="feed-counts-mismatch-banner">
                <p className="text-xs text-rose-700">
                  The transaction counts don&apos;t tally (Total {stats.total} vs categories {(stats.completed || 0) + (stats.review_needed || 0) + (stats.awaiting_terms || 0) + (stats.awaiting_bank || 0) + (stats.duplicates || 0) + (stats.failed || 0)}). This shouldn&apos;t happen.
                </p>
                <ReportIssueButton
                  label="Report it"
                  testId="feed-counts-mismatch-report-btn"
                  fingerprint="feed_counts_mismatch"
                  issueContext={{ trigger: 'feed_counts_mismatch', stats }}
                />
              </div>
            )}
            {txFilter === 'failed' && (stats?.failed || 0) > 0 && (
              <div className="flex justify-end" data-testid="failed-feeds-report-wrap">
                <ReportIssueButton
                  label={`Report ${stats.failed} failed transaction${stats.failed === 1 ? '' : 's'}`}
                  variant="subtle"
                  testId="failed-feeds-report-btn"
                  fingerprint="failed_feed_transactions"
                  issueContext={{ trigger: 'failed_feed_transactions', failed_count: stats.failed }}
                />
              </div>
            )}
            <Suspense fallback={<TabLoader />}>
              <BankFeedTransactionsTab bf={{
                // filters / chips
                txFilter, setTxFilter, stats, potentialDuplicates,
                // iter971k-C P1 - chip candidate counts (context-aware, based
                // on currently active tab; both provided for the header).
                pendingVatCandidates, pendingWhtCandidates, pendingPotentialInputVat,
                processedVatCandidates, processedWhtCandidates, processedPotentialInputVat,
                // data
                filteredPendingItems, filteredProcessedItems, duplicateItems, failedItems,
                selectedItems, bulkProcessing, isApproving, autoApproveOpen, setAutoApproveOpen,
                selectedBankCurrency, availableGlAccounts, bankAccounts, business, taxCtx, taxStatus, tour,
                tableFilters, setTableFilters, searchTerm, setSearchTerm,
                filterDirection, setFilterDirection,
                filterBankAccountId, setFilterBankAccountId,
                filterSource, setFilterSource,
                setFilterGlCode, setFilterStartDate, setFilterEndDate,
                filterGlCode, filterStartDate, filterEndDate,
                txViewMode, setTxViewMode,
                pendingPage, pendingTotalPages, pendingTotalCount, setPendingPage, fetchPendingItems,
                processedPage, processedTotalPages, processedTotalCount, setProcessedPage, fetchProcessedItems,
                // iter1107 - awaiting bank
                awaitingBankItems, awaitingBankTotalCount, fetchAwaitingBankItems,
                toggleSelectAll, clearSelection, toggleItemSelection,
                initiateBulkAction, openBulkReclassifyDialog, openBulkCategoryDialog, openBulkUndoDialog,
                handleQuickApprove, openReclassifyDialog, openSplitDialog, openTaxPaymentDialog, openMultiAllocDialog,
                handleInlineGlChange, handleInlineDescriptionChange, handleInlineCounterpartyChange, handleUnlinkTx, openLinkDialogForTx,
                pendingGlEdits,    // Phase #5: staged edits
                handleSaveRow,     // Phase 1.3: per-row Save for Completed
                handleRequestAiSuggestion, // Phase 2.1. AI re-classify after description edit
                handleApplyAiSuggestion,   // Phase 2.1: one-click apply
                handleToggleVat, handleToggleWht, togglingTax, fetchTaxStatus,
                openUndoClassificationDialog, navigateToDocument,
                openInBooks: (tx) => setInBooksTx(tx),
                setSelectedItem, setShowDetailDialog,
                handleProcessDuplicateAnyway, handleDismissDuplicate,
                handleResolvePotentialDuplicate, processingId, handleRetryFailed,
                openManualProcessDialog, handleDismissFailed,
                isNewTransaction, formatAmount, getSourceBadge, getBankLabel,
                getDirectionIcon, getStatusBadge,
              }} />
            </Suspense>
          </TabsContent>

          {/* Uploads Tab - List of uploaded statements */}
          <TabsContent value="uploads" className="space-y-4" data-testid="uploads-content">
            <Suspense fallback={<TabLoader />}>
              <BankFeedUploadsTab
                uploadedStatements={uploadedStatements}
                fileInputRef={fileInputRef}
                onOpenUploadStarter={openUploadStarter}
                bankAccounts={bankAccounts}
                handleDeleteStatement={handleDeleteStatement}
                deletingStatementId={deletingStatementId}
                handleDownloadStatement={handleDownloadStatement}
                downloadingStatementId={downloadingStatementId}
                onStatementResumed={fetchUploadedStatements}
              />
            </Suspense>
          </TabsContent>

          {/* Reconciliation Tab - Top Level */}
          <TabsContent value="reconciliation" className="space-y-6" data-testid="reconciliation-tab-content">
            <Suspense fallback={<TabLoader />}>
              <BankFeedReconciliationTab
                bankAccounts={bankAccounts}
                business={business}
                reconBankAccountId={reconBankAccountId}
                setReconBankAccountId={setReconBankAccountId}
                reconSummary={reconSummary}
                formatAmount={formatAmount}
                fetchReconSummary={fetchReconSummary}
              />
            </Suspense>
          </TabsContent>

          {/* Inter-Bank Transfers Tab - iter1302 */}
          <TabsContent value="transfers" className="space-y-6" data-testid="transfers-tab-content">
            <Suspense fallback={<TabLoader />}>
              <InterBankTransfersTab bankAccounts={bankAccounts} />
            </Suspense>
          </TabsContent>

          {/* More Section - Connections, AI Learning, Auto-Discovery */}
          <TabsContent value="more" className="space-y-6" data-testid="more-content">
            {/* Section header */}
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={() => { setActiveTab('overview'); setMoreSection(null); }} className="gap-1">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <span className="text-sm text-muted-foreground">/</span>
              <span className="text-sm font-medium capitalize">{moreSection?.replace('-', ' ')}</span>
            </div>

            {/* iter1251 · Connections section removed. Bank Accounts
                page is the single source of truth for connected bank
                status. */}

            {/* AI Learning Content */}
            {moreSection === 'ai-learning' && (
            <div className="space-y-6" data-testid="ai-learning-content">
            <RecentlyLearnedRules />
            {/* AI Learning Header */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <Lightbulb className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">AI Learning Engine</h3>
                    <p className="text-muted-foreground">
                      Every correction you make here teaches the AI.
                      <InfoTip title="How learning works" testId="tip-ai-learning" className="ml-1">When you reclassify a transaction, the AI remembers it and suggests the same correction for similar transactions in future.</InfoTip>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Suggested Corrections */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      Suggested Corrections
                    </CardTitle>
                    <CardDescription>
                      Based on your past corrections, the AI suggests these changes
                    </CardDescription>
                  </div>
                  {suggestedCorrections.length > 0 && (
                    <Button
                      onClick={handleApplyAllCorrections}
                      disabled={applyingCorrections}
                      className="bg-amber-600 hover:bg-amber-700 gap-2"
                      data-testid="apply-all-corrections-btn"
                    >
                      {applyingCorrections ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Apply All ({suggestedCorrections.length})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingCorrections ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    <span className="ml-2 text-muted-foreground">Loading suggestions...</span>
                  </div>
                ) : suggestedCorrections.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-muted-foreground">No corrections suggested</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      The AI will suggest corrections as you reclassify transactions
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {suggestedCorrections.map((suggestion, index) => (
                      <motion.div
                        key={suggestion.transaction_id || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30"
                        data-testid={`correction-suggestion-${index}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {suggestion.transaction_description || 'Transaction'}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-sm">
                            <Badge variant="outline" className="text-red-600">
                              {(suggestion.current_classification || 'unknown').replace(/_/g, ' ')}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            <Badge className="bg-green-100 text-green-700">
                              {suggestion.suggested_classification.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <AIConfidenceBadge
                              score={suggestion.confidence}
                              reason={suggestion.reason}
                              compact
                            />
                            <span className="text-xs text-muted-foreground truncate">
                              {suggestion.reason}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <span className="font-semibold">
                            {formatTxAmount(suggestion, suggestion.transaction_amount)}
                          </span>
                          <Button
                            size="sm"
                            onClick={() => handleApplyCorrection(suggestion)}
                            disabled={applyingCorrections}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid={`apply-correction-${index}`}
                          >
                            {applyingCorrections ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Correction History - table view with consistent GL labels */}
            <CorrectionHistoryTable
              correctionHistory={correctionHistory}
              glAccounts={availableGlAccounts}
              formatAmount={formatAmount}
            />

            {/* Train AI Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-500" />
                      Train AI
                    </CardTitle>
                    <CardDescription>
                      Add an example transaction and its correct category
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleImportFromCorrections}
                      disabled={importingFromCorrections}
                      className="gap-1"
                      data-testid="import-from-corrections-btn"
                    >
                      {importingFromCorrections ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CloudDownload className="w-4 h-4" />
                      )}
                      Import from Corrections
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Training Stats Summary */}
                {trainingStats && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-700">
                        {trainingStats.training_examples?.total || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Training Rules</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">
                        {trainingStats.training_examples?.active || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Active Rules</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-700">
                        {trainingStats.corrections?.total || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Corrections Made</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">
                        {trainingStats.transactions?.completed || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">In Books</p>
                    </div>
                  </div>
                )}

                {/* AI Health Indicator */}
                {trainingStats?.ai_health && (
                  <div className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${
                    trainingStats.ai_health.status === 'trained' 
                      ? 'bg-green-50 border border-green-200' 
                      : trainingStats.ai_health.status === 'improving'
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      trainingStats.ai_health.status === 'trained' 
                        ? 'bg-green-100' 
                        : trainingStats.ai_health.status === 'improving'
                        ? 'bg-amber-100'
                        : 'bg-blue-100'
                    }`}>
                      <Brain className={`w-5 h-5 ${
                        trainingStats.ai_health.status === 'trained' 
                          ? 'text-green-600' 
                          : trainingStats.ai_health.status === 'improving'
                          ? 'text-amber-600'
                          : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium capitalize">{trainingStats.ai_health.status}</p>
                      <p className="text-sm text-muted-foreground">
                        {trainingStats.ai_health.recommendation}
                      </p>
                    </div>
                  </div>
                )}

                {/* iter 741 - Auto-suggested rules (3+ identical classifications without a rule) */}
                <div className="mb-4">
                  <SuggestedRulesPanel onAfterAccept={async () => {
                    await Promise.all([fetchTrainingExamples(), fetchTrainingStats()]);
                  }} />
                </div>

                {/* Training Examples - inline editable table (replaces modal) */}
                <TrainAITable
                  existingExamples={trainingExamples}
                  glAccounts={availableGlAccounts}
                  baseCurrency={business?.base_currency || business?.currency || 'NGN'}
                  loading={loadingTraining}
                  onAfterSave={async () => {
                    await Promise.all([fetchTrainingExamples(), fetchTrainingStats()]);
                  }}
                  onToggleActive={handleToggleTrainingExample}
                  onDelete={handleDeleteTrainingExample}
                />
              </CardContent>
            </Card>
            </div>
            )}

            {/* Auto-Discovery Content */}
            {moreSection === 'auto-discovery' && (
            <div className="space-y-6" data-testid="auto-discovery-content">
            <AutoDiscoveryPanel 
              business={business}
              onRefresh={() => {
                // Refresh stats when auto-discovery actions are taken
                fetchStats();
              }}
            />
            </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Bank Account Selection & Upload Dialog */}
        {/* iter1235 - multi-file review dialog (>=2 files selected) */}
        <StatementMultiUploadReviewDialog
          open={showMultiUploadReview}
          files={pendingMultiFiles}
          bankAccounts={bankAccounts}
          addJob={backgroundJobsCtx?.addJob}
          onClose={() => {
            setShowMultiUploadReview(false);
            setPendingMultiFiles([]);
          }}
          onAllQueued={() => {
            fetchStats?.();
          }}
        />

        {/* Upload starter modal — every "Upload statement" CTA opens this
            first (guide + limits + template downloads). Its primary CTA
            then triggers the OS file picker via the hidden fileInputRef,
            which hands off to the existing handleFileUpload/handleFileSelect
            flow and the bank-account picker dialog below. */}
        <UploadStarterDialog
          open={showUploadStarter}
          onOpenChange={setShowUploadStarter}
          onSelectFile={() => {
            try { fileInputRef.current && fileInputRef.current.click(); } catch { /* noop */ }
          }}
        />

        {/* iter1382 · Post-delete gap-fill modal. Opens automatically
            after a gap-creating delete and stays reachable via a Fill
            Gap chip if the user chose "Do it later". */}
        <PostDeleteGapFillModal
          open={!!showGapFillModal}
          onOpenChange={(o) => { if (!o) setShowGapFillModal(null); }}
          bankAccountId={showGapFillModal?.bank_account_id}
          gapStart={showGapFillModal?.gap_start}
          gapEnd={showGapFillModal?.gap_end}
          onManualUploadClick={() => setShowUploadStarter(true)}
        />

        <Dialog open={showUploadDialog} onOpenChange={(open) => {
          if (!open) {
            // Allow closing dialog at any time - upload continues in background
            setShowUploadDialog(false);
            // Only clear file state if not uploading
            if (!uploading) {
              setPendingFile(null);
              setSelectedBankAccountId('');
              setValidationResult(null);
              setShowValidationWarning(false);
              setDownstreamConnectedPrecheck(null);
              setDownstreamConnectedAck(false);
            }
          }
        }}>          <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="upload-statement-dialog">
            <DialogHeader className="text-left">
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#108981] shrink-0" />
                Upload Bank Statement
              </DialogTitle>
              <DialogDescription>
                Select the bank account this statement belongs to
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2 sm:py-4 min-w-0">
              {/* File Info */}
              {pendingFile && (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg min-w-0" data-testid="upload-file-info">
                  <FileText className="w-8 h-8 text-[#108981] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" title={pendingFile.name}>{pendingFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(pendingFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              )}
              
              {closedPeriodInfo?.closed_through && <ClosedPeriodNotice info={{ ...closedPeriodInfo, held_total: 0 }} compact />}

              {/* Bank Account Selection */}
              <div className="space-y-2 min-w-0">
                <Label htmlFor="bank-account" className="text-sm font-medium">
                  Select Bank Account <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={selectedBankAccountId} 
                  onValueChange={(value) => {
                    setSelectedBankAccountId(value);
                    setValidationResult(null);
                    setShowValidationWarning(false);
                    // iter1382 · fetch upload-precheck for the newly
                    // selected account. If it comes back `connected`,
                    // the amber warn + acknowledge gate above will
                    // render and disable the Process Statement button.
                    setDownstreamConnectedAck(false);
                    setDownstreamConnectedPrecheck(null);
                    if (value) {
                      axios
                        .get(`${API}/bank-feeds/upload-precheck?bank_account_id=${value}`)
                        .then(({ data }) => setDownstreamConnectedPrecheck(data))
                        .catch(() => setDownstreamConnectedPrecheck(null));
                    }
                  }}
                >
                  <SelectTrigger id="bank-account" data-testid="bank-account-select" className="w-full min-w-0 [&>span]:min-w-0 [&>span]:truncate [&>span]:text-left">
                    <SelectValue placeholder="Choose the bank account for this statement" />
                  </SelectTrigger>
                  <SelectContent className="max-w-[calc(100vw-1.5rem)]">
                    {bankAccounts.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No bank accounts found. Add a bank account first.
                      </div>
                    ) : (
                      bankAccounts.map((account) => {
                        const provider = account.open_banking_source || account.connection_type
                          || (account.plaid_account_id ? 'plaid' : null) || (account.mono_account_id ? 'mono' : null)
                          || (['mono', 'plaid', 'stitch'].includes(account.source) ? account.source : null)
                          || (account.open_banking_connected ? 'mono' : null);
                        return (
                        <SelectItem 
                          key={account.id} 
                          value={account.id}
                          data-testid={`bank-account-option-${account.id}`}
                        >
                          <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
                            <Landmark className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="truncate">{account.bank_name || 'Bank'} · {account.account_name}</span>
                            {account.account_number && (
                              <span className="text-xs text-muted-foreground shrink-0">
                                ···{account.account_number.slice(-4)}
                              </span>
                            )}
                            {account.parent_bank_account_id && (
                              <span className="shrink-0 rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-px text-[10px] font-semibold text-emerald-700" data-testid={`bank-account-option-sub-${account.id}`}>
                                Sub-account
                              </span>
                            )}
                            {provider ? (
                              <span
                                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-px text-[10px] font-semibold text-emerald-700"
                                data-testid={`bank-account-option-connected-${account.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Connected
                              </span>
                            ) : (
                              <span
                                className="shrink-0 rounded-full bg-slate-100 border border-slate-200 px-1.5 py-px text-[10px] font-medium text-slate-600"
                                data-testid={`bank-account-option-manual-${account.id}`}
                              >
                                Manual upload
                              </span>
                            )}
                          </span>
                        </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  We check the statement matches this account.
                </p>
              </div>

              {/* iter1382 · Connected-account warning for the downstream
                  upload dialog. Fires when the user has picked an
                  account that ingests via Mono/Plaid. Gates the Process
                  Statement CTA behind an explicit acknowledgement. */}
              {downstreamConnectedPrecheck?.connected && (
                <div
                  className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
                  data-testid="downstream-connected-account-warn"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900 space-y-1.5">
                      <p className="font-semibold text-[13px]">
                        This account is connected via {downstreamConnectedPrecheck.provider === 'plaid' ? 'Plaid' : 'Mono'}.
                      </p>
                      <p>
                        Uploading a statement here can create <strong>duplicate transactions</strong>.
                        You only need to upload manually if your bank feed has a gap
                        that {downstreamConnectedPrecheck.provider === 'plaid' ? 'Plaid' : 'Mono'} did not fill.
                      </p>
                      {downstreamConnectedPrecheck?.coverage_last_close_date && (
                        <p className="text-amber-800">
                          Your books currently cover up to{' '}
                          <strong>{downstreamConnectedPrecheck.coverage_last_close_date}</strong>.
                        </p>
                      )}
                    </div>
                  </div>
                  <label
                    className="flex items-center gap-2 pl-6 cursor-pointer select-none"
                    data-testid="downstream-connected-ack-label"
                  >
                    <input
                      type="checkbox"
                      checked={downstreamConnectedAck}
                      onChange={(e) => setDownstreamConnectedAck(e.target.checked)}
                      data-testid="downstream-connected-account-continue-anyway"
                      className="w-4 h-4 rounded border-amber-400"
                    />
                    <span className="text-xs text-amber-900">
                      I understand and want to continue anyway.
                    </span>
                  </label>
                </div>
              )}
              
              {/* Validation Result */}
              {validationResult && (
                <div className={`p-4 rounded-lg border ${
                  validationResult.is_valid 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {validationResult.is_valid ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${validationResult.is_valid ? 'text-green-700' : 'text-red-700'}`}>
                        {validationResult.is_valid ? 'Statement Validated' : 'Statement Mismatch Detected'}
                      </p>
                      {validationResult.mismatch_reason && (
                        <p className="text-sm text-red-600 mt-1">
                          {validationResult.mismatch_reason}
                        </p>
                      )}
                      {validationResult.detected_bank && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Detected bank: <strong>{validationResult.detected_bank}</strong>
                        </p>
                      )}
                      {validationResult.warnings?.length > 0 && (
                        <ul className="text-sm text-amber-600 mt-2 space-y-1">
                          {validationResult.warnings.map((w, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                              {w}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Validation Warning Dialog */}
              {showValidationWarning && validationResult && !validationResult.is_valid && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-700">
                        Are you sure you want to proceed?
                      </p>
                      <p className="text-sm text-amber-600 mt-1">
                        This statement appears to be from a different bank. Processing it could create incorrect records.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowValidationWarning(false);
                            setSelectedBankAccountId('');
                            setValidationResult(null);
                          }}
                        >
                          Choose Different Account
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleProcessUpload(true)}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Processing...
                            </>
                          ) : (
                            'Proceed Anyway'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* No bank accounts warning */}
              {bankAccounts.length === 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-700">No Bank Accounts</p>
                      <p className="text-sm text-amber-600 mt-1">
                        Please add a bank account in Settings before uploading statements.
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={() => navigate('/settings?tab=banking&highlight=connect-bank')}
                      >
                        Go to Settings
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex-col sm:flex-row sm:items-center gap-2 pt-2 border-t border-border/60 mt-2">
              {uploading && (
                <p className="text-xs text-muted-foreground w-full sm:flex-1 text-center sm:text-left">
                  Processing continues in background if you close this dialog
                </p>
              )}
              <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto sm:justify-end">
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setShowUploadDialog(false);
                    setPendingFile(null);
                    setSelectedBankAccountId('');
                    setValidationResult(null);
                  }}
                >
                  {uploading ? 'Close' : 'Cancel'}
                </Button>
                {!showValidationWarning && (
                  <Button
                    onClick={() => handleProcessUpload(false)}
                    disabled={
                      !selectedBankAccountId || uploading || bankAccounts.length === 0 ||
                      (downstreamConnectedPrecheck?.connected && !downstreamConnectedAck)
                    }
                    className="bg-[#108981] hover:bg-[#0a6b65] w-full sm:w-auto"
                    data-testid="process-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Processing {uploadProgress}%
                      </>
                    ) : validating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Validating...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Process Statement
                      </>
                    )}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* FAD Safety Modal - Shows warnings when importing transactions before Financial Anchor Date */}
        <BankFeedSafetyModal
          open={showFadSafetyModal}
          onClose={handleFadSafetyClose}
          onProceed={handleFadSafetyConfirm}
          startDate={fadSafetyResult?.startDate}
          bankName={bankAccounts.find(b => b.id === selectedBankAccountId)?.bank_name || 'your bank'}
          validationResult={fadSafetyResult}
        />

        {/* Iter 808: orphaned MatchDocumentDialog mount removed - its trigger
            `openMatchDialogFor` (defined at line ~3318) was never called from
            anywhere in BankFeedCenterPage. The active linking flow is
            LinkDocumentDialog (score-based, uses /api/bank-feeds/{id}/match-
            candidates), which is mounted below. MatchDocumentDialog itself is
            still used by BankReconciliationCenter so the component stays. */}

        {/* Transaction Detail Dialog - Extracted Component (Phase A - editable) */}
        <TransactionDetailDialog
          open={showDetailDialog}
          onOpenChange={setShowDetailDialog}
          transaction={selectedItem}
          formatAmount={formatAmount}
          bankCurrency={selectedItem?.currency || business?.currency || 'NGN'}
          baseCurrency={business?.currency || 'NGN'}
          availableGlAccounts={availableGlAccounts}
          onAfterSave={async () => {
            await Promise.all([fetchStats(), fetchProcessedItems(), fetchPendingItems()]);
          }}
          onNavigateToDocument={(docType, docId) => {
            const pluralUrl = {
              invoice: 'invoices', bill: 'bills',
              other_payment: 'other-payments', other_receipt: 'other-receipts',
            }[docType];
            if (pluralUrl && docId) navigate(`/${pluralUrl}/${docId}`);
          }}
        />

        {/* Upload Result Dialog */}
        <Dialog open={showUploadResult} onOpenChange={setShowUploadResult}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Upload Complete
              </DialogTitle>
            </DialogHeader>

            {uploadResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold">{uploadResult.total_received}</p>
                    <p className="text-sm text-muted-foreground">Total Transactions</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{uploadResult.high_confidence_processed}</p>
                    <p className="text-sm text-green-700">Auto-Processed</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{uploadResult.review_needed}</p>
                    <p className="text-sm text-amber-700">Need Review</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-600">{uploadResult.duplicates_skipped}</p>
                    <p className="text-sm text-gray-600">Duplicates Skipped</p>
                  </div>
                </div>

                {uploadResult.review_needed > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      {uploadResult.review_needed} transaction(s) need your review in the "Pending Review" tab.
                    </p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => { setShowUploadResult(false); setActiveTab('pending'); }}>
                View Pending Items
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Confirm Dialog */}
        <Dialog open={showBulkConfirmDialog} onOpenChange={setShowBulkConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {bulkAction === 'approve' ? 'Bulk Approve Transactions' : 'Bulk Reject Transactions'}
              </DialogTitle>
              <DialogDescription>
                {bulkAction === 'approve' 
                  ? `Are you sure you want to approve ${getSelectedItemsData().filter(i => i.status !== 'completed').length} transaction(s)?`
                  : `Are you sure you want to reject ${selectedItems.size} transactions?`}
              </DialogDescription>
            </DialogHeader>
            
            {bulkAction === 'approve' && (() => {
              const candidates = getSelectedItemsData().filter(i => i.status !== 'completed');
              const matchedRows = candidates.filter(i => i.matched_document_id);
              const autoRows = candidates.filter(i => !i.matched_document_id);
              return (
                <div className="space-y-3">
                  {matchedRows.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">
                        Matched documents: settlement journals will post ({matchedRows.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {matchedRows.map(item => (
                          <div key={item.id} className="flex items-center gap-2 text-sm p-2 bg-emerald-50 dark:bg-emerald-950/20 rounded">
                            <Check className="w-4 h-4 text-emerald-500" />
                            <span className="truncate">{item.description?.slice(0, 40)}...</span>
                            <span className={item.direction === 'inflow' ? 'text-emerald-600 ml-auto' : 'text-red-600 ml-auto'}>
                              {formatTxAmount(item)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {autoRows.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">
                        Auto-classify · Other Payment / Receipt docs will be created ({autoRows.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {autoRows.map(item => (
                          <div key={item.id} className="flex items-center gap-2 text-sm p-2 bg-violet-50 dark:bg-violet-950/20 rounded">
                            <Sparkles className="w-4 h-4 text-violet-500" />
                            <span className="truncate">{item.description?.slice(0, 40)}...</span>
                            <span className={item.direction === 'inflow' ? 'text-emerald-600 ml-auto' : 'text-red-600 ml-auto'}>
                              {formatTxAmount(item)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkConfirmDialog(false)} disabled={bulkProcessing}>
                Cancel
              </Button>
              <Button 
                onClick={executeBulkAction}
                disabled={bulkProcessing}
                className={bulkAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
              >
                {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {bulkAction === 'approve' ? 'Approve All' : 'Reject All'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reclassify Dialog */}
        <Dialog open={showReclassifyDialog} onOpenChange={setShowReclassifyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {reclassifyTarget === 'bulk' 
                  ? `Reclassify ${selectedItems.size} Transactions` 
                  : 'Reclassify Transaction'}
              </DialogTitle>
              <DialogDescription>
                {reclassifyTarget === 'bulk' 
                  ? 'Select the correct classification and provide a reason. This helps the AI learn for future transactions.'
                  : 'Create or edit a document for this transaction. Choose the document type that best represents this transaction.'}
              </DialogDescription>
            </DialogHeader>
            
            {/* Single Transaction - Navigate to Document Creation */}
            {reclassifyTarget !== 'bulk' && reclassifyTarget && (
              <div className="space-y-4">
                {/* Transaction Summary */}
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  <p className="text-sm font-medium">{reclassifyTarget.description?.slice(0, 80)}...</p>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Amount: <span className="font-medium text-foreground">{formatTxAmount(reclassifyTarget)}</span></span>
                    <span>Direction: <span className="font-medium text-foreground capitalize">{reclassifyTarget.direction?.replace('_', ' ')}</span></span>
                  </div>
                  {reclassifyTarget.classification && (
                    <p className="text-xs text-muted-foreground">
                      AI Classification: <span className="text-foreground">{reclassifyTarget.classification?.replace(/_/g, ' ')}</span>
                    </p>
                  )}
                  {(reclassifyTarget.ai_gl_debit || reclassifyTarget.ai_gl_credit) && (
                    <div className="flex gap-4 text-xs border-t pt-2 mt-2">
                      <span>Debit: <span className="font-medium">{reclassifyTarget.ai_gl_debit}</span> {reclassifyTarget.ai_gl_debit_name && `(${reclassifyTarget.ai_gl_debit_name})`}</span>
                      <span>Credit: <span className="font-medium">{reclassifyTarget.ai_gl_credit}</span> {reclassifyTarget.ai_gl_credit_name && `(${reclassifyTarget.ai_gl_credit_name})`}</span>
                    </div>
                  )}
                </div>
                
                {/* Document Type Selection for Navigation */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Create as Document Type:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* For Inflows */}
                    {reclassifyTarget.direction === 'inflow' && (
                      <>
                        <Button
                          variant="outline"
                          className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-green-50 hover:border-green-300"
                          onClick={() => navigateToDocumentCreation('invoice')}
                          data-testid="create-as-invoice"
                        >
                          <Receipt className="w-5 h-5 text-green-600" />
                          <span className="font-medium">Invoice</span>
                          <span className="text-[10px] text-muted-foreground">Customer payment received</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-blue-50 hover:border-blue-300"
                          onClick={() => navigateToDocumentCreation('other_receipt')}
                          data-testid="create-as-other-receipt"
                        >
                          <Wallet className="w-5 h-5 text-blue-600" />
                          <span className="font-medium">Other Receipt</span>
                          <span className="text-[10px] text-muted-foreground">Loan, capital, refund, etc.</span>
                        </Button>
                      </>
                    )}
                    
                    {/* For Outflows */}
                    {reclassifyTarget.direction === 'outflow' && (
                      <>
                        <Button
                          variant="outline"
                          className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-orange-50 hover:border-orange-300"
                          onClick={() => navigateToDocumentCreation('bill')}
                          data-testid="create-as-bill"
                        >
                          <FileText className="w-5 h-5 text-orange-600" />
                          <span className="font-medium">Bill</span>
                          <span className="text-[10px] text-muted-foreground">Vendor payment made</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-purple-50 hover:border-purple-300"
                          onClick={() => navigateToDocumentCreation('other_payment')}
                          data-testid="create-as-other-payment"
                        >
                          <Wallet className="w-5 h-5 text-purple-600" />
                          <span className="font-medium">Other Payment</span>
                          <span className="text-[10px] text-muted-foreground">Salary, bank charge, etc.</span>
                        </Button>
                      </>
                    )}
                  </div>
                  {/* Loans, prepayments, investments, asset disposals, deferred revenue and
                      open invoices/bills are MATCHED to what already exists in the books via
                      the same Link-existing-transaction flow used from the row menu. */}
                  <Button
                    variant="outline"
                    className="w-full h-auto py-3 flex flex-col items-center gap-1 border-dashed hover:bg-sky-50 hover:border-sky-300"
                    onClick={() => { const t = reclassifyTarget; setShowReclassifyDialog(false); openLinkDialogForTx(t); }}
                    data-testid="reclassify-link-existing"
                  >
                    <Link2 className="w-5 h-5 text-sky-600" />
                    <span className="font-medium">Link to existing transaction</span>
                    <span className="text-[10px] text-muted-foreground">
                      {reclassifyTarget.direction === 'inflow'
                        ? 'Open invoice, investment income, asset sale, deferred revenue, loan drawdown…'
                        : 'Open bill, loan repayment, prepayment, investment purchase, asset purchase…'}
                    </span>
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground text-center">
                  <Info className="w-3 h-3 inline mr-1" />
                  The form will be pre-filled with this transaction's data
                </p>
              </div>
            )}
            
            {/* Bulk Reclassification - Use existing classification selector */}
            {reclassifyTarget === 'bulk' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>New Classification</Label>
                  <Select value={selectedClassification} onValueChange={setSelectedClassification}>
                    <SelectTrigger data-testid="classification-select">
                      <SelectValue placeholder="Select classification..." />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Inflows (Money In)</div>
                      {CLASSIFICATION_OPTIONS.filter(o => o.direction === 'inflow').map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground mt-2">Outflows (Money Out)</div>
                      {CLASSIFICATION_OPTIONS.filter(o => o.direction === 'outflow').map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground mt-2">Transfers</div>
                      {CLASSIFICATION_OPTIONS.filter(o => o.direction === 'both').map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Reason for Reclassification</Label>
                  <Textarea
                    value={reclassifyReason}
                    onChange={(e) => setReclassifyReason(e.target.value)}
                    placeholder="Why is this classification incorrect? This helps improve AI accuracy..."
                    className="min-h-[80px]"
                    data-testid="reclassify-reason"
                  />
                </div>
                
                {/* Learning Toggle */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium">Help AI Learn</p>
                      <p className="text-xs text-muted-foreground">Apply this correction to improve future classifications</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={applyLearning}
                    onCheckedChange={setApplyLearning}
                    data-testid="apply-learning-toggle"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReclassifyDialog(false)} disabled={bulkProcessing}>
                Cancel
              </Button>
              {reclassifyTarget === 'bulk' && (
                <Button 
                  onClick={handleReclassify}
                  disabled={bulkProcessing || !selectedClassification || !reclassifyReason.trim()}
                  data-testid="confirm-reclassify"
                >
                  {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reclassify {selectedItems.size} Items
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Process Dialog - Extracted Component */}
        <ManualProcessDialog
          open={showManualProcessDialog}
          onOpenChange={setShowManualProcessDialog}
          transaction={manualProcessItem}
          onProcess={async ({ classification, description }) => {
            // Directly call the API since the component manages its own state
            if (!manualProcessItem || !classification) {
              toast.error('Please select a classification');
              return;
            }
            
            setManualProcessing(true);
            try {
              const response = await axios.post(`${API}/bank-feeds/process-manual`, {
                transaction_id: manualProcessItem.id,
                classification: classification,
                description: description,
                amount: manualProcessItem.amount,
                direction: manualProcessItem.direction,
                date: manualProcessItem.date,
                bank_account_id: manualProcessItem.bank_account_id
              });
              
              if (response.data.success || response.data.journal_id) {
                toast.success('Transaction processed manually');
                setShowManualProcessDialog(false);
                setManualProcessItem(null);
                await Promise.all([fetchStats(), fetchFailedItems(), fetchProcessedItems()]);
              }
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Manual processing failed');
            } finally {
              setManualProcessing(false);
            }
          }}
          processing={manualProcessing}
          formatAmount={formatAmount}
        />

        {/* Split Transaction Dialog - Extracted Component */}
        <SplitTransactionDialog
          open={showSplitDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowSplitDialog(false);
              setSplitTransaction(null);
            }
          }}
          transaction={splitTransaction}
          onNavigateToSplit={(transaction, docType) => {
            // Use the proper navigation function that sets up prefill data correctly
            navigateToSplitDocumentCreation(docType);
          }}
          formatAmount={formatAmount}
        />

        {/* Train AI Dialog - Extracted Component */}
        <TrainAIDialog
          open={showTrainDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowTrainDialog(false);
              setNewTrainingExample({
                description: '',
                counterparty: '',
                direction: 'outflow',
                classification: '',
                gl_code: '',
                gl_name: '',
                amount_range: 'any',
                notes: ''
              });
            }
          }}
          onSubmit={handleCreateTrainingExample}
          submitting={savingTraining}
          glAccounts={availableGlAccounts}
          prefillData={newTrainingExample.counterparty || newTrainingExample.description ? newTrainingExample : null}
        />

        {/* Training Validation Warning Dialog */}
        <Dialog open={showWarningConfirm} onOpenChange={(open) => {
          if (!open) {
            setShowWarningConfirm(false);
            setTrainingWarnings([]);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="w-5 h-5" />
                Accounting Concerns Detected
              </DialogTitle>
              <DialogDescription>
                This training rule may not follow standard accounting practices. Please review:
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-3">
              {trainingWarnings.map((warning, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
                >
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{warning}</p>
                </div>
              ))}
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowWarningConfirm(false);
                  setTrainingWarnings([]);
                }}
              >
                Go Back & Edit
              </Button>
              <Button
                onClick={handleConfirmTrainingWithWarnings}
                disabled={savingTraining}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-training-with-warnings-btn"
              >
                {savingTraining ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                I Understand, Create Anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Undo Classification AlertDialog */}
        <AlertDialog open={showUndoClassificationDialog} onOpenChange={(open) => {
          if (!open) {
            setShowUndoClassificationDialog(false);
            setUndoClassificationTransaction(null);
          }
        }}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-orange-700">
                <Undo2 className="w-5 h-5" />
                Undo Classification
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left">
                This will remove the transaction from your ledgers and move it back to the <strong>"Review Needed"</strong> tab for re-classification.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {undoClassificationTransaction && (
              <div className="space-y-4 py-2">
                {/* Transaction Summary */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      undoClassificationTransaction.direction === 'inflow' 
                        ? 'bg-green-100' 
                        : 'bg-red-100'
                    }`}>
                      {undoClassificationTransaction.direction === 'inflow' ? (
                        <ArrowDownRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm leading-tight line-clamp-2">
                        {undoClassificationTransaction.description || 'Transaction'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {undoClassificationTransaction.date} • {undoClassificationTransaction.counterparty || 'Unknown'}
                      </p>
                      <p className={`font-bold text-lg mt-2 ${
                        undoClassificationTransaction.direction === 'inflow' 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {formatTxAmount(undoClassificationTransaction, Math.abs(undoClassificationTransaction.amount))}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Info */}
                <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                  <AlertCircle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-800">This action will:</p>
                    <ul className="list-disc list-inside text-orange-700 mt-1 space-y-1 text-xs">
                      <li>Delete any document created from this transaction</li>
                      <li>Remove the journal entries from your ledgers</li>
                      <li>Move the transaction back to "Review Needed"</li>
                    </ul>
                  </div>
                </div>

                {/* iter1432 Gap #10 · Cascade-undo preview panel.
                    Shown when the settlement has downstream postings
                    (interest accruals, depreciation catchups, revenue
                    recognition entries) that would be ORPHANED if the
                    user reverses this settlement alone. */}
                {undoPreviewLoading && (
                  <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Checking for downstream postings…
                  </div>
                )}
                {undoPreview?.downstream_count > 0 && (
                  <div
                    data-testid="cascade-undo-preview"
                    className="border-2 border-red-300 rounded-lg p-3 bg-red-50 space-y-2 text-sm"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-red-800">
                          {undoPreview.downstream_count} downstream posting{undoPreview.downstream_count > 1 ? 's' : ''} exist after this settlement
                        </p>
                        <p className="text-xs text-red-700 mt-0.5">
                          Interest accruals, depreciation catchups or revenue recognition entries were posted AFTER this bank-feed link. Reversing this settlement alone will leave them orphaned in your P&amp;L.
                        </p>
                      </div>
                    </div>
                    <ul className="max-h-40 overflow-y-auto space-y-1 text-xs pl-1">
                      {undoPreview.downstream_postings.map((d, i) => (
                        <li
                          key={d.journal_id || i}
                          data-testid={`cascade-preview-row-${i}`}
                          className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1 border border-red-200"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">{d.description}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {d.date} · {d.source_type}
                            </p>
                          </div>
                          <span className="font-mono text-red-700 whitespace-nowrap">
                            {formatTxAmount(undoClassificationTransaction, d.amount)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <label className="flex items-start gap-2 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        data-testid="cascade-reverse-checkbox"
                        checked={cascadeReverseChecked}
                        onChange={(e) => setCascadeReverseChecked(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span className="text-xs text-red-800 font-medium">
                        Also reverse the {undoPreview.downstream_count} downstream posting{undoPreview.downstream_count > 1 ? 's' : ''} above ({formatTxAmount(undoClassificationTransaction, undoPreview.downstream_total)} total)
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={processingUndoClassification}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUndoClassification}
                disabled={processingUndoClassification}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-undo-classification-btn"
              >
                {processingUndoClassification ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Undo2 className="w-4 h-4 mr-2" />
                )}
                Undo Classification
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reversal Dialog */}
        <Dialog open={showReversalDialog} onOpenChange={(open) => {
          if (!open) {
            setShowReversalDialog(false);
            setReversalTransaction(null);
            setReversalReason('');
            setReversalNotes('');
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-purple-700">
                <RotateCcw className="w-5 h-5" />
                Reverse Transaction
              </DialogTitle>
              <DialogDescription>
                This will create reversing journal entries to undo the original posting
              </DialogDescription>
            </DialogHeader>

            {reversalTransaction && (
              <div className="space-y-4 py-4">
                {/* Transaction Summary */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    {reversalTransaction.direction === 'inflow' ? (
                      <ArrowDownRight className="w-5 h-5 text-green-600" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5 text-red-600" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{reversalTransaction.description || 'Transaction'}</p>
                      <p className="text-sm text-muted-foreground">
                        {reversalTransaction.date} • {reversalTransaction.counterparty || 'Unknown'}
                      </p>
                    </div>
                    <p className={`font-bold ${reversalTransaction.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatTxAmount(reversalTransaction, Math.abs(reversalTransaction.amount))}
                    </p>
                  </div>
                </div>

                {/* Reason Select */}
                <div className="space-y-2">
                  <Label>Reversal Reason *</Label>
                  <Select value={reversalReason} onValueChange={setReversalReason}>
                    <SelectTrigger data-testid="reversal-reason">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {reversalReasons.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={reversalNotes}
                    onChange={(e) => setReversalNotes(e.target.value)}
                    placeholder="Additional details about the reversal..."
                    rows={3}
                  />
                </div>

                {/* Warning */}
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">This action will:</p>
                    <ul className="list-disc list-inside text-amber-700 mt-1">
                      <li>Create reversing journal entries (swap debits/credits)</li>
                      <li>Update any linked invoices/bills back to unpaid status</li>
                      <li>Mark the original transaction as reversed</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowReversalDialog(false)}
                disabled={processingReversal}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkReversed}
                disabled={processingReversal || !reversalReason}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-reversal-btn"
              >
                {processingReversal ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Reverse Transaction
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Open Banking Setup Dialog */}
        <Dialog open={showOpenBankingSetup} onOpenChange={setShowOpenBankingSetup}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Landmark className="w-5 h-5 text-blue-600" />
                Bank Sync Setup
              </DialogTitle>
              <DialogDescription>
                Connect your bank account for automatic transaction sync
              </DialogDescription>
            </DialogHeader>
            
            {isOpenBankingCountry(business?.country) ? (
            <div className="space-y-4 py-4">
              {/* Country-specific info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      {getCountryDisplayName(business?.country) || 'Your'} Bank Integration
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Pulls transactions from your start month (up to 24 months).
                      <InfoTip title="Security" testId="tip-bank-sync-security" className="ml-1">A secure, read-only bank sync connection. Your credentials are encrypted and never stored by AkountFly.</InfoTip>
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Supported Banks Tooltip */}
              <BankIntegrationTooltip countryCode={business?.country} maxBanksToShow={15}>
                <div className="p-3 border rounded-lg cursor-help hover:bg-muted/50 transition-colors">
                  <p className="text-sm font-medium">Supported Banks</p>
                  <p className="text-xs text-muted-foreground">Hover to see the list of supported banks in your region</p>
                </div>
              </BankIntegrationTooltip>
              
              {/* Benefits */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Benefits of Bank Sync:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Real-time transaction sync via webhooks
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Automatic matching with invoices & bills
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Secure OAuth connection (no credentials stored)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Weekly reconciliation + monthly closing sync
                  </li>
                </ul>
              </div>
              
              {/* Prerequisites */}
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg" data-testid="bank-connection-prerequisites">
                <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5 mb-1.5">
                  <AlertCircle className="w-4 h-4" />
                  Before you connect
                </p>
                <ul className="text-xs text-amber-800 space-y-1 ml-5.5">
                  <li>• <strong>Internet/Mobile banking</strong> must be active on your account</li>
                  <li>• Have your <strong>phone nearby</strong> for OTP verification</li>
                  <li>• AkountFly gets <strong>read-only access</strong> only: we cannot initiate transactions</li>
                </ul>
              </div>

              {/* Status */}
              {connections.some(c => ['mono', 'plaid', 'stitch'].includes(c.source) && c.is_connected) ? (
                <div className="bg-green-50 p-3 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Bank Connected</p>
                    <p className="text-xs text-green-700">Your bank is already linked for automatic sync</p>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 p-3 rounded-lg flex items-center gap-2">
                  <Landmark className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Ready to Connect</p>
                    <p className="text-xs text-blue-700">Click "Connect Bank" below to securely link your bank account</p>
                  </div>
                </div>
              )}
            </div>
            ) : (
            <div className="space-y-4 py-4">
              <div className="text-center py-6">
                <Landmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-semibold text-lg mb-2">Coming Soon</p>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  Bank sync isn't in your region yet. Upload a CSV, PDF or Excel statement instead — same automation.
                </p>
                <Badge variant="outline" className="mt-4 text-amber-600 border-amber-300">Coming Soon</Badge>
              </div>
            </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOpenBankingSetup(false)}>
                {isOpenBankingCountry(business?.country) ? 'Cancel' : 'Close'}
              </Button>
              {isOpenBankingCountry(business?.country) && (
              <Button 
                className="bg-[#108981] hover:bg-[#108981]/90"
                onClick={() => {
                  setShowOpenBankingSetup(false);
                  monoConnectBank(monoConfig?.public_key);
                }}
                data-testid="connect-bank-dialog-btn"
              >
                <Landmark className="w-4 h-4 mr-2" />
                Connect Bank
              </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Statement Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={(open) => {
          if (!open) {
            setShowDeleteDialog(false);
            setDeleteTarget(null);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Bank Statement
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this uploaded statement?
              </DialogDescription>
            </DialogHeader>

            {deleteTarget && (
              <div className="space-y-4 py-4">
                {/* Statement Info */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium">{deleteTarget.filename}</p>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">This action will permanently:</p>
                    <ul className="list-disc list-inside text-red-700 mt-1">
                      <li>Delete all transactions from this upload</li>
                      <li>Reverse all journal entries created from this upload</li>
                      <li>Allow you to re-upload the same statement</li>
                    </ul>
                    <p className="mt-2 text-red-800 font-medium">This action cannot be undone.</p>
                  </div>
                </div>

                {/* iter1382 · Gap-risk warning + step-up notice */}
                {deletePrecheck?.will_create_gap && (
                  <div
                    className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm"
                    data-testid="delete-gap-warning"
                  >
                    <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-semibold text-amber-900">
                        Deleting this will create a gap in your bank feed.
                      </p>
                      <p className="text-amber-900">
                        Your open-banking feed has already continued past this
                        statement
                        {deletePrecheck.ob_first_row_date ? (
                          <> — the next transaction is dated <strong>{deletePrecheck.ob_first_row_date}</strong></>
                        ) : null}
                        . The books between <strong>{deletePrecheck.my_period_end}</strong>
                        {' '}and <strong>{deletePrecheck.ob_first_row_date}</strong> will be
                        missing until you fill the gap.
                      </p>
                      {deletePrecheck.requires_step_up && (
                        <p className="text-amber-900">
                          Your reconciliation is clean, so we&apos;ll ask you to
                          confirm your identity before deleting.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={deletingStatementId}
                data-testid="cancel-delete-statement"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteStatement}
                disabled={deletingStatementId}
                variant="destructive"
                data-testid="confirm-delete-statement"
              >
                {deletingStatementId ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete Statement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Detail Dialog (for Other Payments/Receipts) */}
        <Dialog open={showDocumentDetailDialog} onOpenChange={setShowDocumentDetailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#108981]" />
                {documentDetail?.document_type === 'other_receipt' ? 'Other Receipt' : 'Other Payment'} Details
              </DialogTitle>
              <DialogDescription>
                {documentDetail?.document_number || 'Document details'}
              </DialogDescription>
            </DialogHeader>

            {loadingDocumentDetail ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#108981]" />
              </div>
            ) : documentDetail ? (
              <div className="space-y-4 py-4">
                {/* Amount */}
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#108981]/10 to-[#5FC6C5]/10 rounded-lg">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <span className={`text-2xl font-bold ${
                    documentDetail.document_type === 'other_receipt' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {documentDetail.document_type === 'other_receipt' ? '+' : '-'}
                    {formatTxAmount(documentDetail)}
                  </span>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Date</p>
                    <p className="font-medium">{documentDetail.receipt_date || documentDetail.payment_date || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Reference</p>
                    <p className="font-medium">{documentDetail.reference || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">
                      {documentDetail.document_type === 'other_receipt' ? 'Received From' : 'Paid To'}
                    </p>
                    <p className="font-medium">{documentDetail.received_from || documentDetail.paid_to || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Category</p>
                    <p className="font-medium capitalize">{documentDetail.category?.replace(/_/g, ' ') || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground">GL Account</p>
                    <p className="font-medium">{documentDetail.gl_code} - {documentDetail.gl_name || 'Account'}</p>
                  </div>
                </div>

                {/* Description */}
                {documentDetail.description && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Description</p>
                    <p className="text-sm bg-muted/50 p-3 rounded">{documentDetail.description}</p>
                  </div>
                )}

                {/* Notes */}
                {documentDetail.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm bg-muted/50 p-3 rounded">{documentDetail.notes}</p>
                  </div>
                )}

                {/* Journal Reference */}
                {documentDetail.journal_id && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Landmark className="w-4 h-4" />
                    <span>Posted to GL • Journal: {documentDetail.journal_id.slice(0, 8)}...</span>
                  </div>
                )}
              </div>
            ) : null}

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDocumentDetailDialog(false)}
              >
                Close
              </Button>
              {documentDetail && (
                <Button
                  onClick={() => {
                    // Navigate to edit form based on document type
                    // Other receipts use InvoicesPage with createOtherReceipt state
                    // Other payments use BillsPage with createOtherPayment state
                    if (documentDetail.document_type === 'other_receipt') {
                      navigate('/invoices', { 
                        state: { 
                          editOtherReceipt: documentDetail
                        } 
                      });
                    } else {
                      navigate('/bills', { 
                        state: { 
                          editOtherPayment: documentDetail
                        } 
                      });
                    }
                    setShowDocumentDetailDialog(false);
                  }}
                  className="bg-[#108981] hover:bg-[#0a6b65] gap-1"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit Document
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Special Item full-term modal - the SAME form used on the
            Fixed Assets / Loans / Investments / Prepayments pages. */}
        <SpecialItemModal
          open={specialTermsModal.open}
          onOpenChange={(o) => { if (!o) setSpecialTermsModal({ open: false, transaction: null, itemType: null, family: null }); }}
          itemType={specialTermsModal.itemType}
          lineItem={{
            id: specialTermsModal.transaction?.id,
            description: specialTermsModal.transaction?.description || '',
            // Rule C - the GL chosen on the row (credit side for loans, debit side for assets/prepaids/investments)
            selectedGl: (specialTermsModal.itemType === 'loan' || specialTermsModal.itemType === 'deferred_revenue')
              ? specialTermsModal.transaction?.ai_gl_credit
              : specialTermsModal.transaction?.ai_gl_debit,
          }}
          amount={Math.abs(Number(specialTermsModal.transaction?.amount || 0))}
          currencySymbol=""
          formatNumber={(n) => (n ?? 0).toLocaleString()}
          counterpartyName={specialTermsModal.transaction?.counterparty || ''}
          documentType={specialTermsModal.transaction?.direction === 'inflow' ? 'other_receipt' : 'other_payment'}
          glCode={specialTermsModal.transaction?.ai_gl_debit || ''}
          transactionDate={specialTermsModal.transaction?.date || specialTermsModal.transaction?.transaction_date || null}
          onSave={handleSpecialTermsSave}
          onCancel={() => setSpecialTermsModal({ open: false, transaction: null, itemType: null, family: null })}
        />

        {/* Period Lock Warning Dialog (lazy) */}
        {periodLockDialog.open && (
          <Suspense fallback={null}>
            <PeriodLockWarningDialog
              open={periodLockDialog.open}
              onClose={() => setPeriodLockDialog(prev => ({ ...prev, open: false }))}
              transactionDate={periodLockDialog.transactionDate}
              lockedPeriod={periodLockDialog.lockedPeriod}
              onPostToOriginalDate={periodLockDialog.onPostToOriginalDate}
              onPostToToday={periodLockDialog.onPostToToday}
            />
          </Suspense>
        )}

        {/* Multi-Allocation Dialog */}
        <Dialog open={showMultiAllocDialog} onOpenChange={setShowMultiAllocDialog}>
          <DialogContent className="max-w-2xl" data-testid="multi-alloc-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-500" />
                Allocate to Multiple {multiAllocTransaction?.direction === 'inflow' ? 'Invoices' : 'Bills'}
              </DialogTitle>
              <DialogDescription>
                Split this {formatTxAmount(multiAllocTransaction)} payment across multiple documents
              </DialogDescription>
            </DialogHeader>

            {multiAllocLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : multiAllocSuggestions?.suggestions?.length > 0 ? (
              <div className="space-y-4">
                {/* Transaction Summary */}
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Transaction Amount</span>
                    <span className={`text-xl font-bold ${multiAllocTransaction?.direction === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                      {formatTxAmount(multiAllocTransaction)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {multiAllocTransaction?.description?.slice(0, 80)}
                  </p>
                </div>

                {/* Allocation Summary */}
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <span className="text-sm">
                    <strong>{selectedAllocations.length}</strong> of {multiAllocSuggestions.suggestions.length} documents selected
                  </span>
                  <div className="flex items-center gap-4">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowWhtDiscountPanel(!showWhtDiscountPanel)}
                      className="text-xs"
                    >
                      {showWhtDiscountPanel ? 'Hide' : 'Show'} WHT/Discount
                    </Button>
                    <span className="font-medium text-purple-700">
                      Total: {formatTxAmount(multiAllocTransaction, selectedAllocations.reduce((sum, a) => sum + a.suggested_allocation, 0))}
                    </span>
                  </div>
                </div>

                {/* Document List */}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {multiAllocSuggestions.suggestions.map((alloc, idx) => {
                    const isSelected = selectedAllocations.find(a => a.document_id === alloc.document_id);
                    return (
                      <div
                        key={alloc.document_id}
                        className={`p-3 border rounded-lg transition-colors ${
                          isSelected ? 'bg-purple-50 border-purple-300' : 'hover:bg-muted/50'
                        }`}
                        data-testid={`alloc-item-${idx}`}
                      >
                        <div 
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => toggleAllocationSelection(alloc)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox checked={!!isSelected} />
                            <div>
                              <p className="font-medium">
                                {alloc.document_number || `${alloc.document_type} #${idx + 1}`}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {alloc.counterparty_name} • Outstanding: {formatTxAmount(multiAllocTransaction, alloc.amount_outstanding)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-purple-600">
                              {formatTxAmount(multiAllocTransaction, alloc.suggested_allocation)}
                            </p>
                            {alloc.match_score > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {alloc.match_score}% match
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* WHT and Discount inputs - show when panel is open and item is selected */}
                        {showWhtDiscountPanel && isSelected && (
                          <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-muted-foreground">
                                {multiAllocTransaction?.direction === 'inflow' ? 'WHT Receivable' : 'WHT Payable'}
                              </Label>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={allocWhtAmounts[alloc.document_id] || ''}
                                onChange={(e) => setAllocWhtAmounts(prev => ({
                                  ...prev,
                                  [alloc.document_id]: e.target.value
                                }))}
                                onClick={(e) => e.stopPropagation()}
                                className="h-8 mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">
                                {multiAllocTransaction?.direction === 'inflow' ? 'Discount Given' : 'Discount Received'}
                              </Label>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={allocDiscountAmounts[alloc.document_id] || ''}
                                onChange={(e) => setAllocDiscountAmounts(prev => ({
                                  ...prev,
                                  [alloc.document_id]: e.target.value
                                }))}
                                onClick={(e) => e.stopPropagation()}
                                className="h-8 mt-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Remaining/Difference */}
                {(() => {
                  const totalAllocated = selectedAllocations.reduce((sum, a) => sum + a.suggested_allocation, 0);
                  const txnAmount = Math.abs(multiAllocTransaction?.amount || 0);
                  const diff = txnAmount - totalAllocated;
                  
                  if (Math.abs(diff) > 0.01) {
                    return (
                      <div className={`p-3 rounded-lg ${diff > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
                        <p className={`text-sm ${diff > 0 ? 'text-amber-700' : 'text-red-700'}`}>
                          {diff > 0 
                            ? `${formatTxAmount(multiAllocTransaction, diff)} will remain unallocated`
                            : `Allocation exceeds transaction by ${formatTxAmount(multiAllocTransaction, Math.abs(diff))}`
                          }
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
                <h3 className="text-lg font-semibold">No Matching Documents Found</h3>
                <p className="text-muted-foreground">
                  No outstanding {multiAllocTransaction?.direction === 'inflow' ? 'invoices' : 'bills'} found to allocate this payment to.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMultiAllocDialog(false)}>
                Cancel
              </Button>
              {selectedAllocations.length > 0 && (
                <Button
                  onClick={applyMultiAllocation}
                  disabled={multiAllocLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="apply-multi-alloc-btn"
                >
                  {multiAllocLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Apply Allocation ({selectedAllocations.length} docs)
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Accounting Start Date Modal */}
        <AccountingStartDateModal 
          open={showStartDateModal}
          onClose={() => setShowStartDateModal(false)}
          transactionType="transaction"
        />
        
        {/* Investment Match Dialog (lazy) */}
        {showInvestmentMatchDialog && (
          <Suspense fallback={null}>
            <InvestmentMatchDialog
              open={showInvestmentMatchDialog}
              onOpenChange={setShowInvestmentMatchDialog}
              bankTransaction={investmentMatchTransaction}
              formatAmount={formatAmount}
              onMatchComplete={handleInvestmentMatchComplete}
              bankCurrency={selectedBankCurrency}
              baseCurrency={business?.currency || 'NGN'}
            />
          </Suspense>
        )}

        {/* Loan Match Dialog (lazy) */}
        {taxPaymentTransaction && (
          <Suspense fallback={null}>
            <TaxPaymentDialog
              open={!!taxPaymentTransaction}
              onOpenChange={(o) => { if (!o) setTaxPaymentTransaction(null); }}
              transaction={taxPaymentTransaction}
              currency={selectedBankCurrency}
              onComplete={handleTaxPaymentComplete}
            />
          </Suspense>
        )}
        {showLoanMatchDialog && (
          <Suspense fallback={null}>
            <LoanMatchDialog
              open={showLoanMatchDialog}
              onOpenChange={setShowLoanMatchDialog}
              bankTransaction={loanMatchTransaction}
              formatAmount={formatAmount}
              onMatchComplete={handleLoanMatchComplete}
              bankCurrency={selectedBankCurrency}
              baseCurrency={business?.currency || 'NGN'}
            />
          </Suspense>
        )}

        {/* Prepayment Match Dialog (lazy) */}
        {showPrepaymentMatchDialog && (
          <Suspense fallback={null}>
            <PrepaymentMatchDialog
              open={showPrepaymentMatchDialog}
              onOpenChange={setShowPrepaymentMatchDialog}
              bankTransaction={prepaymentMatchTransaction}
              formatAmount={formatAmount}
              onMatchComplete={handlePrepaymentMatchComplete}
            />
          </Suspense>
        )}

        {/* Asset Disposal Dialog (lazy) */}
        {showAssetDisposalDialog && (
          <Suspense fallback={null}>
            <AssetDisposalDialog
              open={showAssetDisposalDialog}
              onOpenChange={setShowAssetDisposalDialog}
              bankTransaction={assetDisposalTransaction}
              formatAmount={formatAmount}
              onMatchComplete={handleAssetDisposalComplete}
            />
          </Suspense>
        )}

        {/* Inter-Bank Transfer Modal (lazy) */}
        {showTransferModal && (
          <Suspense fallback={null}>
            <InterBankTransferModal
              open={showTransferModal}
              onOpenChange={setShowTransferModal}
              bankAccounts={bankAccounts}
              business={business}
              onSubmit={handleCreateTransfer}
            />
          </Suspense>
        )}

        {/* Column Mapping Dialog - shown when auto-detection fails */}
        {/* Column Mapping Dialog (Phase 4 - extracted) */}
        <ColumnMappingDialog
          open={showColumnMapping}
          job={columnMappingJob}
          columnMapping={columnMapping}
          onColumnMappingChange={setColumnMapping}
          onClose={() => { setShowColumnMapping(false); setColumnMappingJob(null); }}
          onApplied={(jobMeta) => setBackgroundJobs((prev) => [...prev, jobMeta])}
        />
      </div>
      {/* Staged Mono Sync Progress Dialog */}
      <MonoSyncProgress
        open={!!bfcSyncStatus}
        syncStatus={bfcSyncStatus}
        syncMessage={bfcSyncMessage}
        backlogInfo={bfcBacklogInfo}
        confirmingBacklog={confirmingBfcBacklog}
        onConfirmBacklog={confirmBacklog}
        onRollback={rollbackConnection}
        onDismiss={() => { dismissBacklog(); bfcResetState(); }}
        preflightData={bfcPreflightData}
        preflightLoading={bfcPreflightLoading}
        onNavigateToBankFeed={() => { /* Already on Bank Transactions Center */ }}
      />

      {/* Phase B - Auto-approve high-confidence dialog */}
      <AutoApproveDialog
        open={autoApproveOpen}
        onOpenChange={setAutoApproveOpen}
        candidates={filteredPendingItems.filter(t => (t.confidence ?? t.ai_confidence ?? 0) >= 0.80)}
        threshold={0.80}
        currency={(() => {
          const cs = new Set(filteredPendingItems.filter(t => (t.confidence ?? t.ai_confidence ?? 0) >= 0.80).map(t => rowCurrencyOf(t, bankAccounts, business?.currency)));
          return cs.size === 1 ? [...cs][0] : (selectedBankCurrency || business?.currency || 'NGN');
        })()}
        onConfirm={async (txs) => {
          const specialRows = txs.filter((t) => specialDocTypeForGl(t, t.direction === 'outflow' ? t.ai_gl_debit : t.ai_gl_credit) || SPECIAL_DOC_TYPES.includes(t.ai_document_type || ''));
          if (txs.length > 1 && specialRows.length > 0) {
            toast.error(`Auto-approve stopped: ${specialRows.length} of the ${txs.length} high-confidence transactions ${specialRows.length === 1 ? 'is a' : 'are'} special item${specialRows.length === 1 ? '' : 's'} (loan, prepayment, investment, fixed asset or deferred revenue). Approve those individually so you can match them to an existing item or set one up, then run auto-approve again.`, { duration: 10000 });
            return;
          }
          if (txs.length === 1 && specialRows.length === 1) {
            await handleApprove(txs[0]);
            return;
          }
          let ok = 0, fail = 0;
          bulkTaxGuardRef.current = true;
          try {
            for (const tx of txs) {
              try {
                await handleQuickApprove(tx);
                ok += 1;
              } catch (_e) {
                fail += 1;
              }
            }
          } finally {
            bulkTaxGuardRef.current = false;
          }
          if (ok > 0) toast.success(`Approved ${ok} transaction${ok === 1 ? '' : 's'}.`);
          if (fail > 0) toast.error(`${fail} couldn't be approved: check details.`);
          await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
        }}
      />

      {/* Phase G - Bulk Re-classify dialog (atomic reverse + repost on completed rows) */}
      <BulkReclassifyDialog
        open={showBulkCategoryDialog}
        onOpenChange={setShowBulkCategoryDialog}
        selectedTransactions={getSelectedItemsData()}
        glAccounts={availableGlAccounts || []}
        currency={selectedBankCurrency || 'NGN'}
        onConfirm={handleBulkCategorySubmit}
      />

      {/* Phase H - Link / Swap-link to existing document
          iter1411 · Modal routing:
            mode='link'   → fresh Smart Link on a Review-Needed row
            mode='relink' → In-Books relink (atomic teardown + fresh
                            compose via /relink-and-settle)
            mode='swap'   → LEGACY LinkDocumentDialog path, kept for
                            any residual callers. New code should use
                            mode='relink' instead. */}
      {linkDialogTx && linkDialogMode === 'swap' && (
        <LinkDocumentDialog
          open={!!linkDialogTx}
          onOpenChange={(o) => { if (!o) { setLinkDialogTx(null); setLinkDialogMode('link'); } }}
          transaction={linkDialogTx}
          currency={selectedBankCurrency || 'NGN'}
          mode={linkDialogMode}
          onLinked={async () => { await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]); }}
        />
      )}
      {/* iter1401 Slice 5-8 · Smart Link Document Modal with composite settlement.
          iter1411 · Also handles In-Books relink when isRelink=true. */}
      {linkDialogTx && linkDialogMode !== 'swap' && (
        <SmartLinkDocumentModal
          open={!!linkDialogTx}
          onOpenChange={(o) => { if (!o) { setLinkDialogTx(null); setLinkDialogMode('link'); setLinkDialogFocus(null); } }}
          transaction={linkDialogTx}
          focusType={linkDialogFocus}
          isRelink={linkDialogMode === 'relink'}
          onLinked={async () => { await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]); }}
        />
      )}

      {/* iter1404 Phase 4b · In Books drill — journal + linked docs + GL codes */}
      {inBooksTx && (
        <InBooksDetailModal
          open={!!inBooksTx}
          onOpenChange={(o) => { if (!o) setInBooksTx(null); }}
          transaction={inBooksTx}
          bankCurrency={bankAccounts.find((b) => b.id === inBooksTx?.bank_account_id)?.currency}
          baseCurrency={business?.currency}
        />
      )}

      {/* Phase H - Confirm bulk send-back to Review */}
      <Dialog open={showBulkUndoDialog} onOpenChange={setShowBulkUndoDialog}>
        <DialogContent className="max-w-md" data-testid="bulk-undo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Undo2 className="w-5 h-5" />
              Send {selectedItems.size} transaction{selectedItems.size === 1 ? '' : 's'} back to Review?
            </DialogTitle>
            <DialogDescription className="text-sm">
              For each transaction we will <strong>reverse the journal entry</strong> and <strong>delete the document</strong> that was created from it. The bank balance is not affected, but every step is recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUndoDialog(false)} data-testid="bulk-undo-cancel">Cancel</Button>
            <Button onClick={handleBulkUndoConfirm} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="bulk-undo-confirm">
              <Undo2 className="w-4 h-4 mr-1.5" />
              Send {selectedItems.size} back to Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase #5 + Phase 1.2 (Feb 2026) - Floating "Save N changes" bar for
          staged category edits. Now ONLY surfaces edits on already-completed
          rows; Review-Needed edits are auto-committed by Approve so the user
          never has to click a separate Save button. See `handleApprove` for
          the auto-commit logic. */}
      <BankFeedSaveChangesBar
        pendingEdits={
          (() => {
            const filtered = new Map();
            pendingGlEdits.forEach((edit, txId) => {
              const isCompleted = ['completed', 'matched', 'processed', 'created'].includes(edit.status);
              if (isCompleted) filtered.set(txId, edit);
            });
            return filtered;
          })()
        }
        transactionsById={transactionsByIdMap}
        onSave={handleCommitPendingEdits}
        onDiscard={handleDiscardPendingEdits}
        saving={savingPendingEdits}
      />

      {/* Phase 2.2 (Feb 2026) - Special Item Decision Modal. Intercepts
          Approve / Save when the resulting GL is a special-item family.
          The three callbacks fork the flow:
            New + remind_later=false → existing AssetPrepaymentInputDialog
            New + remind_later=true  → POST a tracked-item-skeleton + Approve,
                                       Dashboard "set terms" banner surfaces.
            Match existing           → POST settlement-link endpoint with
                                       variance treatment. */}
      <SpecialItemDecisionModal
        open={specialItemModal.open}
        transaction={specialItemModal.transaction}
        currency={rowCurrencyOf(specialItemModal.transaction, bankAccounts, business?.currency)}
        family={specialItemModal.docType === 'loan' ? 'loan_received' : (specialItemModal.docType || undefined)}
        notNowLabel={specialItemModal.inBooks ? 'Not now: keep the current classification' : null}
        onOpenLinkModal={(tx, focusType) => {
          // Match existing → the Link-existing-transaction modal, focused on the family.
          // In Books: relink mode tears the old classification down (full cascade) atomically.
          const inBooks = !!specialItemModal.inBooks;
          setSpecialItemModal({ open: false, transaction: null, docType: null });
          openLinkDialogForTx(tx, { mode: inBooks ? 'relink' : 'link', focusType });
        }}
        onClose={() => setSpecialItemModal({ open: false, transaction: null, docType: null })}
        onCancel={() => {
          // User chose to keep it in Review Needed. No journal change.
          toast.info('Left in Review Needed.');
        }}
        onConfirmNew={async (tx, { remind_later }) => {
          const docType = specialItemModal.docType;
          if (specialItemModal.inBooks) {
            // In Books → remove the original classification completely first.
            try {
              await undoRowCascade(tx.id, `Reclassified to ${docType} from In Books`);
              if (specialItemModal.glEdit) {
                const body = { new_classification: specialItemModal.glEdit.sub_category, new_document_type: docType, reason: 'In Books reclassify to special item', apply_learning: true };
                if (specialItemModal.glEdit.isInflow) body.new_gl_credit = specialItemModal.glEdit.glCode; else body.new_gl_debit = specialItemModal.glEdit.glCode;
                await axios.post(`${API}/bank-feeds/reclassify/${tx.id}`, body);
                if (specialItemModal.glEdit.isInflow) tx.ai_gl_credit = specialItemModal.glEdit.glCode; else tx.ai_gl_debit = specialItemModal.glEdit.glCode;
              }
              setPendingGlEdits((prev) => { const n = new Map(prev); n.delete(tx.id); return n; });
              await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Could not remove the previous classification.');
              return;
            }
          }
          if (remind_later) {
            // Phase 3 (Feb 2026) - use the existing quick_approve flow.
            // This auto-creates the loan/investment/asset/prepayment with
            // setup_status='pending' AND posts the journal. The existing
            // OpenTasksWidget on Dashboard (powered by ActionEngine) will
            // surface "Configure Loan/Investment/Asset Terms" tasks
            // automatically - no separate banner needed.
            (async () => {
              try {
                const resp = await axios.post(`${API}/bank-feeds/approve/${tx.id}`, null, {
                  params: {
                    quick_approve: true,
                    allow_locked_period: true,
                  },
                });
                toast.success('Approved. Set the terms anytime from the "Action Required" tasks on Dashboard.');
                // iter1100-fix - fire learning toast on special-item remind-later path
                _showLearningToast(resp?.data?.learning);
                await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
              } catch (err) {
                const _det = err?.response?.data?.detail;
                if (err?.response?.status === 409 && _det?.code === 'existing_match_found') {
                  setExistingMatchPrompt({ item: tx, match: _det.match || {}, retryParams: { quick_approve: true, allow_locked_period: true } });
                } else {
                  toast.error(typeof _det === 'string' ? _det : 'Could not approve transaction.');
                }
              }
            })();
          } else {
            // Jul 16 - open the canonical SpecialItemModal (full term form,
            // same as the special-item pages) instead of the older
            // AssetPrepaymentInputDialog which only captured a subset.
            openSpecialTermsModal(tx, docType);
          }
        }}
        onPostUntracked={async (tx) => {
          // In-and-out / manual: post to the chosen GL with NO tracked item (same quick_approve path, no_track=true).
          const docType = specialItemModal.docType;
          const glEdit = specialItemModal.glEdit || pendingGlEdits.get(tx.id);
          if (specialItemModal.inBooks) {
            try {
              await undoRowCascade(tx.id, `Reclassified to untracked ${docType} GL from In Books`);
              if (specialItemModal.glEdit) {
                const body = { new_classification: specialItemModal.glEdit.sub_category, new_document_type: docType, reason: 'In Books reclassify (untracked)', apply_learning: true };
                if (specialItemModal.glEdit.isInflow) body.new_gl_credit = specialItemModal.glEdit.glCode; else body.new_gl_debit = specialItemModal.glEdit.glCode;
                await axios.post(`${API}/bank-feeds/reclassify/${tx.id}`, body);
              }
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Could not remove the previous classification.');
              return;
            }
          }
          try {
            const params = { quick_approve: true, allow_locked_period: true, no_track: true };
            if (glEdit?.glCode) params.gl_code = glEdit.glCode;
            const resp = await axios.post(`${API}/bank-feeds/approve/${tx.id}`, null, { params });
            setPendingGlEdits((prev) => { const n = new Map(prev); n.delete(tx.id); return n; });
            toast.success('Posted to the GL without tracking - no schedule or item was created.');
            _showLearningToast(resp?.data?.learning);
            await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
          } catch (err) {
            const _det = err?.response?.data?.detail;
            toast.error(typeof _det === 'string' ? _det : 'Could not post the transaction.');
          }
        }}
        onConfirmMatch={async (tx, candidate, { variance_treatment, variance_amount, subtype }) => {
          // POST settlement-match endpoint. This is the existing settle_payment
          // / settle_receipt path on the schedule's collection - bank amount
          // wins; variance posted to a variance line when treatment === 'prompt'.
          // Iter 818 - `subtype` captures the user's settlement-type pick.
          try {
            await axios.post(`${API}/bank-feeds/match-settlement/${tx.id}`, {
              target_id: candidate.id,
              target_collection: ({
                fixed_asset: 'fixed_assets',
                prepayment: 'prepayments',
                investment: 'investments',
                loan_received: 'loans',
                deferred_revenue: 'deferred_revenue',
                loan_receivable: 'investments',
              })[
                ({
                  fixed_asset: 'fixed_asset',
                  prepayment: 'prepayment',
                  investment: 'investment',
                  loan: 'loan_received',
                  loan_received: 'loan_received',
                  deferred_revenue: 'deferred_revenue',
                  loan_receivable: 'loan_receivable',
                })[specialItemModal.docType] || 'fixed_asset'
              ],
              variance_treatment,
              variance_amount,
              subtype: subtype || null,
            });
            toast.success(`Matched to ${candidate.name} and approved.`);
            await Promise.all([fetchStats(), fetchPendingItems(), fetchProcessedItems()]);
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not link this transaction.');
            throw err;
          }
        }}
      />

      {/* In-page document dialogs - replaces page navigation so the
          user stays in the Bank Feed context while creating documents. */}
      <BankFeedDocumentDialog
        open={docDialog.open}
        onOpenChange={(o) => setDocDialog((p) => ({ ...p, open: o }))}
        docType={docDialog.type}
        prefillData={docDialog.prefill}
        onSaved={handleInPageDocSaved}
      />
      <RecordOtherReceiptModal
        open={otherReceiptDialog.open}
        onOpenChange={(o) => setOtherReceiptDialog((p) => ({ ...p, open: o }))}
        prefilledData={otherReceiptDialog.prefill}
        onSuccess={() => {
          setOtherReceiptDialog({ open: false, prefill: null });
          handleInPageDocSaved();
        }}
      />
      <RecordOtherPaymentModal
        open={otherPaymentDialog.open}
        onOpenChange={(o) => setOtherPaymentDialog((p) => ({ ...p, open: o }))}
        prefilledData={otherPaymentDialog.prefill}
        onSuccess={() => {
          setOtherPaymentDialog({ open: false, prefill: null });
          handleInPageDocSaved();
        }}
      />
    </DashboardLayout>
  );
};

export default BankFeedCenterPage;
